/**
 * PulseFit Workout Detail & Editor Controller
 * 
 * Manages detailed workout analysis modal/view, station splits editor,
 * station alternatives substitutions, and IDOR-safe split mutations.
 */

import {
  STATION_META,
  HYROX_STATIONS,
  CANONICAL_HYROX_STATIONS,
  isGenericStageName,
  normalizeStationName,
  parseSequentialStationsFromNotes,
  isRawStationSequenceNotes,
  getStationBadgeHtml,
  calculateWorkoutZoneSeconds
} from '../telemetry/index.js';
import {
  getWorkoutTotalDistance,
  postWorkoutToStrava as apiPostWorkoutToStrava,
  refreshStravaTokenIfExpired,
  fetchStravaActivityLaps,
  inferSplitsFromStravaLaps
} from '../integrations/index.js';
import {
  getWorkoutSufferScore
} from '../performance/index.js';
import {
  renderInstagramCard
} from '../export/index.js';
import {
  runAIEvaluation,
  processVoiceCommandWithGemini
} from '../ai-coaching/index.js';
import {
  store,
  saveStateToStorage
} from '../../store.js';
import {
  saveOfflineWorkout,
  deleteOfflineWorkout
} from '../storage/index.js';
import { showView, getPreviousViewId } from './router.js';
import { deleteWorkout, isDistanceStation, escapeHtml } from './workouts.js';
import { isStravaAdmin } from './settings.js';

let currentWorkoutDetailId = null;
let activeEditRow = null;
let activeEditStageIndex = null;
let activeEditIsNewStage = false;
let activeEditStageDraft = null;
let savedDetailScrollState = null;
let activeMap = null;
let workoutDetailSetupDone = false;
let expandedHrModalSetupDone = false;
let currentHrChartWorkout = null;
let isSavingSplits = false;
let shareCardModalSetupDone = false;
let activeShareCardBlob = null;

function captureDetailScrollState() {
  if (typeof document === 'undefined') return null;
  const mainContent = document.querySelector('.main-content');
  const stageContainer = document.getElementById('detail-stage-duration-container');
  const editorList = document.getElementById('detail-stages-editor-list');
  const winScrollY = typeof window !== 'undefined'
    ? (Number.isFinite(window.scrollY) ? window.scrollY : (Number.isFinite(window.pageYOffset) ? window.pageYOffset : (document.documentElement ? document.documentElement.scrollTop : 0)))
    : 0;
  return {
    windowScrollY: winScrollY || 0,
    mainScrollTop: mainContent ? (mainContent.scrollTop || 0) : 0,
    stageContainerScrollTop: stageContainer ? (stageContainer.scrollTop || 0) : 0,
    editorListScrollTop: editorList ? (editorList.scrollTop || 0) : 0
  };
}

function restoreDetailScrollState(scrollState) {
  if (!scrollState || typeof document === 'undefined') return;
  const applyScroll = () => {
    const mainContent = document.querySelector('.main-content');
    if (mainContent && Number.isFinite(scrollState.mainScrollTop)) {
      mainContent.scrollTop = scrollState.mainScrollTop;
    }
    const stageContainer = document.getElementById('detail-stage-duration-container');
    if (stageContainer && Number.isFinite(scrollState.stageContainerScrollTop)) {
      stageContainer.scrollTop = scrollState.stageContainerScrollTop;
    }
    const editorList = document.getElementById('detail-stages-editor-list');
    if (editorList && Number.isFinite(scrollState.editorListScrollTop)) {
      editorList.scrollTop = scrollState.editorListScrollTop;
    }
    if (typeof window !== 'undefined' && Number.isFinite(scrollState.windowScrollY)) {
      if (typeof window.scrollTo === 'function') {
        window.scrollTo(0, scrollState.windowScrollY);
      }
      if (document.documentElement) {
        document.documentElement.scrollTop = scrollState.windowScrollY;
      }
    }
  };
  applyScroll();
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(applyScroll);
  }
  if (typeof setTimeout === 'function') {
    setTimeout(applyScroll, 0);
  }
}

export function isSavingSplitsActive() {
  return isSavingSplits;
}

export function resetWorkoutDetailSetup() {
  workoutDetailSetupDone = false;
  expandedHrModalSetupDone = false;
  currentHrChartWorkout = null;
  isEditorInitialized = false;
  activeEditRow = null;
  activeEditStageIndex = null;
  activeEditIsNewStage = false;
  activeEditStageDraft = null;
  savedDetailScrollState = null;
  shareCardModalSetupDone = false;
  activeShareCardBlob = null;
}


export const DEFAULT_STATION_ALTERNATIVES = {
  "Run": ["Outdoor Run", "Treadmill Run", "Assault Runner", "800m Intervals", "Track Repeats"],
  "SkiErg": ["Echo Bike", "Assault Bike", "Lat Pulldowns", "DB Skiers", "Concept2 Rower"],
  "Sled Push": ["Heavy Leg Press", "Plate Push", "Hill Sprints", "Heavy Sled Drag", "Treadmill Push"],
  "Sled Pull": ["Seated Cable Rows", "Hand-over-Hand Rope Pull", "Trap Bar Deadlifts", "Kettlebell Deadlifts"],
  "Burpee Broad Jump": ["Devil Press", "Bar-Facing Burpees", "Burpee Box Jump Overs", "Target Burpees"],
  "Rowing": ["Concept2 Row", "Echo Bike", "SkiErg", "Assault Bike", "500m Intervals"],
  "Farmer's Carry": ["Trap Bar Carry", "Heavy Dumbbell Carry", "Overhead Carry", "Kettlebell March"],
  "Sandbag Lunges": ["Dumbbell Walking Lunges", "Barbell Lunges", "Bulgarian Split Squats", "Weighted Step-Ups"],
  "Wall Balls": ["Thrusters", "Dumbbell Thrusters", "Medicine Ball Slams", "Air Squats + Push Press"]
};

export function findWorkoutInStore(workoutId) {
  if (workoutId === undefined || workoutId === null || workoutId === '') return null;
  const list = store.workouts || [];
  const targetStr = String(workoutId);
  const targetLower = targetStr.toLowerCase();
  return list.find(w => w && (
    w.id === workoutId ||
    String(w.id) === targetStr ||
    String(w.id || '').toLowerCase() === targetLower ||
    (w.healthKitId && String(w.healthKitId).toLowerCase() === targetLower)
  )) || null;
}

export function getCurrentWorkoutDetailId() {
  return currentWorkoutDetailId;
}

export function openWorkoutDetail(workoutId, options = {}) {
  if (typeof document === 'undefined') return;

  const workout = findWorkoutInStore(workoutId);
  if (!workout) return;
  
  currentWorkoutDetailId = workout.id || workoutId;

  if (activeMap) {
    try {
      activeMap.remove();
    } catch (e) {
      console.error('Error removing active map:', e);
    }
    activeMap = null;
  }
  
  const typeEl = document.getElementById('detail-activity-type');
  const dateEl = document.getElementById('detail-date');
  const durationValEl = document.getElementById('detail-duration-val');
  const caloriesValEl = document.getElementById('detail-calories-val');
  const hrValEl = document.getElementById('detail-hr-val');
  const burnRateValEl = document.getElementById('detail-burn-rate-val');
  const iconEl = document.getElementById('detail-activity-icon');
  const notesInput = document.getElementById('detail-notes-input');
  
  if (typeEl) {
    if (workout.stravaId && workout.notes && workout.notes.startsWith('Synced: "')) {
      const customTitle = workout.notes.replace('Synced: "', '').replace('"', '');
      typeEl.innerText = customTitle;
    } else {
      typeEl.innerText = `${workout.name || workout.type || 'Workout'}`;
    }
  }
  
  if (dateEl) dateEl.innerText = `${workout.date || ''}${workout.time ? ` at ${workout.time}` : ''}`;
  if (durationValEl) durationValEl.innerText = `${workout.duration || 0}m`;
  if (caloriesValEl) caloriesValEl.innerText = `${workout.calories || 0} kcal`;

  const distanceValEl = document.getElementById('detail-distance-val');
  if (distanceValEl) {
    const totalDist = getWorkoutTotalDistance(workout);
    distanceValEl.innerText = totalDist > 0 ? `${totalDist.toFixed(2)} km` : '--';
  }

  if (hrValEl) {
    if (workout.avgHeartrate) {
      let maxHrText = workout.maxHeartrate ? ` / ${workout.maxHeartrate}` : '';
      hrValEl.innerHTML = `${workout.avgHeartrate}<span style="font-size: 14px; font-weight: 500; color: rgba(244, 63, 94, 0.6); margin-left: 2px;">${maxHrText} bpm</span>`;
    } else {
      hrValEl.innerText = '--';
    }
  }

  if (burnRateValEl) {
    if (workout.calories && workout.duration) {
      const burnRate = (workout.calories / workout.duration).toFixed(1);
      burnRateValEl.innerHTML = `${burnRate} <span style="font-size: 12px; color: var(--text-muted); font-weight: 400;">kcal/min</span>`;
    } else {
      burnRateValEl.innerText = '--';
    }
  }
  
  if (iconEl) {
    let emoji = '💪';
    if (workout.type === 'Run') emoji = '🏃';
    else if (workout.type === 'Cycle') emoji = '🚴';
    else if (workout.type === 'Strength') emoji = '🏋️';
    else if (workout.type === 'Walk') emoji = '🚶';
    else if (workout.type === 'Yoga') emoji = '🧘';
    else if (workout.type === 'Swim') emoji = '🏊';
    else if (workout.type === 'Dance') emoji = '💃';
    else if (workout.type === 'Hyrox' || workout.type === 'Hyrox Training') emoji = '⚡';
    iconEl.innerText = emoji;
  }
  
  const sufferValEl = document.getElementById('detail-suffer-val');
  if (sufferValEl) {
    sufferValEl.innerText = getWorkoutSufferScore(workout);
  }
  const multContainer = document.getElementById('detail-suffer-multiplier-container');
  if (multContainer) {
    const currentMult = workout.sufferMultiplier || 1;
    multContainer.querySelectorAll('.mult-pill').forEach(p => {
      const m = parseInt(p.getAttribute('data-mult') || '1', 10);
      p.classList.toggle('active', m === currentMult);
    });
  }

  if (
    workout.aiSummary &&
    !isRawStationSequenceNotes(workout.aiSummary) &&
    (!workout.notes || isRawStationSequenceNotes(workout.notes))
  ) {
    if (isRawStationSequenceNotes(workout.notes) && !workout.stationSequenceNotes) {
      workout.stationSequenceNotes = workout.notes;
    }
    workout.notes = workout.aiSummary;
  }

  if (notesInput) {
    notesInput.value = workout.notes || workout.aiSummary || '';
  }
  
  recoverInMemoryWorkoutStageNames(workout);

  if (Array.isArray(workout.stages)) {
    let lastValidHr = 0;
    workout.stages.forEach(st => {
      let currentHr = parseInt(st.avgHr, 10);
      if (!currentHr || currentHr === 0 || isNaN(currentHr)) {
        st.avgHr = lastValidHr;
      } else {
        st.avgHr = currentHr;
        lastValidHr = currentHr;
      }
    });
  }
  
  renderDetailStagesEditor(workout);
  
  const editorSection = document.getElementById('detail-stages-editor-section');
  const toggleBtn = document.getElementById('btn-toggle-splits-editor');
  if (editorSection && toggleBtn) {
    editorSection.style.display = 'none';
    toggleBtn.innerText = 'Edit Splits';
  }

  renderReadOnlyDetails(workout);
  const badgeEl = document.getElementById('detail-stages-count-badge');
  if (badgeEl) {
    badgeEl.innerText = (workout.stages ? workout.stages.length : 0) + ' splits';
  }
  const inferLapsBtn = document.getElementById('btn-detail-infer-laps');
  if (inferLapsBtn) {
    inferLapsBtn.style.display = (isStravaAdmin() && workout.stravaId) ? 'inline-flex' : 'none';
  }
  updateStravaPostButtonState(workout);
  showView('view-workout-detail', options);
}

export function closeWorkoutDetail() {
  if (activeMap) {
    try {
      activeMap.remove();
    } catch (e) {}
    activeMap = null;
  }
  showView(getPreviousViewId() || 'view-workouts');
}

export function updateDetailStagesCount() {
  if (typeof document === 'undefined') return;
  const listContainer = document.getElementById('detail-stages-editor-list');
  if (!listContainer) return;
  
  const rows = listContainer.querySelectorAll('.detail-stage-row');
  const countEl = document.getElementById('detail-stages-count-lbl');
  if (countEl) {
    countEl.innerText = `${rows.length} Stages`;
  }
}

export function createDetailStageRow(stage, stageNum, isCustomWorkout = false) {
  const row = document.createElement('div');
  row.className = 'detail-stage-row';
  row.setAttribute('data-stage-num', stageNum);
  row.style.display = 'block';
  row.style.width = '100%';
  
  const stageRawName = stage.stationName || stage.name || '';
  const isRoxByName = stageRawName.toLowerCase() === 'roxzone' || (stage.type && stage.type.toLowerCase() === 'roxzone');
  const typeVal = isRoxByName ? 'RoxZone' : (stage.type || (stageNum % 2 !== 0 ? 'Run' : 'Strength'));
  const isRun = typeVal === 'Run' || typeVal === 'Cardio';
  const isRox = typeVal === 'RoxZone' || typeVal === 'Roxzone';
  const isRest = typeVal === 'Rest';
  const isStrength = !isRox && (typeVal === 'Strength' || typeVal === 'Station');

  const STATIONS = ['SkiErg', 'Sled Push', 'Sled Pull', 'Burpee Broad Jumps', 'Rowing', 'Farmers Carry', 'Sandbag Lunges', 'Wall Balls'];
  const stationName = stage.stationName || stage.name || (isStrength ? (STATIONS[(stageNum - 1) % STATIONS.length] || 'SkiErg') : '');
  const durMin = stage.durationMin !== undefined ? stage.durationMin : Math.floor((stage.duration || stage.duration_seconds || 0) / 60);
  const durSec = stage.durationSec !== undefined ? stage.durationSec : Math.round((stage.duration || stage.duration_seconds || 0) % 60);
  const avgHr = stage.avgHr !== undefined ? stage.avgHr : (stage.avg_hr || 0);
  const rawDistance = stage.distance !== undefined && stage.distance !== null ? stage.distance : (stage.distance_meters || 0);
  const rawReps = stage.reps !== undefined && stage.reps !== null ? stage.reps : 0;
  const isDistMetric = !isStrength || isDistanceStation(stationName, typeVal);
  const metricValue = isDistMetric ? rawDistance : (rawReps > 0 ? rawReps : rawDistance);
  const metricDisplayStr = isDistMetric ? `${metricValue}m` : `${metricValue} reps`;

  let title = '';
  let emoji = '💪';
  if (isRun) { title = 'Run'; emoji = '🏃'; }
  else if (isRox) { title = 'RoxZone'; emoji = '⚡'; }
  else if (isRest) { title = 'Rest'; emoji = '☕'; }
  else if (typeVal === 'Cardio') { title = 'Cardio'; emoji = '🚴'; }
  else { title = stationName || 'Station'; emoji = '💪'; }

  row.innerHTML = `
    <!-- Hidden inputs to preserve saveWorkoutSplits compatibility -->
    <input type="hidden" class="detail-stage-type-select" value="${typeVal}">
    <input type="hidden" class="detail-stage-station-select" value="${stationName}">
    <input type="hidden" class="detail-min-input" value="${durMin}">
    <input type="hidden" class="detail-sec-input" value="${durSec}">
    <input type="hidden" class="detail-stage-metric-input" value="${metricValue}">
    <input type="hidden" class="detail-hr-input" value="${avgHr}">
    
    <!-- Clean Read-Only Flexbox Layout (Two Lines) -->
    <div style="display: flex; flex-direction: column; gap: 8px; padding: 12px 14px; background: rgba(0,0,0,0.2); border: 1px solid rgba(255,255,255,0.05); border-radius: 8px; width: 100%; box-sizing: border-box;">
      
      <!-- Top Row: Stage Info -->
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 11px; font-weight: 800; color: var(--text-muted); text-transform: uppercase; background: rgba(255,255,255,0.05); padding: 2px 6px; border-radius: 4px;">Stage ${stageNum}</span>
        <span class="row-display-title" style="font-weight: 600; font-size: 14px; color: var(--text-primary);">${emoji} ${title}</span>
      </div>
      
      <!-- Bottom Row: Metrics & Edit -->
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <div style="display: flex; align-items: center; gap: 16px;">
          <span class="row-display-time" style="font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: var(--text-primary);">${durMin}m ${durSec}s</span>
          <span class="row-display-dist" style="font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 14px; color: var(--text-secondary);">${metricDisplayStr}</span>
          <span style="font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 14px; color: #f43f5e; display: flex; align-items: center; gap: 4px;">❤️ ${avgHr}</span>
        </div>
        <button type="button" class="btn-edit-stage-modal" style="background: rgba(255,255,255,0.08); border: none; border-radius: 6px; color: var(--text-primary); padding: 8px; cursor: pointer; transition: background 0.2s; display: flex; align-items: center; justify-content: center; width: 32px; height: 32px;" title="Edit Stage">✏️</button>
      </div>

    </div>
  `;

  const editBtn = row.querySelector('.btn-edit-stage-modal');
  if (editBtn) {
    editBtn.addEventListener('click', () => {
      openStageEditorModal(row, stageNum);
    });
  }

  return row;
}

export function renderDetailStagesEditor(workout) {
  if (typeof document === 'undefined') return;
  const listContainer = document.getElementById('detail-stages-editor-list');
  if (!listContainer) return;

  listContainer.innerHTML = '';
  const stages = workout.stages || [];

  if (stages.length === 0) {
    listContainer.innerHTML = `<div id="detail-stages-empty-msg" style="color: var(--text-muted); font-size: 12px; font-style: italic; padding: 10px 0;">No stage splits recorded yet. Tap "+ Add Split" below.</div>`;
    updateDetailStagesCount();
    return;
  }

  const isCustomWorkout = (workout.type && workout.type.toLowerCase().includes('custom')) || (workout.name && workout.name.toLowerCase().includes('custom'));

  stages.forEach((st, idx) => {
    const rowNode = createDetailStageRow(st, idx + 1, isCustomWorkout);
    listContainer.appendChild(rowNode);
  });

  updateDetailStagesCount();
}

/**
 * IDOR-Safe Save Workout Splits
 */
export async function saveWorkoutSplits(workoutId, updatedStages) {
  if (isSavingSplits) {
    console.warn('[Editor] saveWorkoutSplits already in progress. Ignoring duplicate invocation.');
    return;
  }
  isSavingSplits = true;
  const scrollToRestore = savedDetailScrollState || captureDetailScrollState();

  const saveSplitsBtn = typeof document !== 'undefined' ? document.getElementById('btn-detail-save-stages') : null;
  const originalBtnText = saveSplitsBtn ? saveSplitsBtn.innerText : 'Save Splits';
  if (saveSplitsBtn) {
    saveSplitsBtn.disabled = true;
    saveSplitsBtn.innerText = 'Saving Splits...';
    saveSplitsBtn.style.opacity = '0.6';
    saveSplitsBtn.style.cursor = 'not-allowed';
  }

  try {
    const workout = findWorkoutInStore(workoutId);
    if (!workout) return;

    let totalDurationSec = 0;
    let totalDistanceMeters = 0;

    const normalizedStages = (updatedStages || []).map((st, idx) => {
      const sec = (st.duration_seconds !== undefined && st.duration_seconds !== null)
        ? Math.round(st.duration_seconds)
        : (((st.durationMin || 0) * 60) + (st.durationSec || 0));
      const dMin = st.durationMin !== undefined ? st.durationMin : Math.floor(sec / 60);
      const dSec = st.durationSec !== undefined ? st.durationSec : (sec % 60);
      const stageType = st.type || st.stage_type || 'Other';
      const isRun = stageType === 'Run' || stageType === 'Cardio';
      const isRox = stageType === 'RoxZone' || stageType === 'Roxzone';
      const isRest = stageType === 'Rest';

      let stageName = st.stationName || st.name;
      if (!stageName) {
        if (isRun) stageName = stageType === 'Cardio' ? 'Cardio' : 'Run';
        else if (isRox) stageName = 'RoxZone';
        else if (isRest) stageName = 'Rest';
        else stageName = 'Station';
      }

      const dist = st.distance !== undefined ? st.distance : (st.distance_meters || 0);
      const numericDist = Number(dist);
      const validDist = Number.isFinite(numericDist) && numericDist > 0 ? numericDist : 0;

      totalDurationSec += sec;
      if (isRun || isRox) {
        totalDistanceMeters += validDist;
      }

      return {
        ...st,
        stageNum: st.stageNum || idx + 1,
        stage_index: st.stage_index || idx + 1,
        name: stageName,
        stationName: stageName,
        type: stageType,
        stage_type: stageType,
        duration_seconds: sec,
        duration: sec,
        durationMin: dMin,
        durationSec: dSec,
        distance: dist,
        distance_meters: dist,
        avgHr: st.avgHr !== undefined ? st.avgHr : (st.avg_hr || 0),
        maxHr: st.maxHr !== undefined ? st.maxHr : (st.max_hr || 0),
        reps: st.reps !== undefined ? st.reps : null,
        weight: st.weight !== undefined ? st.weight : (st.weight_kg !== undefined ? st.weight_kg : null),
        weight_kg: st.weight_kg !== undefined ? st.weight_kg : (st.weight !== undefined ? st.weight : null),
        metadata: st.metadata || {}
      };
    });

    workout.stages = normalizedStages;
    workout.duration = Math.round(totalDurationSec / 60);
    workout.duration_seconds = totalDurationSec;
    workout.distance = parseFloat((totalDistanceMeters / 1000).toFixed(2));
    workout.total_distance_meters = totalDistanceMeters;

    // Supabase IDOR-Safe Dual-Write Mutation
    const supabase = (typeof window !== 'undefined') ? window.supabaseClient : null;
    let supabaseError = null;
    if (supabase && store.currentUser && store.currentUser.id && !store.isGuest && store.currentUser.id !== 'guest-preview-user') {
      try {
        // 1. Delete existing normalized stages for this workout with Zero-Trust IDOR check
        const stagesTable = typeof supabase.from === 'function' ? supabase.from('workout_stages') : null;
        if (stagesTable && typeof stagesTable.delete === 'function') {
          const { error: delError } = await stagesTable
            .delete()
            .eq('workout_id', workoutId)
            .eq('user_id', store.currentUser.id);

          if (delError) {
            console.error('Error clearing old workout_stages:', delError);
            supabaseError = delError;
          }

          // 2. Insert new normalized stage rows
          if (normalizedStages.length > 0 && typeof stagesTable.insert === 'function') {
            const stageRows = normalizedStages.map((st, idx) => ({
              workout_id: workoutId,
              user_id: store.currentUser.id,
              stage_index: idx + 1,
              name: st.name,
              stage_type: st.stage_type,
              duration_seconds: st.duration_seconds,
              distance_meters: st.distance_meters,
              avg_hr: st.avgHr || null,
              max_hr: st.maxHr || null,
              reps: st.reps !== undefined ? st.reps : null,
              weight_kg: st.weight_kg !== undefined ? st.weight_kg : null,
              metadata: st.metadata || {}
            }));

            const { error: insError } = await stagesTable.insert(stageRows);

            if (insError) {
              console.error('Error inserting normalized workout_stages:', insError);
              supabaseError = insError;
            }
          }
        }

        // 3. Update parent workout with durations, distance, stages JSONB snapshot, and metadata
        const existingMeta = workout.rawMetadata && typeof workout.rawMetadata === 'object' ? workout.rawMetadata : {};
        const preservedStationSeq = workout.stationSequenceNotes ||
          existingMeta.stationSequenceNotes ||
          (isRawStationSequenceNotes(existingMeta.notes) ? existingMeta.notes : undefined);
        const meta = {
          ...existingMeta,
          stages: workout.stages,
          zones: workout.zones,
          notes: workout.notes,
          avgHeartrate: workout.avgHeartrate,
          maxHeartrate: workout.maxHeartrate,
          sufferScore: workout.sufferScore,
          aiSummary: workout.aiSummary,
          ...(workout.userNotesEdited !== undefined ? { userNotesEdited: workout.userNotesEdited } : {}),
          ...(workout.notesUpdatedAt !== undefined ? { notesUpdatedAt: workout.notesUpdatedAt } : {}),
          ...(preservedStationSeq ? { stationSequenceNotes: preservedStationSeq } : {}),
          ...(workout.healthKitId !== undefined ? { healthKitId: workout.healthKitId } : {}),
          ...(workout.stravaId !== undefined ? { stravaId: workout.stravaId } : {}),
          ...(workout.stravaRunId !== undefined ? { stravaRunId: workout.stravaRunId } : {}),
          ...(workout.sufferMultiplier !== undefined ? { sufferMultiplier: workout.sufferMultiplier } : {}),
          ...(workout.excludeFromStats !== undefined ? { excludeFromStats: workout.excludeFromStats } : {}),
          ...(workout.isFavorite !== undefined ? { isFavorite: workout.isFavorite } : {}),
          ...(workout.elevationGain !== undefined ? { elevationGain: workout.elevationGain } : {})
        };
        workout.rawMetadata = meta;
        const workoutsTable = typeof supabase.from === 'function' ? supabase.from('workouts') : null;
        if (workoutsTable && typeof workoutsTable.update === 'function') {
          const { error } = await workoutsTable
            .update({
              duration_seconds: totalDurationSec,
              total_distance_meters: totalDistanceMeters,
              stages: workout.stages,
              healthkit_raw_metadata: meta
            })
            .eq('id', workoutId)
            .eq('user_id', store.currentUser.id);

          if (error) {
            console.error('Error saving splits to Supabase:', error);
            supabaseError = error;
          }
        }
      } catch (e) {
        console.error('Exception updating splits in Supabase:', e);
        supabaseError = e;
      }
      if (supabaseError && typeof saveOfflineWorkout === 'function') {
        await saveOfflineWorkout(workout);
      } else if (!supabaseError && typeof deleteOfflineWorkout === 'function') {
        await deleteOfflineWorkout(workout.id || workoutId);
      }
    }

    saveStateToStorage();
    openWorkoutDetail(workoutId, { preserveScroll: true });
    restoreDetailScrollState(scrollToRestore);
    savedDetailScrollState = null;
    if (typeof window !== 'undefined') {
      if (typeof window.renderWorkouts === 'function') window.renderWorkouts();
      if (typeof window.renderDashboard === 'function') window.renderDashboard();
      if (typeof window.showToast === 'function') {
        if (supabaseError) {
          window.showToast('Splits saved locally (offline). Will sync when online.', 'info');
        } else {
          window.showToast('Stage splits saved successfully!');
        }
      }
    }
  } finally {
    isSavingSplits = false;
    if (saveSplitsBtn) {
      saveSplitsBtn.disabled = false;
      saveSplitsBtn.innerText = originalBtnText;
      saveSplitsBtn.style.opacity = '1';
      saveSplitsBtn.style.cursor = 'pointer';
    }
  }
}

export function openStationAlternativesModal() {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('modal-station-alternatives') || document.getElementById('dialog-station-alternatives');
  if (modal && typeof modal.showModal === 'function') {
    modal.showModal();
  } else {
    // Navigate to settings view where alternatives editor is embedded
    showView('view-settings');
    const container = document.getElementById('station-alts-chips-container');
    if (container && typeof container.scrollIntoView === 'function') {
      container.scrollIntoView({ behavior: 'smooth' });
    }
  }
}

export function closeStationAlternativesModal() {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('modal-station-alternatives') || document.getElementById('dialog-station-alternatives');
  if (modal && typeof modal.close === 'function') {
    modal.close();
  }
}

export function getStationAlternatives() {
  if (!store.stationAlternatives || typeof store.stationAlternatives !== 'object') {
    store.stationAlternatives = JSON.parse(JSON.stringify(DEFAULT_STATION_ALTERNATIVES));
  }
  Object.keys(DEFAULT_STATION_ALTERNATIVES).forEach(k => {
    if (!Array.isArray(store.stationAlternatives[k])) {
      store.stationAlternatives[k] = [...DEFAULT_STATION_ALTERNATIVES[k]];
    }
  });
  return store.stationAlternatives;
}

export function saveStationAlternatives(alts) {
  store.stationAlternatives = alts;
  saveStateToStorage();

  if (typeof window !== 'undefined' && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.stationAlternativesHandler) {
    try {
      window.webkit.messageHandlers.stationAlternativesHandler.postMessage(alts);
    } catch (e) {
      console.warn("Failed to dispatch station alternatives to iOS native bridge:", e);
    }
  }
}

export function renderStationAlternativesChips() {
  if (typeof document === 'undefined') return;
  const selectEl = document.getElementById('select-alt-station');
  const container = document.getElementById('station-alts-chips-container');
  if (!selectEl || !container) return;

  const currentStation = selectEl.value;
  const alts = getStationAlternatives();
  const list = alts[currentStation] || [];

  container.innerHTML = '';

  if (list.length === 0) {
    const emptyMsg = document.createElement('span');
    emptyMsg.style.cssText = 'color: var(--text-muted); font-size: 12px; font-style: italic; padding: 4px 0;';
    emptyMsg.textContent = 'No alternatives configured for this station.';
    container.appendChild(emptyMsg);
    return;
  }

  list.forEach((item, index) => {
    const chip = document.createElement('div');
    chip.style.cssText = 'display: inline-flex; align-items: center; gap: 8px; padding: 5px 12px; background: rgba(255,255,255,0.08); border: 1px solid var(--border-light); border-radius: 20px; font-size: 12px; color: var(--text-primary); transition: background 0.15s ease;';

    const label = document.createElement('span');
    label.textContent = item;
    chip.appendChild(label);

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.innerHTML = '&times;';
    removeBtn.setAttribute('aria-label', `Remove ${item}`);
    removeBtn.style.cssText = 'background: none; border: none; color: var(--text-muted); cursor: pointer; font-size: 16px; line-height: 1; padding: 0 0 0 2px; display: flex; align-items: center; transition: color 0.15s ease;';
    removeBtn.onclick = () => {
      const updated = getStationAlternatives();
      if (updated[currentStation]) {
        updated[currentStation].splice(index, 1);
        saveStationAlternatives(updated);
        renderStationAlternativesChips();
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast(`Removed "${item}" from ${currentStation}`);
        }
      }
    };

    chip.appendChild(removeBtn);
    container.appendChild(chip);
  });
}

export function setupStationAlternativesEditor() {
  if (typeof document === 'undefined') return;
  const selectEl = document.getElementById('select-alt-station');
  const inputEl = document.getElementById('input-new-alt-name');
  const addBtn = document.getElementById('btn-add-station-alt');

  if (!selectEl) return;

  renderStationAlternativesChips();
  selectEl.addEventListener('change', () => renderStationAlternativesChips());

  const handleAdd = () => {
    if (!inputEl) return;
    const val = inputEl.value.trim();
    if (!val) return;
    const station = selectEl.value;
    const alts = getStationAlternatives();
    if (!alts[station]) alts[station] = [];

    const exists = alts[station].some(a => a.toLowerCase() === val.toLowerCase());
    if (exists) {
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast(`"${val}" already exists for ${station}`, 'warning');
      }
      return;
    }

    alts[station].push(val);
    saveStationAlternatives(alts);
    inputEl.value = '';
    renderStationAlternativesChips();
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(`✅ Added "${val}" to ${station}`);
    }
  };

  if (addBtn) addBtn.addEventListener('click', handleAdd);
  if (inputEl) {
    inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    });
  }
}

export function confirmDeleteWorkout(workoutId) {
  const confirmed = typeof confirm === 'function' ? confirm('Are you sure you want to delete this workout? This action cannot be undone.') : true;
  if (confirmed) {
    deleteWorkout(workoutId);
    closeWorkoutDetail();
  }
}

export function setupWorkoutDetail() {
  if (typeof document === 'undefined' || workoutDetailSetupDone) return;
  workoutDetailSetupDone = true;
  setupStageEditorModal();
  setupExpandedHeartRateModal();

  const backBtn = document.getElementById('btn-back-to-workouts');
  if (backBtn) {
    backBtn.addEventListener('click', () => closeWorkoutDetail());
  }

  const shareCardBtn = document.getElementById('btn-detail-share-card');
  if (shareCardBtn) {
    shareCardBtn.addEventListener('click', () => {
      const workout = findWorkoutInStore(currentWorkoutDetailId);
      if (!workout) {
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast('No workout selected to share.', 'warning');
        }
        return;
      }
      openShareCardModal(workout);
    });
  }

  const deleteBtn = document.getElementById('btn-delete-detail-workout');
  if (deleteBtn) {
    deleteBtn.addEventListener('click', () => {
      if (currentWorkoutDetailId) confirmDeleteWorkout(currentWorkoutDetailId);
    });
  }

  const stageDurationContainer = document.getElementById('detail-stage-duration-container');
  if (stageDurationContainer) {
    stageDurationContainer.addEventListener('click', (e) => {
      const target = e.target;
      if (!target || typeof target.closest !== 'function') return;
      const btn = target.closest('.btn-edit-stage-row');
      if (!btn) return;
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
      const idx = parseInt(btn.getAttribute('data-stage-idx'), 10);
      if (!isNaN(idx)) {
        openStageEditorForIndex(idx);
      }
    });
  }

  const toggleEditorBtn = document.getElementById('btn-toggle-splits-editor');
  if (toggleEditorBtn) {
    toggleEditorBtn.addEventListener('click', () => {
      const editorSection = document.getElementById('detail-stages-editor-section');
      if (editorSection) {
        if (editorSection.style.display === 'none') {
          editorSection.style.display = 'flex';
          toggleEditorBtn.innerText = 'Cancel Edit';
        } else {
          editorSection.style.display = 'none';
          toggleEditorBtn.innerText = 'Edit Splits';
        }
      }
    });
  }

  const addSplitBtn = document.getElementById('btn-detail-add-stage');
  if (addSplitBtn) {
    addSplitBtn.addEventListener('click', () => {
      const workout = (store.workouts || []).find(w => w && (w.id === currentWorkoutDetailId || String(w.id) === String(currentWorkoutDetailId)));
      const existingStages = Array.isArray(workout?.stages) ? workout.stages : [];
      const stageIdx = existingStages.length;
      const stageNum = stageIdx + 1;
      const isOdd = stageNum % 2 !== 0;

      const newStage = {
        stageNum,
        stage_index: stageNum,
        type: isOdd ? 'Run' : 'Station',
        stage_type: isOdd ? 'Run' : 'Station',
        name: isOdd ? 'Run' : 'SkiErg',
        stationName: isOdd ? 'Run' : 'SkiErg',
        durationMin: 0,
        durationSec: 0,
        duration: 0,
        duration_seconds: 0,
        avgHr: workout?.avgHeartrate || 0,
        distance: 0,
        distance_meters: 0
      };

      const listContainer = document.getElementById('detail-stages-editor-list');
      if (listContainer) {
        const emptyMsg = document.getElementById('detail-stages-empty-msg');
        if (emptyMsg) emptyMsg.remove();
        const rowNode = createDetailStageRow(newStage, stageNum);
        listContainer.appendChild(rowNode);
        updateDetailStagesCount();
      }

      openStageEditorForIndex(stageIdx, true, newStage);
    });
  }

  const saveSplitsBtn = document.getElementById('btn-detail-save-stages');
  if (saveSplitsBtn) {
    saveSplitsBtn.addEventListener('click', async () => {
      if (!currentWorkoutDetailId) return;
      if (isSavingSplits) return;
      const listContainer = document.getElementById('detail-stages-editor-list');
      if (!listContainer) return;

      const rows = listContainer.querySelectorAll('.detail-stage-row');
      const newStages = [];
      const currentWorkout = (store.workouts || []).find(w => w.id === currentWorkoutDetailId);
      const existingStages = currentWorkout?.stages || [];

      rows.forEach((row, idx) => {
        const typeSelect = row.querySelector('.detail-stage-type-select').value;
        const stationSelect = row.querySelector('.detail-stage-station-select')?.value || '';
        const minVal = parseInt(row.querySelector('.detail-min-input').value, 10) || 0;
        const secVal = parseInt(row.querySelector('.detail-sec-input').value, 10) || 0;
        const metricVal = parseFloat(row.querySelector('.detail-stage-metric-input')?.value) || 0;
        const hrVal = parseInt(row.querySelector('.detail-hr-input')?.value, 10) || 0;

        const prevStage = existingStages[idx] || {};

        let stageName = (typeSelect === 'Station' || typeSelect === 'Strength') ? stationSelect : null;
        if (!stageName) {
          if (typeSelect === 'Run') stageName = 'Run';
          else if (typeSelect === 'RoxZone') stageName = 'RoxZone';
          else if (typeSelect === 'Rest') stageName = 'Rest';
          else if (typeSelect === 'Cardio') stageName = 'Cardio';
          else stageName = 'Station';
        }

        const durSec = (minVal * 60) + secVal;
        const isStationRow = typeSelect === 'Station' || typeSelect === 'Strength';
        const isDistMetric = !isStationRow || isDistanceStation(stageName, typeSelect);

        newStages.push({
          ...prevStage,
          stageNum: idx + 1,
          type: typeSelect,
          name: stageName,
          stationName: stageName,
          durationMin: minVal,
          durationSec: secVal,
          duration: durSec,
          duration_seconds: durSec,
          distance: isDistMetric ? metricVal : 0,
          distance_meters: isDistMetric ? metricVal : 0,
          avgHr: hrVal,
          reps: isDistMetric ? (prevStage.reps !== undefined ? prevStage.reps : null) : metricVal,
          weight_kg: prevStage.weight_kg !== undefined ? prevStage.weight_kg : (prevStage.weight !== undefined ? prevStage.weight : null),
          metadata: prevStage.metadata || {}
        });
      });

      await saveWorkoutSplits(currentWorkoutDetailId, newStages);
    });
  }

  setupStationAlternativesEditor();

  const saveNotesBtn = document.getElementById('btn-save-detail-notes');
  if (saveNotesBtn) {
    saveNotesBtn.addEventListener('click', async () => {
      const workoutId = getCurrentWorkoutDetailId();
      if (!workoutId) return;
      const notesInput = document.getElementById('detail-notes-input');
      const val = notesInput ? notesInput.value.trim() : '';
      await saveWorkoutNotes(workoutId, val);
    });
  }

  const detailReevaluateBtn = document.getElementById('btn-detail-reevaluate-notes');
  if (detailReevaluateBtn) {
    detailReevaluateBtn.addEventListener('click', async () => {
      const workoutId = getCurrentWorkoutDetailId();
      if (!workoutId) return;
      const workout = findWorkoutInStore(workoutId);
      if (!workout) return;

      const originalHtml = detailReevaluateBtn.innerHTML;
      detailReevaluateBtn.disabled = true;
      detailReevaluateBtn.innerHTML = '<span>🤖 Analyzing...</span>';

      const notesInput = document.getElementById('detail-notes-input');
      if (notesInput) {
        notesInput.value = "🤖 Generating updated AI Coach Evaluation...";
      }

      try {
        const supabase = (typeof window !== 'undefined' && window.supabaseClient) || store.supabaseClient;
        const outputText = await runAIEvaluation(workout, {
          isDistanceStation,
          customInstructions: store.geminiInstructions,
          customKey: store.geminiApiKey,
          supabaseClient: supabase
        });

        if (outputText) {
          if (notesInput) {
            notesInput.value = outputText;
          }
          const expDialog = document.getElementById('expanded-notes-dialog');
          if (expDialog && expDialog.open) {
            const expTextarea = document.getElementById('expanded-notes-textarea');
            if (expTextarea) expTextarea.value = outputText;
          }
          await saveWorkoutNotes(workout.id, outputText);
        }
      } catch (err) {
        console.error('Error running AI evaluation:', err);
        if (notesInput) {
          notesInput.value = workout.notes || workout.aiSummary || '';
        }
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast(err.message || 'Failed to generate AI evaluation', 'error');
        }
      } finally {
        detailReevaluateBtn.disabled = false;
        detailReevaluateBtn.innerHTML = originalHtml;
      }
    });
  }

  const detailAiSummaryBtn = document.getElementById('btn-detail-ai-summary');
  if (detailAiSummaryBtn) {
    detailAiSummaryBtn.addEventListener('click', async () => {
      const workoutId = getCurrentWorkoutDetailId();
      if (!workoutId) return;
      await generateAISummary(workoutId);
    });
  }

  const reevaluateSummaryBtn = document.getElementById('btn-reevaluate-summary');
  if (reevaluateSummaryBtn) {
    reevaluateSummaryBtn.addEventListener('click', async () => {
      const workoutId = getCurrentWorkoutDetailId();
      if (!workoutId) return;
      await generateAISummary(workoutId, { forceReevaluate: true });
    });
  }

  const backFromAiSummaryBtn = document.getElementById('btn-back-from-ai-summary');
  if (backFromAiSummaryBtn) {
    backFromAiSummaryBtn.addEventListener('click', () => {
      const workoutId = getCurrentWorkoutDetailId();
      if (workoutId) {
        openWorkoutDetail(workoutId, { preserveScroll: true });
      } else {
        showView('view-workouts');
      }
    });
  }

  const multContainer = document.getElementById('detail-suffer-multiplier-container');
  if (multContainer) {
    multContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('.mult-pill');
      if (!btn) return;

      const multVal = parseInt(btn.getAttribute('data-mult') || '1', 10);
      const workoutId = getCurrentWorkoutDetailId();
      if (!workoutId) return;
      const workout = findWorkoutInStore(workoutId);
      if (workout) {
        workout.sufferMultiplier = multVal;
        multContainer.querySelectorAll('.mult-pill').forEach(p => p.classList.remove('active'));
        btn.classList.add('active');
        saveStateToStorage();
        openWorkoutDetail(workout.id || workoutId, { preserveScroll: true });
        if (typeof window !== 'undefined' && typeof window.renderWorkouts === 'function') {
          window.renderWorkouts();
        }
      }
    });
  }

  const postStravaBtn = document.getElementById('btn-detail-post-strava');
  if (postStravaBtn) {
    postStravaBtn.addEventListener('click', async () => {
      if (!currentWorkoutDetailId) return;
      const workout = findWorkoutInStore(currentWorkoutDetailId);
      if (!workout) return;
      await handlePostWorkoutToStrava(workout);
    });
  }

  const repostStravaBtn = document.getElementById('btn-detail-repost-strava');
  if (repostStravaBtn) {
    repostStravaBtn.addEventListener('click', async () => {
      if (!currentWorkoutDetailId) return;
      const workout = findWorkoutInStore(currentWorkoutDetailId);
      if (!workout) return;
      const confirmed = typeof confirm === 'function' ? confirm('Are you sure you want to re-post this workout to Strava? This may create a duplicate activity if you have not deleted the previous one on Strava.') : true;
      if (!confirmed) return;
      await handlePostWorkoutToStrava(workout, { forceRepost: true, buttonEl: repostStravaBtn });
    });
  }

  const inferLapsBtn = document.getElementById('btn-detail-infer-laps');
  if (inferLapsBtn) {
    inferLapsBtn.addEventListener('click', async () => {
      if (!currentWorkoutDetailId) return;
      const workout = findWorkoutInStore(currentWorkoutDetailId);
      if (!workout || !workout.stravaId) return;

      const originalText = inferLapsBtn.innerText;
      inferLapsBtn.innerText = '⏳ Fetching Laps...';
      inferLapsBtn.disabled = true;

      try {
        if (store.strava) {
          store.strava = await refreshStravaTokenIfExpired(store.strava, store.strava.clientId, store.strava.clientSecret);
          saveStateToStorage();
          if (typeof window !== 'undefined' && typeof window.saveUserSettingsToCloud === 'function') {
            window.saveUserSettingsToCloud();
          }
        }
        const laps = await fetchStravaActivityLaps(workout.stravaId, store.strava?.accessToken);
        if (!laps || laps.length === 0) {
          if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
            window.showToast('No laps found for this Strava activity.', 'warning');
          }
        } else {
          const inferredStages = inferSplitsFromStravaLaps(laps, workout.type);
          await saveWorkoutSplits(workout.id, inferredStages);
          if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
            window.showToast(`Inferred ${inferredStages.length} splits from Strava laps!`);
          }
        }
      } catch (err) {
        console.error('Error inferring splits from Strava laps:', err);
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast(err.message || 'Failed to fetch laps from Strava.', 'error');
        }
      } finally {
        inferLapsBtn.innerText = originalText;
        inferLapsBtn.disabled = false;
      }
    });
  }
}

export function updateStravaPostButtonState(workout) {
  if (typeof document === 'undefined') return;
  const postBtn = document.getElementById('btn-detail-post-strava');
  const repostBtn = document.getElementById('btn-detail-repost-strava');
  if (!postBtn) return;

  if (!isStravaAdmin() || !workout || !store.strava || !store.strava.accessToken) {
    postBtn.style.display = 'none';
    if (repostBtn) repostBtn.style.display = 'none';
    return;
  }

  postBtn.style.display = 'inline-flex';
  if (workout.stravaId) {
    postBtn.innerHTML = '✅ Posted to Strava';
    postBtn.disabled = false;
    postBtn.style.background = 'rgba(16, 185, 129, 0.15)';
    postBtn.style.color = '#10b981';
    postBtn.style.borderColor = 'rgba(16, 185, 129, 0.3)';
    postBtn.title = 'Click to view on Strava';
    postBtn.onclick = (e) => {
      e.stopPropagation();
      if (typeof window !== 'undefined' && typeof window.open === 'function') {
        window.open(`https://www.strava.com/activities/${workout.stravaId}`, '_blank');
      }
    };
    if (repostBtn) {
      repostBtn.style.display = 'inline-flex';
      repostBtn.disabled = false;
      repostBtn.innerHTML = '🔄 Re-post';
    }
  } else {
    postBtn.innerHTML = '🚀 Post to Strava';
    postBtn.disabled = false;
    postBtn.style.background = 'rgba(252, 76, 2, 0.15)';
    postBtn.style.color = '#fc4c02';
    postBtn.style.borderColor = 'rgba(252, 76, 2, 0.35)';
    postBtn.title = 'Upload workout with splits & HR to Strava';
    postBtn.onclick = null;
    if (repostBtn) {
      repostBtn.style.display = 'none';
    }
  }
}

export async function updateWorkoutInCloud(workout) {
  const supabase = (typeof window !== 'undefined' && window.supabaseClient) || store.supabaseClient;
  if (!supabase || !store.currentUser || !store.currentUser.id || store.isGuest || store.currentUser.id === 'guest-preview-user' || !workout) return;
  try {
    const existingMeta = workout.rawMetadata && typeof workout.rawMetadata === 'object' ? workout.rawMetadata : {};
    const preservedStationSeq = workout.stationSequenceNotes ||
      existingMeta.stationSequenceNotes ||
      (isRawStationSequenceNotes(existingMeta.notes) ? existingMeta.notes : undefined);
    const meta = {
      ...existingMeta,
      stages: workout.stages,
      zones: workout.zones,
      notes: workout.notes,
      avgHeartrate: workout.avgHeartrate,
      maxHeartrate: workout.maxHeartrate,
      sufferScore: workout.sufferScore,
      aiSummary: workout.aiSummary,
      ...(workout.userNotesEdited !== undefined ? { userNotesEdited: workout.userNotesEdited } : {}),
      ...(workout.notesUpdatedAt !== undefined ? { notesUpdatedAt: workout.notesUpdatedAt } : {}),
      ...(preservedStationSeq ? { stationSequenceNotes: preservedStationSeq } : {}),
      ...(workout.healthKitId !== undefined ? { healthKitId: workout.healthKitId } : {}),
      ...(workout.stravaId !== undefined ? { stravaId: workout.stravaId } : {}),
      ...(workout.stravaRunId !== undefined ? { stravaRunId: workout.stravaRunId } : {}),
      ...(workout.sufferMultiplier !== undefined ? { sufferMultiplier: workout.sufferMultiplier } : {}),
      ...(workout.excludeFromStats !== undefined ? { excludeFromStats: workout.excludeFromStats } : {}),
      ...(workout.isFavorite !== undefined ? { isFavorite: workout.isFavorite } : {}),
      ...(workout.elevationGain !== undefined ? { elevationGain: workout.elevationGain } : {})
    };
    workout.rawMetadata = meta;

    const { error } = await supabase
      .from('workouts')
      .update({
        activity_type: workout.type || 'RoxKit Training',
        duration_seconds: Math.round((workout.duration || 0) * 60),
        total_energy_burned_kcal: Math.round(workout.calories || 0),
        total_distance_meters: Math.round((workout.distance || 0) * 1000),
        healthkit_raw_metadata: meta
      })
      .eq('id', workout.id)
      .eq('user_id', store.currentUser.id);
    if (error) {
      console.warn('Could not update workout in cloud:', error);
    } else if (typeof deleteOfflineWorkout === 'function') {
      await deleteOfflineWorkout(workout.id);
    }
  } catch (e) {
    console.warn('Error updating workout in cloud:', e);
  }
}

export async function handlePostWorkoutToStrava(workout, options = {}) {
  const forceRepost = !!options.forceRepost;
  if (!store.strava || !store.strava.accessToken) {
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast('Connect Strava in Settings first.', 'error');
    }
    return;
  }

  if (!forceRepost && workout.stravaId) {
    if (typeof window !== 'undefined' && typeof window.open === 'function') {
      window.open(`https://www.strava.com/activities/${workout.stravaId}`, '_blank');
    }
    return;
  }

  const postBtn = typeof document !== 'undefined' ? document.getElementById('btn-detail-post-strava') : null;
  const activeBtn = options.buttonEl || postBtn;
  if (activeBtn) {
    activeBtn.disabled = true;
    activeBtn.innerHTML = '⏳ Uploading TCX...';
  }

  try {
    store.strava = await refreshStravaTokenIfExpired(store.strava, store.strava.clientId, store.strava.clientSecret);
    saveStateToStorage();
    if (typeof window !== 'undefined' && typeof window.saveUserSettingsToCloud === 'function') {
      window.saveUserSettingsToCloud();
    }

    const result = await apiPostWorkoutToStrava(workout, store.strava.accessToken, {
      ...options,
      onProgress: (msg) => {
        if (activeBtn) activeBtn.innerHTML = msg;
      },
      onWarning: (warnMsg) => {
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast(warnMsg, 'warning');
        }
      }
    });

    if (result && result.stravaId) {
      workout.stravaId = String(result.stravaId);
    }
    if (result && result.stravaRunId) {
      workout.stravaRunId = String(result.stravaRunId);
    }

    saveStateToStorage();
    await updateWorkoutInCloud(workout);
    updateStravaPostButtonState(workout);

    const inferLapsBtn = typeof document !== 'undefined' ? document.getElementById('btn-detail-infer-laps') : null;
    if (inferLapsBtn) {
      inferLapsBtn.style.display = (isStravaAdmin() && workout.stravaId) ? 'inline-flex' : 'none';
    }

    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      if (result.status === 'duplicate_updated') {
        window.showToast('Activity already existed on Strava — updated its title & splits! 🎉');
      } else if (result.status === 'processing') {
        window.showToast('Uploaded to Strava! Activity is still processing on Strava.');
      } else if (result.stravaRunId) {
        window.showToast('Workout + Companion Run posted to Strava! 🚀');
      } else {
        window.showToast(forceRepost ? 'Workout re-posted to Strava! 🚀' : 'Workout posted to Strava! 🚀');
      }
    }
    return result;
  } catch (err) {
    console.error('Error posting to Strava:', err);
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(err.message || 'Failed to post workout to Strava.', 'error');
    }
    updateStravaPostButtonState(workout);
    throw err;
  }
}

export function buildHyroxStageList() {
  if (typeof document === 'undefined') return;
  const container = document.getElementById('hyrox-stages-container');
  const stageCountInput = document.getElementById('hyrox-stage-count');
  if (!container || !stageCountInput) return;
  
  const count = parseInt(stageCountInput.value, 10) || 8;
  container.innerHTML = '';
  
  const speedInput = document.getElementById('hyrox-run-speed');
  const currentSpeed = parseFloat(speedInput ? speedInput.value : 5.0) || 5.0;

  for (let i = 1; i <= count; i++) {
    const row = document.createElement('div');
    row.className = 'hyrox-stage-row';
    
    const isEven = i % 2 === 0;
    const defaultType = isEven ? 'Strength' : 'Run';
    const stationIndex = isEven ? Math.floor((i / 2 - 1) % 8) : 0;
    const durMin = defaultType === 'Run' ? 5 : 4;
    const durSec = 0;
    
    let defaultDist = 0;
    if (defaultType === 'Run') {
      defaultDist = Math.round(((durMin * 60 + durSec) / 3600) * currentSpeed * 1609.344);
    }

    row.innerHTML = `
      <div class="stage-num-lbl">Stage ${i}</div>
      <select class="stage-type-select" data-stage="${i}">
        <option value="Run" ${defaultType === 'Run' ? 'selected' : ''}>🏃 Run</option>
        <option value="Strength" ${defaultType === 'Strength' ? 'selected' : ''}>💪 Station</option>
      </select>
      <select class="stage-station-select ${defaultType === 'Run' ? 'hidden' : ''}" data-stage="${i}">
        ${HYROX_STATIONS.map((station, idx) => `
          <option value="${station}" ${stationIndex === idx ? 'selected' : ''}>${idx + 1}. ${station}</option>
        `).join('')}
      </select>
      <div class="stage-duration-input-group">
        <input type="number" class="stage-min-input" min="0" max="999" value="${durMin}" required>
        <span>m</span>
        <input type="number" class="stage-sec-input" min="0" max="59" value="00" required>
        <span>s</span>
      </div>
      <div class="stage-dist-input-group" style="display: flex; align-items: center; gap: 2px;">
        <input type="number" class="stage-dist-input" min="0" max="50000" value="${defaultDist || ''}" placeholder="dist (m)" style="width: 65px; padding: 4px; font-size: 11px; text-align: center; border-radius: 4px; border: 1px solid var(--border-light); background: rgba(0,0,0,0.2); color: var(--text-primary);" title="Distance in meters">
        <span style="font-size: 11px; color: var(--text-muted);">m</span>
      </div>
    `;
    
    const typeSelect = row.querySelector('.stage-type-select');
    const stationSelect = row.querySelector('.stage-station-select');
    
    const updateRowDistance = () => {
      const isRun = typeSelect.value === 'Run';
      const isRox = typeSelect.value === 'Strength' && stationSelect.value === 'RoxZone';
      const min = parseInt(row.querySelector('.stage-min-input').value, 10) || 0;
      const sec = parseInt(row.querySelector('.stage-sec-input').value, 10) || 0;
      const totalSec = (min * 60) + sec;
      const distInput = row.querySelector('.stage-dist-input');
      
      if (isRun && distInput) {
        const speedVal = parseFloat(document.getElementById('hyrox-run-speed')?.value || 5.0) || 5.0;
        distInput.value = Math.round((totalSec / 3600) * speedVal * 1609.344);
      } else if (isRox && distInput) {
        distInput.value = Math.round((totalSec / 3600) * 3.0 * 1609.344);
      }
    };

    
  const numInputs = [
    document.getElementById('modal-stage-min'),
    document.getElementById('modal-stage-sec'),
    document.getElementById('modal-stage-dist')
  ];
  numInputs.forEach(input => {
    if(input) {
      input.addEventListener('focus', function() {
        this.select();
        resetOnNextInput = true;
      });
    }
  });

  typeSelect.addEventListener('change', () => {
      if (typeSelect.value === 'Strength') {
        stationSelect.classList.remove('hidden');
      } else {
        stationSelect.classList.add('hidden');
      }
      updateRowDistance();
      recalculateHyroxTotal();
    });
    
    stationSelect.addEventListener('change', () => {
      updateRowDistance();
      recalculateHyroxTotal();
    });

    row.querySelectorAll('.stage-min-input, .stage-sec-input').forEach(input => {
      input.addEventListener('input', () => {
        updateRowDistance();
        recalculateHyroxTotal();
      });
    });
    
    container.appendChild(row);
  }
  recalculateHyroxTotal();
}

export function recalculateHyroxTotal() {
  if (typeof document === 'undefined') return;
  const container = document.getElementById('hyrox-stages-container');
  if (!container) return;
  
  const rows = container.querySelectorAll('.hyrox-stage-row');
  let totalSeconds = 0;
  
  rows.forEach(row => {
    const min = parseInt(row.querySelector('.stage-min-input').value, 10) || 0;
    const sec = parseInt(row.querySelector('.stage-sec-input').value, 10) || 0;
    totalSeconds += (min * 60) + sec;
  });
  
  const durationInput = document.getElementById('workout-duration');
  if (durationInput) {
    durationInput.value = (totalSeconds / 60).toFixed(1);
  }
}

export function setupModal() {
  if (typeof document === 'undefined') return;
  const dialog = document.getElementById('workout-dialog');
  const openBtn = document.getElementById('btn-quick-workout');
  const closeBtn = document.getElementById('close-dialog-btn');
  const cancelBtn = document.getElementById('btn-cancel-workout');
  const form = document.getElementById('workout-form');
  
  if (!dialog || !openBtn) return;
  
  const typeSelect = document.getElementById('workout-type');
  const hyroxSection = document.getElementById('hyrox-section');
  const durationInput = document.getElementById('workout-duration');
  
  openBtn.addEventListener('click', () => dialog.showModal());
  
  const closeModal = () => {
    if (form) form.reset();
    if (hyroxSection) hyroxSection.classList.add('hidden');
    const distGroup = document.getElementById('distance-group');
    if (distGroup) distGroup.classList.add('hidden');
    const distVal = document.getElementById('workout-distance');
    if (distVal) distVal.value = '';
    
    const stagesContainer = document.getElementById('hyrox-stages-container');
    if (stagesContainer) stagesContainer.innerHTML = '';
    if (durationInput) durationInput.readOnly = false;
    dialog.close();
  };
  
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (cancelBtn) cancelBtn.addEventListener('click', closeModal);
}

export async function saveWorkoutNotes(workoutId, notesText) {
  if (!workoutId) return false;
  const resolvedNotesText = notesText !== undefined
    ? notesText
    : (typeof document !== 'undefined' && document.getElementById('detail-notes-input')
      ? document.getElementById('detail-notes-input').value.trim()
      : '');
  const workout = findWorkoutInStore(workoutId);
  if (!workout) return false;

  const existingMeta = workout.rawMetadata && typeof workout.rawMetadata === 'object' ? workout.rawMetadata : {};
  const preservedStationSeq = workout.stationSequenceNotes ||
    existingMeta.stationSequenceNotes ||
    (isRawStationSequenceNotes(workout.notes) ? workout.notes : (isRawStationSequenceNotes(existingMeta.notes) ? existingMeta.notes : undefined));

  const nowMs = Date.now();
  workout.notes = resolvedNotesText;
  workout.aiSummary = resolvedNotesText;
  workout.userNotesEdited = true;
  workout.notesUpdatedAt = nowMs;
  if (preservedStationSeq) {
    workout.stationSequenceNotes = preservedStationSeq;
  }

  const meta = {
    ...existingMeta,
    stages: workout.stages,
    zones: workout.zones,
    notes: resolvedNotesText,
    aiSummary: resolvedNotesText,
    userNotesEdited: true,
    notesUpdatedAt: nowMs,
    ...(preservedStationSeq ? { stationSequenceNotes: preservedStationSeq } : {}),
    avgHeartrate: workout.avgHeartrate,
    maxHeartrate: workout.maxHeartrate,
    sufferScore: workout.sufferScore,
    ...(workout.healthKitId !== undefined ? { healthKitId: workout.healthKitId } : {}),
    ...(workout.stravaId !== undefined ? { stravaId: workout.stravaId } : {}),
    ...(workout.stravaRunId !== undefined ? { stravaRunId: workout.stravaRunId } : {}),
    ...(workout.sufferMultiplier !== undefined ? { sufferMultiplier: workout.sufferMultiplier } : {}),
    ...(workout.excludeFromStats !== undefined ? { excludeFromStats: workout.excludeFromStats } : {}),
    ...(workout.isFavorite !== undefined ? { isFavorite: workout.isFavorite } : {}),
    ...(workout.elevationGain !== undefined ? { elevationGain: workout.elevationGain } : {})
  };
  workout.rawMetadata = meta;

  if (typeof document !== 'undefined' && String(currentWorkoutDetailId || '').toLowerCase() === String(workout.id || workoutId).toLowerCase()) {
    const notesInput = document.getElementById('detail-notes-input');
    if (notesInput && notesInput.value !== resolvedNotesText) {
      notesInput.value = resolvedNotesText;
    }
    const expDialog = document.getElementById('expanded-notes-dialog');
    if (expDialog && expDialog.open) {
      const expTextarea = document.getElementById('expanded-notes-textarea');
      if (expTextarea && expTextarea.value !== resolvedNotesText) {
        expTextarea.value = resolvedNotesText;
      }
    }
  }

  saveStateToStorage();

  let supabaseError = null;
  const supabase = (typeof window !== 'undefined' && window.supabaseClient) || store.supabaseClient;
  if (supabase && store.currentUser && store.currentUser.id && !store.isGuest && store.currentUser.id !== 'guest-preview-user') {
    try {
      const workoutsTable = typeof supabase.from === 'function' ? supabase.from('workouts') : null;
      if (workoutsTable && typeof workoutsTable.update === 'function') {
        const updateQuery = workoutsTable
          .update({
            healthkit_raw_metadata: meta
          })
          .eq('id', workout.id || workoutId)
          .eq('user_id', store.currentUser.id);

        const { data: updatedRows, error } = typeof updateQuery?.select === 'function'
          ? await updateQuery.select('id')
          : await updateQuery;

        if (error) {
          console.error('Error saving workout notes to Supabase:', error);
          supabaseError = error;
        } else if (Array.isArray(updatedRows) && updatedRows.length === 0) {
          let fallbackMatched = false;
          const baseUpdate = workoutsTable.update({ healthkit_raw_metadata: meta });
          if (baseUpdate && typeof baseUpdate.ilike === 'function') {
            const ilikeQuery = baseUpdate
              .ilike('id', String(workout.id || workoutId))
              .eq('user_id', store.currentUser.id);
            const { data: ilikeRows, error: ilikeErr } = typeof ilikeQuery?.select === 'function'
              ? await ilikeQuery.select('id')
              : await ilikeQuery;
            if (!ilikeErr && Array.isArray(ilikeRows) && ilikeRows.length > 0) {
              fallbackMatched = true;
            }
          }
          if (!fallbackMatched && typeof workoutsTable.upsert === 'function') {
            const startMs = Number(workout.timestamp) || Date.parse(workout.startTime || workout.date || '') || Date.now();
            const durSec = Math.round((Number(workout.duration) || 0) * 60);
            const { error: upsertErr } = await workoutsTable.upsert({
              id: String(workout.id || workoutId).toLowerCase(),
              user_id: store.currentUser.id,
              created_at: new Date(startMs).toISOString(),
              activity_type: workout.type || 'Hyrox Training',
              duration_seconds: durSec,
              start_time: new Date(startMs).toISOString(),
              end_time: new Date(startMs + durSec * 1000).toISOString(),
              total_energy_burned_kcal: Math.round(Number(workout.calories) || 0),
              total_distance_meters: Math.round((Number(workout.distance) || 0) * 1000),
              is_favorite: Boolean(workout.isFavorite),
              source_name: workout.source || 'HealthKit',
              stages: workout.stages || null,
              healthkit_raw_metadata: meta
            }, { onConflict: 'id' });
            if (upsertErr) {
              console.error('Error upserting workout notes to Supabase:', upsertErr);
              supabaseError = upsertErr;
            }
          }
        }
      }
    } catch (e) {
      console.error('Exception updating workout notes in Supabase:', e);
      supabaseError = e;
    }
    if (supabaseError && typeof saveOfflineWorkout === 'function') {
      await saveOfflineWorkout(workout);
    } else if (!supabaseError && typeof deleteOfflineWorkout === 'function') {
      await deleteOfflineWorkout(workout.id || workoutId);
      if (String(workoutId) !== String(workout.id)) {
        await deleteOfflineWorkout(workoutId);
      }
    }
  }

  if (typeof window !== 'undefined') {
    if (typeof window.renderWorkouts === 'function') {
      window.renderWorkouts();
    }
    if (typeof window.showToast === 'function') {
      if (supabaseError) {
        window.showToast('Notes saved locally (offline). Will sync when online.', 'info');
      } else {
        window.showToast('Workout notes and evaluation saved successfully!');
      }
    }
  }
  return true;
}

export async function generateAISummary(workoutOrId, options = {}) {
  const workout = (workoutOrId && typeof workoutOrId === 'object')
    ? (findWorkoutInStore(workoutOrId.id) || workoutOrId)
    : findWorkoutInStore(workoutOrId);
  if (!workout) return null;

  currentWorkoutDetailId = workout.id;

  const summaryCardEl = typeof document !== 'undefined' ? document.getElementById('ai-summary-workout-card') : null;
  const summaryTextEl = typeof document !== 'undefined' ? document.getElementById('ai-summary-text-container') : null;
  const statusBadgeEl = typeof document !== 'undefined' ? document.getElementById('ai-summary-status-badge') : null;
  const notesInput = typeof document !== 'undefined' ? document.getElementById('detail-notes-input') : null;

  if (summaryCardEl) {
    summaryCardEl.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; gap:12px;">
        <div>
          <strong style="font-size:16px; color:var(--text-primary);">${escapeHtml(workout.name || workout.type || 'Workout')}</strong>
          <div style="font-size:12px; color:var(--text-muted); margin-top:2px;">${escapeHtml(workout.date || '')}${workout.time ? ` at ${escapeHtml(workout.time)}` : ''}</div>
        </div>
        <div style="text-align:right; font-size:13px; color:var(--text-secondary);">
          <div><strong>${workout.duration || 0}m</strong> • ${workout.calories || 0} kcal</div>
          ${workout.avgHeartrate ? `<div style="color:#f43f5e;">❤️ ${workout.avgHeartrate} bpm</div>` : ''}
        </div>
      </div>
    `;
  }

  const existingSummary = (!options.forceReevaluate && workout.notes && !isRawStationSequenceNotes(workout.notes))
    ? workout.notes
    : ((!options.forceReevaluate && workout.aiSummary && !isRawStationSequenceNotes(workout.aiSummary)) ? workout.aiSummary : null);

  if (typeof document !== 'undefined' && document.getElementById('view-ai-summary')) {
    showView('view-ai-summary');
  }

  if (existingSummary) {
    if (summaryTextEl) summaryTextEl.innerText = existingSummary;
    if (statusBadgeEl) statusBadgeEl.innerText = 'AI Analysis Ready';
    return existingSummary;
  }

  if (summaryTextEl) {
    summaryTextEl.innerText = '🤖 Generating updated AI Coach Evaluation...';
  }
  if (notesInput) {
    notesInput.value = '🤖 Generating updated AI Coach Evaluation...';
  }
  if (statusBadgeEl) {
    statusBadgeEl.innerText = 'Analyzing Telemetry...';
  }

  try {
    const supabase = (typeof window !== 'undefined' && window.supabaseClient) || store.supabaseClient;
    const outputText = await runAIEvaluation(workout, {
      isDistanceStation,
      customInstructions: store.geminiInstructions,
      customKey: store.geminiApiKey,
      supabaseClient: supabase
    });

    if (outputText) {
      if (summaryTextEl) summaryTextEl.innerText = outputText;
      if (notesInput) notesInput.value = outputText;
      if (statusBadgeEl) statusBadgeEl.innerText = 'AI Analysis Saved';
      await saveWorkoutNotes(workout.id, outputText);
      return outputText;
    }
  } catch (err) {
    console.error('Error generating AI summary:', err);
    const fallbackText = workout.notes || workout.aiSummary || 'Failed to generate AI evaluation.';
    if (summaryTextEl) summaryTextEl.innerText = fallbackText;
    if (notesInput) notesInput.value = workout.notes || workout.aiSummary || '';
    if (statusBadgeEl) statusBadgeEl.innerText = 'Analysis Error';
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(err.message || 'Failed to generate AI evaluation', 'error');
    }
  }
  return null;
}

export function setupExpandedWorkoutNotesModal() {
  if (typeof document === 'undefined') return;
  const expandBtn = document.getElementById('btn-expand-detail-notes');
  const dialog = document.getElementById('expanded-notes-dialog');
  const closeBtn = document.getElementById('close-expanded-notes-btn');
  const closeBtn2 = document.getElementById('btn-close-expanded-notes');
  const saveBtn = document.getElementById('btn-save-expanded-notes');
  const textarea = document.getElementById('expanded-notes-textarea');
  const titleEl = document.getElementById('expanded-notes-title');
  const charCountEl = document.getElementById('expanded-notes-char-count');

  if (!dialog || !expandBtn) return;

  function updateCharCount() {
    if (!charCountEl || !textarea) return;
    const text = textarea.value || '';
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    charCountEl.innerText = `${words} words • ${text.length} characters`;
  }

  function openModal() {
    const workoutId = getCurrentWorkoutDetailId();
    const workout = (store.workouts || []).find(w => w && (w.id === workoutId || String(w.id) === String(workoutId)));
    const notesInput = document.getElementById('detail-notes-input');
    const currentNotes = (notesInput && notesInput.value) ? notesInput.value : (workout ? (workout.notes || workout.aiSummary || '') : '');

    if (textarea) {
      textarea.value = currentNotes;
    }
    if (titleEl && workout) {
      titleEl.innerHTML = `<span>📝 ${escapeHtml(workout.type || 'Workout')} Notes & AI Review</span>`;
    } else if (titleEl) {
      titleEl.innerHTML = `<span>📝 Workout Notes & AI Review</span>`;
    }

    updateCharCount();
    dialog.showModal();
    if (textarea) {
      setTimeout(() => {
        if (textarea && typeof textarea.focus === 'function') {
          textarea.focus();
        }
      }, 50);
    }
  }

  expandBtn.addEventListener('click', openModal);

  if (textarea) {
    textarea.addEventListener('input', updateCharCount);
  }

  if (saveBtn) {
    saveBtn.addEventListener('click', async () => {
      const updatedNotes = textarea ? textarea.value.trim() : '';
      const workoutId = getCurrentWorkoutDetailId();
      if (workoutId) {
        await saveWorkoutNotes(workoutId, updatedNotes);
      }
      const notesInput = document.getElementById('detail-notes-input');
      if (notesInput) {
        notesInput.value = updatedNotes;
      }
      dialog.close();
    });
  }

  if (closeBtn) closeBtn.addEventListener('click', () => dialog.close());
  if (closeBtn2) closeBtn2.addEventListener('click', () => dialog.close());

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) {
      dialog.close();
    }
  });
}

export function initDictateAssistant() {
  if (typeof document === 'undefined') return;
  const dialog = document.getElementById('dictate-assistant-dialog');
  const btnClose = document.getElementById('btn-close-dictate-dialog');
  const btnCancel = document.getElementById('btn-dictate-cancel');
  const btnStart = document.getElementById('btn-dictate-start');
  const btnStop = document.getElementById('btn-dictate-stop');
  const btnApply = document.getElementById('btn-dictate-apply');
  const btnDictateOpen = document.getElementById('btn-detail-dictate');
  const transcriptArea = document.getElementById('dictate-transcript-area');
  const statusText = document.getElementById('dictate-status-text');
  const micWrapper = document.getElementById('dictate-mic-wrapper');

  if (!dialog) return;

  let recognition = null;
  let isListening = false;

  function stopDictation(showStoppedMsg = true) {
    const isNativeApp = typeof window !== 'undefined' && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.speechHandler;
    if (isNativeApp) {
      try { window.webkit.messageHandlers.speechHandler.postMessage('stop'); } catch(e){}
    }
    if (recognition) {
      try { recognition.stop(); } catch(e){}
    }
    isListening = false;
    if (micWrapper) {
      micWrapper.className = 'mic-inactive';
      micWrapper.style.background = 'rgba(255,255,255,0.05)';
      micWrapper.style.borderColor = 'var(--border-light)';
      micWrapper.style.color = '';
    }
    if (showStoppedMsg && statusText) {
      statusText.innerText = "Stopped listening.";
    }
    if (btnStart) btnStart.disabled = false;
    if (btnStop) { btnStop.disabled = true; btnStop.style.opacity = '0.5'; }
  }

  // Register window callbacks for native iOS WKWebView Speech Bridge
  if (typeof window !== 'undefined') {
    window.onNativeSpeechResult = (text, isFinal) => {
      if (transcriptArea) {
        transcriptArea.value = text;
      }
      if (isFinal) {
        stopDictation(true);
      }
    };

    window.onNativeSpeechError = (errorMsg) => {
      if (statusText) statusText.innerText = `Native Error: ${errorMsg}`;
      stopDictation(false);
    };
  }

  // Closers
  if (btnClose) btnClose.addEventListener('click', () => { stopDictation(); dialog.close(); });
  if (btnCancel) btnCancel.addEventListener('click', () => { stopDictation(); dialog.close(); });
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) { stopDictation(); dialog.close(); }
  });

  // Open trigger
  if (btnDictateOpen) {
    btnDictateOpen.addEventListener('click', () => {
      if (transcriptArea) transcriptArea.value = '';
      if (statusText) statusText.innerText = "Ready to listen. Click 'Start'.";
      if (micWrapper) {
        micWrapper.className = 'mic-inactive';
        micWrapper.style.background = 'rgba(255,255,255,0.05)';
        micWrapper.style.borderColor = 'var(--border-light)';
        micWrapper.style.color = '';
      }
      if (btnStart) btnStart.disabled = false;
      if (btnStop) { btnStop.disabled = true; btnStop.style.opacity = '0.5'; }
      if (btnApply) { btnApply.disabled = false; btnApply.innerText = "Apply"; }
      dialog.showModal();
    });
  }

  if (btnStart) {
    btnStart.addEventListener('click', () => {
      const isNativeApp = typeof window !== 'undefined' && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.speechHandler;
      if (isNativeApp) {
        if (isListening) return;
        isListening = true;
        if (micWrapper) {
          micWrapper.className = 'mic-active';
          micWrapper.style.background = 'rgba(239, 68, 68, 0.15)';
          micWrapper.style.borderColor = '#ef4444';
          micWrapper.style.color = '#ef4444';
        }
        if (statusText) statusText.innerText = "Listening (Native iOS)... Speak now.";
        if (btnStart) btnStart.disabled = true;
        if (btnStop) { btnStop.disabled = false; btnStop.style.opacity = '1'; }
        
        try { window.webkit.messageHandlers.speechHandler.postMessage('start'); } catch(e){}
        return;
      }

      const SpeechRecognition = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
      if (!SpeechRecognition) {
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast("Speech Recognition is not supported by your browser. Please type your notes manually.", "error");
        }
        if (statusText) statusText.innerText = "Speech API unsupported by browser.";
        return;
      }

      if (isListening) return;

      recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        isListening = true;
        if (micWrapper) {
          micWrapper.className = 'mic-active';
          micWrapper.style.background = 'rgba(239, 68, 68, 0.15)';
          micWrapper.style.borderColor = '#ef4444';
          micWrapper.style.color = '#ef4444';
        }
        if (statusText) statusText.innerText = "Listening... Speak now.";
        if (btnStart) btnStart.disabled = true;
        if (btnStop) { btnStop.disabled = false; btnStop.style.opacity = '1'; }
      };

      recognition.onerror = (event) => {
        console.error("Speech Recognition Error:", event.error);
        let errorMsg = `Error: ${event.error}.`;
        if (event.error === 'not-allowed') {
          errorMsg = "Microphone access denied. Please check permissions.";
        } else if (event.error === 'service-not-allowed') {
          errorMsg = "Speech service blocked (requires HTTPS).";
        } else if (event.error === 'no-speech') {
          errorMsg = "No speech detected. Please try again.";
        }
        if (statusText) statusText.innerText = errorMsg;
        stopDictation(false);
      };

      recognition.onend = () => {
        isListening = false;
        if (micWrapper) {
          micWrapper.className = 'mic-inactive';
          micWrapper.style.background = 'rgba(255,255,255,0.05)';
          micWrapper.style.borderColor = 'var(--border-light)';
          micWrapper.style.color = '';
        }
        if (btnStart) btnStart.disabled = false;
        if (btnStop) { btnStop.disabled = true; btnStop.style.opacity = '0.5'; }
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        if (finalTranscript && transcriptArea) {
          transcriptArea.value += (transcriptArea.value ? ' ' : '') + finalTranscript;
        }
      };

      try {
        recognition.start();
      } catch (err) {
        console.error("Speech start error:", err);
      }
    });
  }

  if (btnStop) {
    btnStop.addEventListener('click', () => {
      stopDictation();
    });
  }

  if (btnApply) {
    btnApply.addEventListener('click', async () => {
      stopDictation();

      const text = transcriptArea ? transcriptArea.value.trim() : '';
      if (!text) {
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast("Please speak or enter some transcript text first.", "error");
        }
        return;
      }

      btnApply.disabled = true;
      btnApply.innerText = "Processing...";
      if (statusText) statusText.innerText = "Processing transcript with Gemini AI...";

      try {
        const workoutId = getCurrentWorkoutDetailId();
        if (!workoutId) return;
        const workout = (store.workouts || []).find(w => w && (w.id === workoutId || String(w.id) === String(workoutId)));
        if (!workout) return;

        const supabase = (typeof window !== 'undefined' && window.supabaseClient) || store.supabaseClient;
        const payload = await processVoiceCommandWithGemini(text, {
          supabaseClient: supabase,
          customKey: store.geminiApiKey,
          systemInstruction: store.geminiInstructions
        });

        // Apply notes
        if (payload.notes) {
          if (payload.appendNotes && workout.notes) {
            workout.notes += '\n' + payload.notes;
          } else {
            workout.notes = payload.notes;
          }
          workout.aiSummary = workout.notes;
        }

        // Apply stages/splits
        if (payload.stages && Array.isArray(payload.stages)) {
          if (payload.stages.length === 0) {
            workout.stages = null;
          } else {
            const oldStages = workout.stages || [];
            workout.stages = payload.stages.map((stage, idx) => {
              const isRox = stage.type === 'RoxZone' || stage.type === 'Roxzone' || stage.stationName === 'RoxZone' || stage.stationName === 'Roxzone' || (stage.stationName && stage.stationName.toLowerCase().includes('rox'));
              const isDist = typeof isDistanceStation === 'function' ? isDistanceStation(stage.stationName, stage.type) : false;
              const oldStage = oldStages[idx];
              const preservedAvgHr = oldStage ? (oldStage.avgHr || 0) : 0;
              return {
                stageNum: idx + 1,
                type: stage.type,
                stationName: stage.stationName || (isRox ? 'RoxZone' : ''),
                durationMin: parseInt(stage.durationMin, 10) || 0,
                durationSec: parseInt(stage.durationSec, 10) || 0,
                avgHr: preservedAvgHr,
                distance: isDist ? (parseInt(stage.distance, 10) || 0) : null,
                reps: !isDist ? (parseInt(stage.reps, 10) || 0) : null
              };
            });

            // Re-calculate workout total duration
            let totalMin = 0;
            workout.stages.forEach(st => {
              totalMin += (st.durationMin || 0) + ((st.durationSec || 0) / 60);
            });
            workout.duration = Math.round(totalMin * 10) / 10;
          }
        }

        saveStateToStorage();
        if (workout.notes) {
          await saveWorkoutNotes(workoutId, workout.notes);
        }

        openWorkoutDetail(workoutId, { preserveScroll: true });
        if (typeof window !== 'undefined') {
          if (typeof window.renderWorkouts === 'function') window.renderWorkouts();
          if (typeof window.renderDashboard === 'function') window.renderDashboard();
          if (typeof window.showToast === 'function') window.showToast("Voice updates applied successfully!");
        }
        dialog.close();
      } catch (err) {
        console.error("Voice dictation error:", err);
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast(err.message || "Failed to process voice command", "error");
        }
        if (statusText) statusText.innerText = "Failed to parse. Please try again.";
      } finally {
        if (btnApply) {
          btnApply.disabled = false;
          btnApply.innerText = "Apply";
        }
      }
    });
  }
}

let isEditorInitialized = false;

export function initEditorView() {
  if (isEditorInitialized) return;
  isEditorInitialized = true;
  setupWorkoutDetail();
  setupModal();
  setupExpandedWorkoutNotesModal();
  initDictateAssistant();
  setupShareCardModal();
}


export function recoverInMemoryWorkoutStageNames(workout) {
  if (!workout || !Array.isArray(workout.stages) || workout.stages.length === 0) return;
  const rawMeta = workout.rawMetadata || workout.healthkit_raw_metadata || null;
  let rawMetaStages = rawMeta && Array.isArray(rawMeta.stages) ? rawMeta.stages : null;
  if (!rawMetaStages && typeof workout.rawHealthKitPayloadJSON === 'string') {
    try {
      const parsedHw = JSON.parse(workout.rawHealthKitPayloadJSON);
      rawMetaStages = parsedHw?.station_splits || parsedHw?.workoutActivities || parsedHw?.stages || null;
    } catch (e) {
      rawMetaStages = null;
    }
  }
  const notesText = workout.stationSequenceNotes ||
    (rawMeta && rawMeta.stationSequenceNotes) ||
    workout.notes ||
    (rawMeta && rawMeta.notes) ||
    '';
  const notesStations = parseSequentialStationsFromNotes(notesText);

  workout.stages.forEach((st, idx) => {
    if (!st || typeof st !== 'object') return;
    const rawTypeLower = String(st.type || st.stage_type || '').toLowerCase();
    const isExplicitNonStation = rawTypeLower === 'run' || rawTypeLower === 'roxzone' || rawTypeLower === 'rest' || rawTypeLower === 'cardio';
    if (isExplicitNonStation) return;

    const currentName = st.stationName || st.station_name || st.name || '';
    if (!isGenericStageName(currentName)) return;

    const stageIdxZeroBased = (st.stageNum || st.stage_index || (idx + 1)) - 1;
    let recovered = '';
    if (Array.isArray(rawMetaStages)) {
      const rawMatch = rawMetaStages.find((rs, rIdx) => {
        if (!rs || typeof rs !== 'object') return false;
        const rNum = rs.stageNum || rs.stage_index || (rIdx + 1);
        return rNum === (stageIdxZeroBased + 1);
      }) || rawMetaStages[stageIdxZeroBased];
      if (rawMatch && typeof rawMatch === 'object') {
        const rawCandidate = rawMatch.stationName || rawMatch.station_name || rawMatch.name || '';
        if (!isGenericStageName(rawCandidate)) {
          recovered = normalizeStationName(rawCandidate) || rawCandidate;
        }
      }
    }
    if (!recovered && notesStations[stageIdxZeroBased] && !isGenericStageName(notesStations[stageIdxZeroBased])) {
      recovered = notesStations[stageIdxZeroBased];
    }
    if (recovered) {
      st.name = recovered;
      st.stationName = recovered;
    }
  });
}

export function renderReadOnlyDetails(workout) {
  const statsContainer = document.getElementById('detail-stats-container');
  const stageDurationContainer = document.getElementById('detail-stage-duration-container');
  
  if (!statsContainer || !stageDurationContainer) return;

  recoverInMemoryWorkoutStageNames(workout);

  // 1. Render Stats Breakdown
  let breakdownHtml = '';
  breakdownHtml += '<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 16px; margin-bottom: 24px;">';
  
  if (workout.duration) {
    breakdownHtml += `
      <div class="detail-metric-card" style="border-color: rgba(99, 102, 241, 0.2); background: rgba(99, 102, 241, 0.015); padding: 12px; border-radius: 8px; border: 1px solid var(--border-light);">
        <span class="detail-metric-title" style="color: #818cf8; font-size: 11px; text-transform: uppercase; font-weight: 700; display: block; margin-bottom: 4px;">⏱️ Duration</span>
        <span class="detail-metric-value" style="color: #818cf8; font-size: 20px; font-weight: 800;">${workout.duration}<span class="detail-metric-unit" style="font-size: 12px; font-weight: 600; margin-left: 4px; color: rgba(129, 140, 248, 0.6);">min</span></span>
      </div>
    `;
  }
  
  const hasStages = Array.isArray(workout.stages) && workout.stages.length > 0;
  const effectiveDistance = hasStages
    ? getWorkoutTotalDistance(workout)
    : ((Number.isFinite(workout.distance) && workout.distance > 0)
      ? workout.distance
      : getWorkoutTotalDistance(workout));
  if (effectiveDistance > 0) {
    breakdownHtml += `
      <div class="detail-metric-card" style="border-color: rgba(6, 182, 212, 0.2); background: rgba(6, 182, 212, 0.015); padding: 12px; border-radius: 8px; border: 1px solid var(--border-light);">
        <span class="detail-metric-title" style="color: #22d3ee; font-size: 11px; text-transform: uppercase; font-weight: 700; display: block; margin-bottom: 4px;">📍 Distance</span>
        <span class="detail-metric-value" style="color: #22d3ee; font-size: 20px; font-weight: 800;">${effectiveDistance}<span class="detail-metric-unit" style="font-size: 12px; font-weight: 600; margin-left: 4px; color: rgba(34, 211, 238, 0.6);">km</span></span>
      </div>
    `;
  }

  if (workout.calories) {
    breakdownHtml += `
      <div class="detail-metric-card" style="border-color: rgba(244, 63, 94, 0.2); background: rgba(244, 63, 94, 0.015); padding: 12px; border-radius: 8px; border: 1px solid var(--border-light);">
        <span class="detail-metric-title" style="color: #fb7185; font-size: 11px; text-transform: uppercase; font-weight: 700; display: block; margin-bottom: 4px;">🔥 Calories</span>
        <span class="detail-metric-value" style="color: #fb7185; font-size: 20px; font-weight: 800;">${Math.round(workout.calories)}<span class="detail-metric-unit" style="font-size: 12px; font-weight: 600; margin-left: 4px; color: rgba(251, 113, 133, 0.6);">kcal</span></span>
      </div>
    `;
  }

  if (workout.averageWatts) {
    breakdownHtml += `
      <div class="detail-metric-card" style="border-color: rgba(16, 185, 129, 0.2); background: rgba(16, 185, 129, 0.015); padding: 12px; border-radius: 8px; border: 1px solid var(--border-light);">
        <span class="detail-metric-title" style="color: #10b981; font-size: 11px; text-transform: uppercase; font-weight: 700; display: block; margin-bottom: 4px;">⚡ Avg Power</span>
        <span class="detail-metric-value" style="color: #10b981; font-size: 20px; font-weight: 800;">${Math.round(workout.averageWatts)}<span class="detail-metric-unit" style="font-size: 12px; font-weight: 600; margin-left: 4px; color: rgba(16, 185, 129, 0.6);">W</span></span>
      </div>
    `;
  }
  
  if (workout.sufferScore) {
    breakdownHtml += `
      <div class="detail-metric-card" style="border-color: rgba(251, 146, 60, 0.2); background: rgba(251, 146, 60, 0.015); padding: 12px; border-radius: 8px; border: 1px solid var(--border-light);">
        <span class="detail-metric-title" style="color: #fb923c; font-size: 11px; text-transform: uppercase; font-weight: 700; display: block; margin-bottom: 4px;">⚡ Suffer Score</span>
        <span class="detail-metric-value" style="color: #fb923c; font-size: 20px; font-weight: 800;">${workout.sufferScore}</span>
      </div>
    `;
  }

  breakdownHtml += '</div>'; // close grid

  // Hyrox Analysis - activates for any workout containing recorded stage splits
  if (workout.stages && Array.isArray(workout.stages) && workout.stages.length > 0) {
    let runTotalSec = 0, stationTotalSec = 0, runCount = 0, stationCount = 0;
    let runSecsList = [], stationSecsList = [];
    
    workout.stages.forEach((st, idx) => {
      const sec = Number.isFinite(st.duration_seconds)
        ? Math.round(st.duration_seconds)
        : (Number.isFinite(st.duration)
          ? Math.round(st.duration)
          : (((st.durationMin || 0) * 60) + (st.durationSec || 0)));
      if (!Number.isFinite(sec) || sec <= 0) return;

      const stageNum = st.stageNum || st.stage_index || (idx + 1);
      const stName = st.stationName || st.name || st.station_name || 'Station';
      const trimmedStName = stName.trim();
      const rawType = (st.type || st.stage_type || '').toLowerCase();
      const lowerName = trimmedStName.toLowerCase();
      const isExplicitStation = rawType === 'station' || rawType === 'strength';
      const isRun = rawType === 'run' || rawType === 'running' || (!isExplicitStation && /^(?:run|running)(?:\b|\s*:)/i.test(trimmedStName));
      const isRox = rawType.includes('rox') || lowerName.includes('rox');
      const isRest = rawType === 'rest' || (!isExplicitStation && /^rest(?:\s+period)?$/i.test(trimmedStName));
      const isCardio = rawType === 'cardio' || lowerName === 'cardio';

      if (isRun) {
        runTotalSec += sec;
        runCount++;
        runSecsList.push({ num: stageNum, name: 'Run', sec });
      } else if (!isRox && !isRest && !isCardio) {
        stationTotalSec += sec;
        stationCount++;
        stationSecsList.push({ num: stageNum, name: stName, sec });
      }
    });
    
    const formatSec = (totalSec) => `${Math.floor(totalSec / 60)}m ${totalSec % 60}s`;
    const runAvg = runCount > 0 ? Math.round(runTotalSec / runCount) : 0;
    const stationAvg = stationCount > 0 ? Math.round(stationTotalSec / stationCount) : 0;
    
    runSecsList.sort((a,b) => a.sec - b.sec);
    stationSecsList.sort((a,b) => a.sec - b.sec);
    
    let runStatsHtml = '', stationStatsHtml = '';
    
    if (runCount > 0) {
      const fastestRun = runSecsList[0];
      const slowestRun = runSecsList[runSecsList.length - 1];
      runStatsHtml = `
        <div style="display: flex; justify-content: space-between; padding: 4px 0;">
          <span style="color: var(--text-muted);">🏃 Fastest Run:</span>
          <span style="font-weight: 600; color: #10b981;">Stage ${fastestRun.num} (${formatSec(fastestRun.sec)})</span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 4px 0;">
          <span style="color: var(--text-muted);">🐢 Slowest Run:</span>
          <span style="font-weight: 600; color: var(--text-muted);">Stage ${slowestRun.num} (${formatSec(slowestRun.sec)})</span>
        </div>
      `;
    }

    if (stationCount > 0) {
      const fastestStation = stationSecsList[0];
      const slowestStation = stationSecsList[stationSecsList.length - 1];
      stationStatsHtml = `
        <div style="display: flex; justify-content: space-between; padding: 4px 0;">
          <span style="color: var(--text-muted);">💪 Fastest Station:</span>
          <span style="font-weight: 600; color: #a78bfa;">Stage ${fastestStation.num} (${escapeHtml(fastestStation.name)} - ${formatSec(fastestStation.sec)})</span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 4px 0;">
          <span style="color: var(--text-muted);">🐌 Slowest Station:</span>
          <span style="font-weight: 600; color: var(--text-muted);">Stage ${slowestStation.num} (${escapeHtml(slowestStation.name)} - ${formatSec(slowestStation.sec)})</span>
        </div>
      `;
    }

    let statsListHtml = '';
    if (runStatsHtml || stationStatsHtml) {
      const divider = (runStatsHtml && stationStatsHtml) ? '<hr style="border: none; border-top: 1px solid rgba(255,255,255,0.05); margin: 8px 0;">' : '';
      statsListHtml = `
        <div style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 8px; padding: 16px; margin-bottom: 24px; font-size: 13px; display: flex; flex-direction: column;">
          ${runStatsHtml}
          ${divider}
          ${stationStatsHtml}
        </div>
      `;
    }

    if (runCount > 0 || stationCount > 0) {
      breakdownHtml += `
        <div style="margin-bottom: 20px;">
          <h4 style="font-size: 13px; text-transform: uppercase; color: var(--text-secondary); margin-top: 0; margin-bottom: 12px; letter-spacing: 0.5px;">Workout Time Split Analysis</h4>
          <div class="hyrox-summary-grid">
            <div class="hyrox-summary-card">
              <span class="hyrox-summary-label">Total Running</span>
              <span class="hyrox-summary-value running">${formatSec(runTotalSec)}</span>
            </div>
            <div class="hyrox-summary-card">
              <span class="hyrox-summary-label">Total Stations</span>
              <span class="hyrox-summary-value station">${formatSec(stationTotalSec)}</span>
            </div>
            <div class="hyrox-summary-card">
              <span class="hyrox-summary-label">Avg Run (${workout.type === 'Hyrox' || workout.type === 'Hyrox Training' ? '1km' : 'Split'})</span>
              <span class="hyrox-summary-value">${formatSec(runAvg)}</span>
            </div>
            <div class="hyrox-summary-card">
              <span class="hyrox-summary-label">Avg Station</span>
              <span class="hyrox-summary-value">${formatSec(stationAvg)}</span>
            </div>
          </div>
          ${statsListHtml}
        </div>
      `;
    }
  }

  // Heart Rate Timeline Across Splits & Heart Rate Zones
  const hasStreamHr = Boolean(workout.streams && workout.streams.heartrate && Array.isArray(workout.streams.heartrate.data) && workout.streams.heartrate.data.length > 0);
  const hasStageHr = Array.isArray(workout.stages) && workout.stages.length > 0 && (
    workout.stages.some(s => (Number(s.avgHr || s.avg_hr || s.average_heart_rate || 0)) > 0) ||
    Number(workout.avgHeartrate) > 0
  );
  const hasHrData = hasStreamHr || hasStageHr;

  const hrAcrossSplitsHtml = hasHrData ? `
    <div id="detail-hr-across-splits-section" class="exec-hr-splits-section" style="margin-top: 18px; margin-bottom: 10px;">
      <div class="exec-hr-section-header">
        <div class="exec-hr-title-stack">
          <h4 class="exec-hr-main-title">Heart Rate Across Splits</h4>
          <span class="exec-hr-subtitle">EXECUTIVE PERFORMANCE MONITOR • SPLIT TELEMETRY &amp; RECOVERY ANALYSIS</span>
        </div>
        <div class="exec-hr-header-controls">
          <div class="exec-hr-legend-pills" aria-label="Heart Rate Zone Legend">
            <span class="exec-hr-legend-item"><i class="legend-dot legend-dot-peak"></i>Peak Z5</span>
            <span class="exec-hr-legend-item"><i class="legend-dot legend-dot-thresh"></i>Threshold Z3–4</span>
            <span class="exec-hr-legend-item"><i class="legend-dot legend-dot-base"></i>Aerobic Z1–2</span>
          </div>
          <button type="button" id="btn-expand-hr-chart" class="btn btn-secondary exec-hr-expand-btn" title="Expand & Analyze Heart Rate">
            <span>⛶ Expand Timeline</span>
          </button>
        </div>
      </div>
      <div id="detail-hr-chart-container" class="hr-chart-wrapper exec-hr-chart-card" style="cursor: pointer; margin-bottom: 0;" title="Click to expand Heart Rate across splits"></div>
    </div>
  ` : '';

  if (workout.zones || hasHrData || workout.avgHeartrate) {
    breakdownHtml += `
      <div style="margin-bottom: 24px;">
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;">
          <h4 style="font-size: 13px; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.5px; margin: 0;">Heart Rate Zones</h4>
          <div id="detail-suffer-badge-container"></div>
        </div>
        <div id="detail-hr-zones-container" style="display: flex; flex-direction: column; gap: 10px;"></div>
      </div>
    `;
  }

  statsContainer.innerHTML = breakdownHtml || '<div style="color: var(--text-muted); font-size: 12px; font-style: italic;">No advanced stats available for this workout.</div>';

  const sufferBadgeContainer = document.getElementById('detail-suffer-badge-container');
  const calculatedSuffer = getWorkoutSufferScore(workout);
  if (sufferBadgeContainer && calculatedSuffer > 0) {
    let tier = 'tier-easy';
    let label = 'Active';
    if (calculatedSuffer > 150) {
      tier = 'tier-extreme';
      label = 'Extreme effort';
    } else if (calculatedSuffer > 80) {
      tier = 'tier-hard';
      label = 'Hard effort';
    } else if (calculatedSuffer > 30) {
      tier = 'tier-moderate';
      label = 'Moderate effort';
    }
    sufferBadgeContainer.innerHTML = `
      <span class="suffer-score-badge ${tier}" title="Relative Effort / Suffer Score: ${calculatedSuffer}">
        ⚡ ${label} (${calculatedSuffer})
      </span>
    `;
  }

  const hrZonesContainer = document.getElementById('detail-hr-zones-container');
  if (hrZonesContainer) {
    const zSec = calculateWorkoutZoneSeconds(workout);
    const totalZSec = zSec.reduce((a, b) => a + b, 0);
    if (totalZSec > 0) {
      const zonesArr = zSec.map(s => ({ time: s }));
      renderHeartRateZonesUI(zonesArr, hrZonesContainer);
    }
  }

  // 2. Render Stage Durations (Splits) + Heart Rate Across Splits immediately below splits
  const badgeEl = document.getElementById('detail-stages-count-badge');
  if (badgeEl) {
    badgeEl.innerText = (workout.stages ? workout.stages.length : 0) + ' splits';
  }

  if (!workout.stages || workout.stages.length === 0) {
    stageDurationContainer.innerHTML = `<div style="color: var(--text-muted); font-size: 12px; font-style: italic; padding: 10px 0;">No stage splits recorded yet. Tap "+ Add Split" above to add one.</div>${hrAcrossSplitsHtml}`;
  } else {
    let stageDurationHtml = `
      <div style="margin-bottom: 16px;">
        <div style="display: flex; flex-direction: column; gap: 8px;">
    `;
    
    const stageDurations = workout.stages.map(st => {
      if (st.durationMin !== undefined) return (st.durationMin * 60) + (st.durationSec || 0);
      return Math.round(st.duration_seconds || st.duration || 0);
    });
    const maxDur = Math.max(...stageDurations, 60);
    
    workout.stages.forEach((st, idx) => {
      const durSecTotal = Number.isFinite(st.duration_seconds)
        ? Math.round(st.duration_seconds)
        : (Number.isFinite(st.duration)
          ? Math.round(st.duration)
          : ((st.durationMin || 0) * 60 + (st.durationSec || 0)));
      const dMin = st.durationMin !== undefined ? st.durationMin : Math.floor(durSecTotal / 60);
      const dSec = st.durationSec !== undefined ? st.durationSec : (durSecTotal % 60);
      const durationSecs = (dMin * 60) + dSec;
      const pct = Math.min(100, (durationSecs / maxDur) * 100);

      const stageNum = st.stageNum || st.stage_index || (idx + 1);
      const stName = st.stationName || st.station_name || st.name || '';
      const trimmedStName = stName.trim();
      const rawType = (st.type || st.stage_type || '').toLowerCase();
      const isExplicitStation = rawType === 'station' || rawType === 'strength';
      const isRox = rawType.includes('rox') || trimmedStName.toLowerCase().includes('rox');
      const isRun = st.type === 'Run' || rawType === 'run' || rawType === 'running' || (!isExplicitStation && /^(?:run|running)(?:\b|\s*:)/i.test(trimmedStName));
      const isRest = st.type === 'Rest' || rawType === 'rest' || (!isExplicitStation && /^rest(?:\s+period)?$/i.test(trimmedStName));
      const isCardio = st.type === 'Cardio' || rawType === 'cardio' || trimmedStName.toLowerCase() === 'cardio';

      let title = '';
      if (isRun) {
        const rawDist = st.distance !== null && st.distance !== undefined ? st.distance : st.distance_meters;
        const dist = Number.isFinite(Number(rawDist)) && Number(rawDist) > 0 ? Number(rawDist) : 0;
        title = `🏃 Run (${dist}m)`;
      } else if (isRox) {
        title = `⚡ RoxZone`;
      } else if (isRest) {
        title = `⏳ Rest Period`;
      } else if (isCardio) {
        const distVal = Number(st.distance !== null && st.distance !== undefined ? st.distance : (st.distance_meters || 0));
        title = `🚴 Cardio${distVal > 0 ? ` (${distVal}m)` : ''}`;
      } else {
        const distVal = Number(st.distance !== null && st.distance !== undefined ? st.distance : (st.distance_meters || 0));
        const repsVal = Number(st.reps || 0);
        const isDistance = isDistanceStation(stName, st.type);
        const effectiveReps = repsVal > 0 ? repsVal : (!isDistance && distVal > 0 ? distVal : 0);
        const metricValStr = isDistance
          ? (distVal > 0 ? `${distVal}m` : '')
          : (effectiveReps > 0 ? `${effectiveReps} reps` : '');
        const cleanStName = (!stName || isGenericStageName(stName)) ? 'Station' : stName;
        title = `💪 ${escapeHtml(cleanStName)}${metricValStr ? ` (${metricValStr})` : ''}`;
      }
      
      const durationStr = `${dMin}:${(dSec || 0).toString().padStart(2, '0')}`;
      const avgHr = Number.isFinite(st.avgHr) ? st.avgHr : (Number.isFinite(st.avg_hr) ? st.avg_hr : (Number.isFinite(st.average_heart_rate) ? st.average_heart_rate : 0));
      const hrStr = avgHr ? ` <span style="color:#f43f5e; font-size:10.5px; margin-left:5px; white-space:nowrap;">❤️ ${Math.round(avgHr)} bpm</span>` : '';
      
      // Determine bar color based on type
      let barColor = 'rgba(255,255,255,0.2)';
      if (isRun) barColor = 'var(--color-accent)';
      else if (isRox) barColor = '#eab308';
      else if (isRest) barColor = '#94a3b8';
      else barColor = '#a78bfa';
      
      stageDurationHtml += `
        <div class="detail-stage-split-row" data-stage-idx="${idx}" style="display: flex; flex-direction: column; gap: 6px; padding: 8px; background: rgba(0,0,0,0.2); border-radius: 6px; border: 1px solid rgba(255,255,255,0.05);">
          <div style="display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 4px;">
            <span class="detail-stage-row-title" style="font-weight: 500; font-size: 12px; letter-spacing: -0.1px; line-height: 1.35; color: var(--text-primary); flex: 1 1 auto; min-width: 0; overflow-wrap: break-word;">Stage ${stageNum}: ${title}</span>
            <span style="font-weight: 600; font-family: 'Outfit', sans-serif; font-size: 12.5px; display: flex; align-items: center; flex-shrink: 0; margin-left: auto; white-space: nowrap;">${durationStr}${hrStr}<button type="button" class="btn-edit-stage-row btn-edit-stage-modal" data-stage-idx="${idx}" data-stage-num="${stageNum}" style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; color: var(--text-primary); padding: 3px 5px; margin-left: 5px; cursor: pointer; font-size: 11px; line-height: 1; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;" title="Edit Stage ${stageNum}" aria-label="Edit Stage ${stageNum}">✏️</button></span>
          </div>
          <div style="height: 6px; background: rgba(255,255,255,0.05); border-radius: 3px; overflow: hidden;">
            <div style="height: 100%; width: ${pct}%; background: ${barColor}; border-radius: 3px;"></div>
          </div>
        </div>
      `;
    });
    
    stageDurationHtml += `
        </div>
      </div>
      ${hrAcrossSplitsHtml}
    `;
    
    stageDurationContainer.innerHTML = stageDurationHtml;
  }

  currentHrChartWorkout = workout;
  if (hasHrData) {
    const hrChartContainer = document.getElementById('detail-hr-chart-container');
    if (hrChartContainer) {
      if (hasStageHr) {
        renderStagesHeartRateChart(workout, hrChartContainer);
      } else if (hasStreamHr) {
        const timeArr = workout.streams.time ? workout.streams.time.data : [];
        renderHeartRateChart(workout.streams.heartrate.data, timeArr, hrChartContainer);
      }
      if (!hrChartContainer._hrExpandBound) {
        hrChartContainer._hrExpandBound = true;
        hrChartContainer.addEventListener('click', (e) => {
          if (!currentHrChartWorkout) return;
          const target = e && e.target;
          const band = target && typeof target.closest === 'function' ? target.closest('.stage-band-interactive') : null;
          const stageIdx = band ? parseInt(band.getAttribute('data-stage-idx'), 10) : null;
          openExpandedHeartRateModal(currentHrChartWorkout, Number.isFinite(stageIdx) ? stageIdx : null);
        });
      }
    }
    const expandHrBtn = document.getElementById('btn-expand-hr-chart');
    if (expandHrBtn && !expandHrBtn._hrExpandBound) {
      expandHrBtn._hrExpandBound = true;
      expandHrBtn.addEventListener('click', () => {
        if (currentHrChartWorkout) {
          openExpandedHeartRateModal(currentHrChartWorkout);
        }
      });
    }
  }
}

export function renderHeartRateZonesUI(zonesData, containerEl) {
  if (!containerEl) return;
  containerEl.innerHTML = '';

  if (!zonesData || zonesData.length === 0) {
    containerEl.innerHTML = `<div class="empty-state" style="padding: 15px;"><p style="font-size: 12px;">No zone distribution data available.</p></div>`;
    return;
  }

  let totalSec = 0;
  zonesData.forEach(z => totalSec += (z.time || 0));

  if (totalSec === 0) {
    containerEl.innerHTML = `<div class="empty-state" style="padding: 15px;"><p style="font-size: 12px;">0 seconds logged in heart rate zones.</p></div>`;
    return;
  }

  const th = resolveHrZoneThresholds();
  const zoneMeta = [
    { label: `Z1 • Endurance (< ${th.z1})`, color: '#94a3b8' },
    { label: `Z2 • Moderate (${th.z1} - ${th.z2 - 1})`, color: '#38bdf8' },
    { label: `Z3 • Tempo (${th.z2} - ${th.z3 - 1})`, color: '#10b981' },
    { label: `Z4 • Threshold (${th.z3} - ${th.z4 - 1})`, color: '#f59e0b' },
    { label: `Z5 • Anaerobic (> ${th.z4})`, color: '#f43f5e' }
  ];

  let rowsHtml = '';
  zonesData.forEach((z, idx) => {
    const meta = zoneMeta[idx] || { label: `Zone ${idx + 1}`, color: '#a5b4fc' };
    const sec = z.time || 0;
    const pct = totalSec > 0 ? Math.round((sec / totalSec) * 100) : 0;

    const mins = Math.floor(sec / 60);
    const remSecs = sec % 60;
    const timeStr = `${mins}:${remSecs.toString().padStart(2, '0')}`;

    rowsHtml += `
      <div class="zone-bar-row">
        <div class="zone-bar-header">
          <span style="color: ${meta.color}; font-weight: 700;">${meta.label}</span>
          <span style="color: var(--text-muted); font-family: monospace;">${timeStr} (${pct}%)</span>
        </div>
        <div class="zone-bar-track">
          <div class="zone-bar-fill" style="width: ${pct}%; background: ${meta.color}; box-shadow: 0 0 8px ${meta.color}66;"></div>
        </div>
      </div>
    `;
  });
  containerEl.innerHTML = rowsHtml;
}

function resolveHrZoneThresholds() {
  const raw = store.hrThresholds || {};
  return {
    z1: Number(raw.z1 || raw.z2Min || 120),
    z2: Number(raw.z2 || raw.z3Min || 140),
    z3: Number(raw.z3 || raw.z4Min || 155),
    z4: Number(raw.z4 || raw.z5Min || 170)
  };
}

/**
 * Generates a smooth Fritsch-Carlson Monotone Cubic Hermite SVG path (`M ... C ...`)
 * that interpolates all points without overshooting local extrema.
 */
function buildMonotoneCubicSpline(pts, baselineY, minY, maxY) {
  if (!pts || pts.length === 0) return { linePath: '', areaPath: '' };
  if (pts.length === 1) {
    const p = pts[0];
    const linePath = `M ${p.x.toFixed(1)},${p.y.toFixed(1)} L ${(p.x + 1).toFixed(1)},${p.y.toFixed(1)}`;
    const areaPath = `${linePath} L ${(p.x + 1).toFixed(1)},${baselineY.toFixed(1)} L ${p.x.toFixed(1)},${baselineY.toFixed(1)} Z`;
    return { linePath, areaPath };
  }

  const n = pts.length;
  const dx = new Array(n - 1);
  const dy = new Array(n - 1);
  const slopes = new Array(n - 1);

  for (let i = 0; i < n - 1; i++) {
    dx[i] = Math.max(1e-4, pts[i + 1].x - pts[i].x);
    dy[i] = pts[i + 1].y - pts[i].y;
    slopes[i] = dy[i] / dx[i];
  }

  const tangents = new Array(n);
  tangents[0] = slopes[0];
  tangents[n - 1] = slopes[n - 2];

  for (let i = 1; i < n - 1; i++) {
    if (slopes[i - 1] * slopes[i] <= 0) {
      tangents[i] = 0;
    } else {
      tangents[i] = (slopes[i - 1] + slopes[i]) / 2;
    }
  }

  for (let i = 0; i < n - 1; i++) {
    if (Math.abs(slopes[i]) < 1e-6) {
      tangents[i] = 0;
      tangents[i + 1] = 0;
    } else {
      const alpha = tangents[i] / slopes[i];
      const beta = tangents[i + 1] / slopes[i];
      const hypot = Math.hypot(alpha, beta);
      if (hypot > 3) {
        const tau = 3 / hypot;
        tangents[i] = tau * alpha * slopes[i];
        tangents[i + 1] = tau * beta * slopes[i];
      }
    }
  }

  const clampY = (val) => Math.max(minY, Math.min(maxY, val));
  let linePath = `M ${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;

  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const segDx = dx[i] / 3;
    const cp1x = p0.x + segDx;
    const cp1y = clampY(p0.y + tangents[i] * segDx);
    const cp2x = p1.x - segDx;
    const cp2y = clampY(p1.y - tangents[i + 1] * segDx);
    linePath += ` C ${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p1.x.toFixed(1)},${p1.y.toFixed(1)}`;
  }

  const areaPath = `${linePath} L ${pts[n - 1].x.toFixed(1)},${baselineY.toFixed(1)} L ${pts[0].x.toFixed(1)},${baselineY.toFixed(1)} Z`;
  return { linePath, areaPath };
}

/**
 * Computes balanced stage visual geometry, summary telemetry metrics, and collision-free callouts.
 */
function buildStagesTelemetryModel(workout, width, height, padLeft, padRight, padTop, padBottom, isExpanded = false) {
  const stages = workout && Array.isArray(workout.stages) ? workout.stages : [];
  const fallbackWorkoutHr = workout && Number.isFinite(Number(workout.avgHeartrate)) && Number(workout.avgHeartrate) > 0
    ? Math.round(Number(workout.avgHeartrate))
    : (workout && Number.isFinite(Number(workout.avgHr)) && Number(workout.avgHr) > 0 ? Math.round(Number(workout.avgHr)) : 0);

  const resolveStageHr = (s) => {
    const val = Number.isFinite(s.avgHr) && s.avgHr > 0
      ? s.avgHr
      : (Number.isFinite(s.avg_hr) && s.avg_hr > 0
        ? s.avg_hr
        : (Number.isFinite(s.average_heart_rate) && s.average_heart_rate > 0
          ? s.average_heart_rate
          : (parseInt(s.avgHr || s.avg_hr || 0, 10) || fallbackWorkoutHr)));
    return Math.round(val);
  };

  const resolveStageSecs = (s) => {
    if (s.durationMin !== undefined) return Math.max(10, ((parseInt(s.durationMin, 10) || 0) * 60) + (parseInt(s.durationSec, 10) || 0));
    const sec = Number.isFinite(s.duration_seconds) ? Math.round(s.duration_seconds) : (Number.isFinite(s.duration) ? Math.round(s.duration) : 0);
    return Math.max(10, sec);
  };

  const validHrs = stages.map(resolveStageHr).filter(hr => hr > 0);
  if (validHrs.length === 0) return null;

  const actualMinHr = Math.min(...validHrs);
  const actualMaxHr = Math.max(...validHrs);
  const avgStageHr = Math.round(validHrs.reduce((a, b) => a + b, 0) / validHrs.length);
  const resolvedWorkoutAvgHr = fallbackWorkoutHr || avgStageHr;

  const minHr = Math.max(55, actualMinHr - 14);
  const maxHr = Math.min(220, actualMaxHr + 16);
  const range = Math.max(18, maxHr - minHr);

  const plotWidth = Math.max(100, width - padLeft - padRight);
  const plotTop = padTop;
  const plotBottom = height - padBottom;
  const plotHeight = Math.max(60, plotBottom - plotTop);

  let totalSec = 0;
  const rawStages = stages.map((s, idx) => {
    const dur = resolveStageSecs(s);
    const startSec = totalSec;
    totalSec += dur;
    return {
      ...s,
      stageIdx: idx,
      stageNum: s.stageNum || s.stage_index || (idx + 1),
      avgHr: resolveStageHr(s),
      durationMin: s.durationMin !== undefined ? s.durationMin : Math.floor(dur / 60),
      durationSec: s.durationSec !== undefined ? s.durationSec : (dur % 60),
      startSec,
      endSec: totalSec,
      dur
    };
  });

  if (totalSec === 0) totalSec = 1;

  // Balanced visual column widths: blend damped duration weight with a minimum column floor
  // so short transitions (e.g. RoxZone) never crush adjacent labels or drop S1..Sn ticks.
  const dampedWeights = rawStages.map(s => Math.pow(Math.max(10, s.dur), 0.38));
  const avgWeight = dampedWeights.reduce((a, b) => a + b, 0) / Math.max(1, dampedWeights.length);
  const blendedWeights = dampedWeights.map(w => (w * 0.55) + (avgWeight * 0.45));
  const weightSum = blendedWeights.reduce((a, b) => a + b, 0) || 1;

  const th = resolveHrZoneThresholds();
  const peakHighlightCutoff = Math.max(th.z3 || 155, Math.round(actualMinHr + (actualMaxHr - actualMinHr) * 0.62));

  let cursorX = padLeft;
  let peakStageIdx = 0;
  let minStageIdx = 0;

  const stageSegments = rawStages.map((seg, idx) => {
    const w = (blendedWeights[idx] / weightSum) * plotWidth;
    const x1 = cursorX;
    const x2 = idx === rawStages.length - 1 ? (padLeft + plotWidth) : (cursorX + w);
    const actualW = Math.max(2, x2 - x1);
    const midX = x1 + actualW / 2;
    cursorX = x2;

    const hr = seg.avgHr > 0 ? seg.avgHr : minHr;
    const y = plotBottom - (((hr - minHr) / range) * plotHeight);

    if (hr >= (rawStages[peakStageIdx].avgHr || 0)) peakStageIdx = idx;
    if (hr <= (rawStages[minStageIdx].avgHr || 999)) minStageIdx = idx;

    let nodeColor = '#38bdf8';
    let zoneTier = 'base';
    if (hr >= (th.z4 || 170) || hr >= actualMaxHr - 3) {
      nodeColor = '#f43f5e';
      zoneTier = 'peak';
    } else if (hr >= (th.z2 || 140) || hr >= peakHighlightCutoff) {
      nodeColor = '#f59e0b';
      zoneTier = 'threshold';
    }

    const isHighExertion = hr >= peakHighlightCutoff;

    return {
      ...seg,
      x1,
      x2,
      w: actualW,
      midX,
      y,
      nodeColor,
      zoneTier,
      isHighExertion
    };
  });

  // Build spline control points with graceful organic lead-in and lead-out
  const splinePoints = [];
  if (stageSegments.length === 1) {
    const only = stageSegments[0];
    splinePoints.push({ x: padLeft, y: only.y, seg: only });
    splinePoints.push({ x: only.midX, y: only.y, seg: only });
    splinePoints.push({ x: padLeft + plotWidth, y: only.y, seg: only });
  } else {
    const first = stageSegments[0];
    const second = stageSegments[1];
    const last = stageSegments[stageSegments.length - 1];
    const prevLast = stageSegments[stageSegments.length - 2];

    const clampPlotY = (val) => Math.max(plotTop + 4, Math.min(plotBottom - 4, val));
    const leadInY = clampPlotY(first.y + (first.y - second.y) * 0.22);
    const leadOutY = clampPlotY(last.y + (last.y - prevLast.y) * 0.16);

    splinePoints.push({ x: padLeft, y: leadInY, seg: first });
    stageSegments.forEach(seg => {
      splinePoints.push({ x: seg.midX, y: seg.y, seg });
    });
    splinePoints.push({ x: padLeft + plotWidth, y: leadOutY, seg: last });
  }

  const { linePath, areaPath } = buildMonotoneCubicSpline(splinePoints, plotBottom, plotTop + 4, plotBottom - 2);

  // Smart Collision-Avoidant Callout Layout
  // Prioritize: 1. Global Peak, 2. Global Min, 3. First & Last Stage, 4. Local Extrema, 5. Remaining stages
  const minClearanceX = isExpanded ? 34 : 44;
  const placedCallouts = [];

  const priorityOrder = stageSegments.map((seg, idx) => {
    const prevHr = idx > 0 ? stageSegments[idx - 1].avgHr : seg.avgHr;
    const nextHr = idx < stageSegments.length - 1 ? stageSegments[idx + 1].avgHr : seg.avgHr;
    const isLocalPeak = seg.avgHr >= prevHr && seg.avgHr >= nextHr && (seg.avgHr > prevHr || seg.avgHr > nextHr);
    const isLocalValley = seg.avgHr <= prevHr && seg.avgHr <= nextHr && (seg.avgHr < prevHr || seg.avgHr < nextHr);

    let priority = 10;
    if (idx === peakStageIdx) priority = 100;
    else if (idx === stageSegments.length - 1) priority = 85;
    else if (idx === 0) priority = 80;
    else if (idx === minStageIdx) priority = 75;
    else if (isLocalPeak || isLocalValley) priority = 60;
    return { seg, idx, priority };
  }).sort((a, b) => b.priority - a.priority);

  priorityOrder.forEach(({ seg, idx, priority }) => {
    if (seg.avgHr <= 0) return;
    const baseLabelY = Math.max(plotTop + 13, seg.y - (idx === peakStageIdx ? 15 : 12));
    const colliding = placedCallouts.find(p => Math.abs(p.x - seg.midX) < minClearanceX);

    if (!colliding) {
      placedCallouts.push({
        idx,
        x: seg.midX,
        y: baseLabelY,
        anchorY: seg.y,
        hr: seg.avgHr,
        isPeak: idx === peakStageIdx,
        staggered: false,
        priority
      });
    } else if (isExpanded) {
      // In expanded view, stagger vertically so all stage HR values remain visible without colliding
      const canPlaceBelow = seg.y + 22 < plotBottom - 10;
      const staggeredY = canPlaceBelow ? (seg.y + 20) : Math.max(plotTop + 10, colliding.y - 16);
      placedCallouts.push({
        idx,
        x: seg.midX,
        y: staggeredY,
        anchorY: seg.y,
        hr: seg.avgHr,
        isPeak: idx === peakStageIdx,
        staggered: true,
        below: canPlaceBelow,
        priority
      });
    }
  });

  placedCallouts.sort((a, b) => a.idx - b.idx);

  // Compute Executive Summary Telemetry Metrics (matching Image 2)
  const peakSeg = stageSegments[peakStageIdx] || stageSegments[0];
  const lastSeg = stageSegments[stageSegments.length - 1] || stageSegments[0];
  const highExertionSecs = stageSegments
    .filter(s => s.isHighExertion)
    .reduce((acc, s) => acc + s.dur, 0) || peakSeg.dur;
  const peakEffortMins = Math.floor(highExertionSecs / 60);
  const peakEffortRemSecs = highExertionSecs % 60;
  const peakEffortTimeStr = `${peakEffortMins}m ${peakEffortRemSecs.toString().padStart(2, '0')}s`;

  const peakDeltaPct = resolvedWorkoutAvgHr > 0
    ? (((actualMaxHr - resolvedWorkoutAvgHr) / resolvedWorkoutAvgHr) * 100).toFixed(1)
    : '0.0';
  const recoveryDropBpm = actualMaxHr - (lastSeg ? lastSeg.avgHr : actualMinHr);
  const hasRecoveryDrop = stageSegments.length >= 2 && recoveryDropBpm >= 5 && lastSeg.stageIdx !== peakStageIdx;

  // Detect longest contiguous high-exertion / threshold run for executive annotation
  let bestRunStart = -1;
  let bestRunLen = 0;
  let curStart = -1;
  let curLen = 0;
  stageSegments.forEach((s, i) => {
    if (s.isHighExertion) {
      if (curStart === -1) curStart = i;
      curLen++;
      if (curLen > bestRunLen) {
        bestRunLen = curLen;
        bestRunStart = curStart;
      }
    } else {
      curStart = -1;
      curLen = 0;
    }
  });

  return {
    minHr,
    maxHr,
    actualMinHr,
    actualMaxHr,
    resolvedWorkoutAvgHr,
    range,
    totalSec,
    plotLeft: padLeft,
    plotRight: padLeft + plotWidth,
    plotTop,
    plotBottom,
    plotWidth,
    plotHeight,
    stageSegments,
    linePath,
    areaPath,
    placedCallouts,
    peakStageIdx,
    minStageIdx,
    peakSeg,
    lastSeg,
    peakEffortTimeStr,
    peakDeltaPct,
    recoveryDropBpm,
    hasRecoveryDrop,
    sustainedRun: bestRunLen >= 2 ? { startIdx: bestRunStart, endIdx: bestRunStart + bestRunLen - 1 } : null
  };
}

export function renderHeartRateChart(hrArray, timeArray, containerEl) {
  if (!containerEl) return;
  containerEl.innerHTML = '';

  if (!hrArray || hrArray.length === 0) {
    containerEl.innerHTML = `<div class="empty-state" style="height: 100%; display: flex; align-items: center; justify-content: center;"><p style="font-size: 12px;">No heart rate stream telemetry available for this activity.</p></div>`;
    return;
  }

  containerEl.lastHrData = hrArray;
  containerEl.lastTimeData = timeArray;

  const maxPoints = 160;
  const step = Math.max(1, Math.floor(hrArray.length / maxPoints));
  const sampledHr = [];
  const sampledTime = [];
  for (let i = 0; i < hrArray.length; i += step) {
    sampledHr.push(hrArray[i]);
    sampledTime.push(timeArray && timeArray[i] !== undefined ? timeArray[i] : i);
  }

  const actualMin = Math.min(...sampledHr);
  const actualMax = Math.max(...sampledHr);
  const avgHr = Math.round(sampledHr.reduce((a, b) => a + b, 0) / sampledHr.length);
  const minHr = Math.max(40, actualMin - 12);
  const maxHr = Math.min(220, actualMax + 14);
  const range = Math.max(12, maxHr - minHr);

  const width = 720;
  const height = 240;
  const padLeft = 46;
  const padRight = 16;
  const padTop = 24;
  const padBottom = 26;
  const plotWidth = width - padLeft - padRight;
  const plotHeight = height - padTop - padBottom;
  const plotBottom = height - padBottom;

  const pts = sampledHr.map((hr, idx) => {
    const x = padLeft + (idx / Math.max(1, sampledHr.length - 1)) * plotWidth;
    const y = plotBottom - (((hr - minHr) / range) * plotHeight);
    return { x, y, hr };
  });

  const { linePath, areaPath } = buildMonotoneCubicSpline(pts, plotBottom, padTop + 2, plotBottom - 2);

  let vGridHtml = '';
  for (let i = 0; i <= 8; i++) {
    const gx = padLeft + (i / 8) * plotWidth;
    vGridHtml += `<line x1="${gx.toFixed(1)}" y1="${padTop}" x2="${gx.toFixed(1)}" y2="${plotBottom}" stroke="rgba(148, 163, 184, 0.05)" stroke-width="1"/>`;
  }

  containerEl.innerHTML = `
    <div id="hr-chart-tooltip"></div>
    <div class="exec-hr-panel-bar">
      <span class="exec-hr-panel-label">CONTINUOUS TELEMETRY STREAM • REAL-TIME HR PROFILE</span>
      <div class="exec-hr-status-pill">
        <span class="exec-hr-delta-tag">+${avgHr > 0 ? (((actualMax - avgHr) / avgHr) * 100).toFixed(1) : '0.0'}% PEAK</span>
        <span class="exec-status-dot dot-emerald"></span>
        <span class="exec-status-dot dot-amber"></span>
        <span class="exec-status-dot dot-rose"></span>
      </div>
    </div>
    <svg id="hr-svg-chart" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" style="width: 100%; height: auto; display: block; overflow: visible; cursor: pointer;">
      <defs>
        <linearGradient id="hrStreamStrokeGrad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stop-color="#38bdf8"/>
          <stop offset="48%" stop-color="#f59e0b"/>
          <stop offset="100%" stop-color="#f43f5e"/>
        </linearGradient>
        <linearGradient id="hrGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#f43f5e" stop-opacity="0.42"/>
          <stop offset="55%" stop-color="#fb923c" stop-opacity="0.14"/>
          <stop offset="100%" stop-color="#0f172a" stop-opacity="0.0"/>
        </linearGradient>
      </defs>
      ${vGridHtml}
      <line x1="${padLeft}" y1="${padTop}" x2="${width - padRight}" y2="${padTop}" stroke="rgba(148,163,184,0.1)" stroke-dasharray="3,3"/>
      <text x="6" y="${padTop + 4}" fill="rgba(148,163,184,0.65)" font-size="10" font-family="'Outfit', monospace" font-weight="600">${maxHr} bpm</text>
      <line x1="${padLeft}" y1="${(padTop + plotBottom) / 2}" x2="${width - padRight}" y2="${(padTop + plotBottom) / 2}" stroke="rgba(148,163,184,0.08)" stroke-dasharray="3,3"/>
      <text x="6" y="${(padTop + plotBottom) / 2 + 4}" fill="rgba(148,163,184,0.55)" font-size="10" font-family="'Outfit', monospace" font-weight="600">${Math.round((maxHr + minHr) / 2)} bpm</text>
      <line x1="${padLeft}" y1="${plotBottom}" x2="${width - padRight}" y2="${plotBottom}" stroke="rgba(148,163,184,0.14)"/>
      <text x="6" y="${plotBottom + 3}" fill="rgba(148,163,184,0.55)" font-size="10" font-family="'Outfit', monospace" font-weight="600">${minHr} bpm</text>
      <path d="${areaPath}" fill="url(#hrGrad)" style="pointer-events: none;"/>
      <path d="${linePath}" fill="none" stroke="url(#hrStreamStrokeGrad)" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 0 6px rgba(244, 63, 94, 0.35)); pointer-events: none;"/>
      <line id="hr-stream-hover-line" x1="0" y1="${padTop}" x2="0" y2="${plotBottom}" stroke="rgba(255,255,255,0.45)" stroke-width="1" stroke-dasharray="3,3" style="display: none; pointer-events: none;"/>
    </svg>
    <div class="exec-hr-footer-strip">
      <div class="exec-hr-metric-cell">
        <span class="exec-metric-lbl">AVERAGE HR</span>
        <strong class="exec-metric-val">${avgHr} <small>bpm</small></strong>
      </div>
      <div class="exec-hr-metric-cell">
        <span class="exec-metric-lbl">MAX HR</span>
        <strong class="exec-metric-val text-rose">${actualMax} <small>bpm</small></strong>
      </div>
      <div class="exec-hr-metric-cell">
        <span class="exec-metric-lbl">MIN / BASE HR</span>
        <strong class="exec-metric-val text-sky">${actualMin} <small>bpm</small></strong>
      </div>
    </div>
  `;

  const svgEl = containerEl.querySelector('#hr-svg-chart');
  const tooltip = containerEl.querySelector('#hr-chart-tooltip');
  const hoverLine = containerEl.querySelector('#hr-stream-hover-line');
  if (svgEl && tooltip && hoverLine) {
    svgEl.addEventListener('mousemove', (e) => {
      const rect = svgEl.getBoundingClientRect();
      const relX = Math.max(0, Math.min(1, (e.clientX - rect.left) / (rect.width || 1)));
      const idx = Math.min(sampledHr.length - 1, Math.max(0, Math.round(relX * (sampledHr.length - 1))));
      const svgX = padLeft + relX * plotWidth;
      hoverLine.setAttribute('x1', svgX);
      hoverLine.setAttribute('x2', svgX);
      hoverLine.style.display = 'block';
      const tSec = sampledTime[idx] || 0;
      const durStr = `${Math.floor(tSec / 60)}m ${(Math.floor(tSec % 60)).toString().padStart(2, '0')}s`;
      tooltip.style.display = 'block';
      tooltip.style.opacity = '1';
      tooltip.style.left = `${relX * 100}%`;
      tooltip.innerHTML = `<div style="text-align:center; line-height:1.3;"><span style="color:#fda4af;">❤️ ${sampledHr[idx]} bpm</span> • <span style="color:var(--text-muted);">${durStr}</span></div>`;
    });
    svgEl.addEventListener('mouseleave', () => {
      hoverLine.style.display = 'none';
      tooltip.style.opacity = '0';
      tooltip.style.display = 'none';
    });
  }
}

export function renderStagesHeartRateChart(workout, containerEl) {
  if (!containerEl) return;
  containerEl.innerHTML = '';

  const width = 740;
  const height = 248;
  const padLeft = 52;
  const padRight = 18;
  const padTop = 34;
  const padBottom = 30;

  const model = buildStagesTelemetryModel(workout, width, height, padLeft, padRight, padTop, padBottom, false);
  if (!model) return;

  const {
    minHr,
    maxHr,
    actualMinHr,
    actualMaxHr,
    resolvedWorkoutAvgHr,
    plotLeft,
    plotRight,
    plotTop,
    plotBottom,
    plotWidth,
    stageSegments,
    linePath,
    areaPath,
    placedCallouts,
    peakStageIdx,
    peakSeg,
    lastSeg,
    peakEffortTimeStr,
    peakDeltaPct,
    hasRecoveryDrop,
    sustainedRun
  } = model;

  let gridHtml = '';
  const horizontalSteps = 3;
  for (let i = 0; i <= horizontalSteps; i++) {
    const ratio = i / horizontalSteps;
    const gy = plotTop + ratio * (plotBottom - plotTop);
    const gHr = Math.round(maxHr - ratio * (maxHr - minHr));
    const isBaseline = i === horizontalSteps;
    gridHtml += `
      <line x1="${plotLeft}" y1="${gy.toFixed(1)}" x2="${plotRight}" y2="${gy.toFixed(1)}" stroke="${isBaseline ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.07)'}" stroke-dasharray="${isBaseline ? 'none' : '3,4'}" style="pointer-events:none;"/>
      <text x="${plotLeft - 8}" y="${(gy + 3.5).toFixed(1)}" fill="rgba(148,163,184,0.6)" font-size="9.5" font-weight="600" font-family="'Outfit', monospace" text-anchor="end" style="pointer-events:none;">${gHr} bpm</text>
    `;
  }

  let bandsHtml = '';
  let peakColumnsHtml = '';
  let labelsHtml = '';

  stageSegments.forEach((seg, idx) => {
    // Subtle vertical technical grid line at every split boundary
    gridHtml += `<line x1="${seg.x1.toFixed(1)}" y1="${plotTop}" x2="${seg.x1.toFixed(1)}" y2="${plotBottom}" stroke="rgba(148,163,184,0.05)" stroke-width="1" style="pointer-events:none;"/>`;

    // Selective Peak Effort / High-Exertion column glow (matching Image 2 Split B & C)
    if (seg.isHighExertion) {
      peakColumnsHtml += `
        <rect x="${seg.x1.toFixed(1)}" y="${plotTop}" width="${seg.w.toFixed(1)}" height="${(plotBottom - plotTop).toFixed(1)}" fill="url(#hrPeakColGrad)" style="pointer-events:none;"/>
        <line x1="${seg.x1.toFixed(1)}" y1="${plotTop}" x2="${seg.x1.toFixed(1)}" y2="${plotBottom}" stroke="rgba(244,63,94,0.22)" stroke-width="1" stroke-dasharray="2,3" style="pointer-events:none;"/>
        <line x1="${seg.x2.toFixed(1)}" y1="${plotTop}" x2="${seg.x2.toFixed(1)}" y2="${plotBottom}" stroke="rgba(244,63,94,0.22)" stroke-width="1" stroke-dasharray="2,3" style="pointer-events:none;"/>
      `;
    }

    bandsHtml += `<rect class="stage-band-interactive" data-stage-idx="${idx}" x="${seg.x1.toFixed(1)}" y="${plotTop}" width="${seg.w.toFixed(1)}" height="${(plotBottom - plotTop).toFixed(1)}" fill="transparent" stroke="transparent" stroke-width="1" style="cursor: pointer;"/>`;

    // Clean X-axis stage labels (S1..Sn) along the bottom track
    const showAllOrEven = stageSegments.length <= 16 || idx % 2 === 0 || idx === stageSegments.length - 1 || idx === peakStageIdx;
    if (showAllOrEven) {
      const isPeak = idx === peakStageIdx;
      labelsHtml += `
        <line x1="${seg.midX.toFixed(1)}" y1="${plotBottom}" x2="${seg.midX.toFixed(1)}" y2="${plotBottom + 4}" stroke="${isPeak ? '#f43f5e' : 'rgba(148,163,184,0.28)'}" stroke-width="1" style="pointer-events:none;"/>
        <text x="${seg.midX.toFixed(1)}" y="${height - 8}" fill="${isPeak ? '#fda4af' : 'rgba(148,163,184,0.72)'}" font-size="${stageSegments.length > 12 ? '8.5' : '9.5'}" font-weight="700" font-family="'Outfit', sans-serif" text-anchor="middle" style="pointer-events:none;">S${seg.stageNum}</text>
      `;
    }
  });

  // Contextual annotations inspired by Image 2 (Sustained Threshold / Recovery Stage)
  let annotationsHtml = '';
  if (sustainedRun) {
    const sStart = stageSegments[sustainedRun.startIdx];
    const sEnd = stageSegments[sustainedRun.endIdx];
    const spanMidX = (sStart.x1 + sEnd.x2) / 2;
    annotationsHtml += `
      <text x="${spanMidX.toFixed(1)}" y="${plotTop - 10}" fill="rgba(251, 146, 60, 0.85)" font-size="8.5" font-weight="700" font-family="'Outfit', sans-serif" letter-spacing="0.8" text-anchor="middle" style="pointer-events:none;">AEROBIC THRESHOLD SUSTAINED</text>
    `;
  }
  if (hasRecoveryDrop && lastSeg) {
    annotationsHtml += `
      <text x="${Math.min(plotRight - 36, lastSeg.midX).toFixed(1)}" y="${Math.max(plotTop + 12, lastSeg.y - 24).toFixed(1)}" fill="rgba(56, 189, 248, 0.85)" font-size="8" font-weight="700" font-family="'Outfit', sans-serif" letter-spacing="0.6" text-anchor="middle" style="pointer-events:none;">RECOVERY</text>
    `;
  }

  let dotsHtml = '';
  stageSegments.forEach((seg, idx) => {
    if (seg.avgHr > 0) {
      const isPeak = idx === peakStageIdx;
      if (isPeak) {
        dotsHtml += `<line x1="${seg.midX.toFixed(1)}" y1="${seg.y.toFixed(1)}" x2="${seg.midX.toFixed(1)}" y2="${plotBottom}" stroke="rgba(244,63,94,0.45)" stroke-width="1.2" stroke-dasharray="3,3" style="pointer-events:none;"/>`;
      }
      dotsHtml += `
        <circle cx="${seg.midX.toFixed(1)}" cy="${seg.y.toFixed(1)}" r="${isPeak ? '8.5' : '6'}" fill="${seg.nodeColor}" fill-opacity="${isPeak ? '0.28' : '0.18'}" style="pointer-events:none;"/>
        <circle class="stage-band-interactive stage-dot-interactive" data-stage-idx="${seg.stageIdx}" cx="${seg.midX.toFixed(1)}" cy="${seg.y.toFixed(1)}" r="${isPeak ? '4.2' : '3.4'}" fill="#ffffff" stroke="${seg.nodeColor}" stroke-width="${isPeak ? '2.4' : '2'}" style="cursor: pointer;"/>
      `;
    }
  });

  let calloutsHtml = '';
  placedCallouts.forEach(c => {
    if (c.isPeak) {
      calloutsHtml += `
        <rect x="${(c.x - 18).toFixed(1)}" y="${(c.y - 11).toFixed(1)}" width="36" height="15" rx="7.5" fill="rgba(15, 23, 42, 0.92)" stroke="rgba(244, 63, 94, 0.55)" stroke-width="1" style="pointer-events:none;"/>
        <text x="${c.x.toFixed(1)}" y="${c.y.toFixed(1)}" fill="#ffffff" font-size="10" font-weight="800" font-family="'Outfit', sans-serif" text-anchor="middle" style="pointer-events:none;">${c.hr}</text>
      `;
    } else {
      calloutsHtml += `
        <text x="${c.x.toFixed(1)}" y="${c.y.toFixed(1)}" fill="#e2e8f0" font-size="9.5" font-weight="700" font-family="'Outfit', sans-serif" text-anchor="middle" style="pointer-events:none; text-shadow: 0 1px 4px rgba(0,0,0,0.9);">${c.hr}</text>
      `;
    }
  });

  // Mini sparkline path for the Executive Footer Strip
  const sparkPts = stageSegments.map((s, i) => {
    const sx = (i / Math.max(1, stageSegments.length - 1)) * 48;
    const sy = 16 - (((s.avgHr - minHr) / Math.max(1, maxHr - minHr)) * 13);
    return `${sx.toFixed(1)},${sy.toFixed(1)}`;
  }).join(' ');

  containerEl.innerHTML = `
    <div id="hr-chart-tooltip"></div>
    <div class="exec-hr-panel-bar">
      <span class="exec-hr-panel-label">SPLIT TELEMETRY: ACTIVITY INTENSITY &amp; RECOVERY PROFILE</span>
      <div class="exec-hr-status-pill">
        <span class="exec-hr-delta-tag">+${peakDeltaPct}%</span>
        <span class="exec-status-dot dot-emerald"></span>
        <span class="exec-status-dot dot-sky"></span>
        <span class="exec-status-dot dot-amber"></span>
      </div>
    </div>
    <svg id="hr-stages-svg-chart" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" style="width: 100%; height: auto; display: block; overflow: visible; cursor: pointer;">
      <defs>
        <linearGradient id="hrStagesStrokeGrad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stop-color="#38bdf8"/>
          <stop offset="45%" stop-color="#fb923c"/>
          <stop offset="100%" stop-color="#f43f5e"/>
        </linearGradient>
        <linearGradient id="hrStagesGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#f43f5e" stop-opacity="0.44"/>
          <stop offset="52%" stop-color="#fb923c" stop-opacity="0.15"/>
          <stop offset="100%" stop-color="#0f172a" stop-opacity="0.0"/>
        </linearGradient>
        <linearGradient id="hrPeakColGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#f43f5e" stop-opacity="0.16"/>
          <stop offset="65%" stop-color="#f43f5e" stop-opacity="0.05"/>
          <stop offset="100%" stop-color="#f43f5e" stop-opacity="0.0"/>
        </linearGradient>
        <filter id="hrSplineGlow" x="-10%" y="-20%" width="120%" height="140%">
          <feGaussianBlur stdDeviation="2.5" result="blur"/>
          <feMerge>
            <feMergeNode in="blur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      ${peakColumnsHtml}
      ${gridHtml}
      ${bandsHtml}
      <path d="${areaPath}" fill="url(#hrStagesGrad)" style="pointer-events: none;"/>
      <path d="${linePath}" fill="none" stroke="url(#hrStagesStrokeGrad)" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round" filter="url(#hrSplineGlow)" style="pointer-events: none;"/>
      ${annotationsHtml}
      ${dotsHtml}
      ${calloutsHtml}
      ${labelsHtml}
      <line id="hr-stages-hover-line" x1="0" y1="${plotTop}" x2="0" y2="${plotBottom}" stroke="rgba(255,255,255,0.5)" stroke-width="1.2" stroke-dasharray="3,3" style="display: none; pointer-events: none;"/>
      <circle id="hr-stages-hover-dot" cx="0" cy="0" r="5" fill="#f43f5e" stroke="#ffffff" stroke-width="2" style="display: none; pointer-events: none;"/>
    </svg>
    <div class="exec-hr-footer-strip">
      <div class="exec-hr-metric-cell">
        <span class="exec-metric-lbl">AVERAGE HR</span>
        <strong class="exec-metric-val">${resolvedWorkoutAvgHr} <small>bpm</small></strong>
      </div>
      <div class="exec-hr-metric-cell">
        <span class="exec-metric-lbl">MAX HR (S${peakSeg.stageNum})</span>
        <strong class="exec-metric-val text-rose">${actualMaxHr} <small>bpm</small></strong>
      </div>
      <div class="exec-hr-metric-cell exec-hr-metric-spark">
        <div>
          <span class="exec-metric-lbl">PEAK EFFORT</span>
          <strong class="exec-metric-val">${peakEffortTimeStr}</strong>
        </div>
        <div class="exec-mini-spark-wrap">
          <span class="exec-spark-delta">+${peakDeltaPct}%</span>
          <svg width="50" height="18" viewBox="0 0 50 18" style="overflow:visible;">
            <polyline fill="none" stroke="#f43f5e" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" points="${sparkPts}"/>
          </svg>
        </div>
      </div>
      <div class="exec-hr-metric-cell">
        <span class="exec-metric-lbl">${hasRecoveryDrop ? 'FINAL RECOVERY' : 'MIN SPLIT HR'}</span>
        <strong class="exec-metric-val text-sky">${hasRecoveryDrop ? lastSeg.avgHr : actualMinHr} <small>bpm</small></strong>
      </div>
    </div>
  `;

  const svgEl = containerEl.querySelector('#hr-stages-svg-chart');
  const tooltip = containerEl.querySelector('#hr-chart-tooltip');
  const hoverLine = containerEl.querySelector('#hr-stages-hover-line');
  const hoverDot = containerEl.querySelector('#hr-stages-hover-dot');

  if (svgEl && tooltip && hoverLine) {
    svgEl.addEventListener('mousemove', (e) => {
      const rect = svgEl.getBoundingClientRect();
      const relX = Math.max(0, Math.min(1, (e.clientX - rect.left) / (rect.width || 1)));
      const svgX = relX * width;

      const activeSeg = stageSegments.find(s => svgX >= s.x1 && svgX <= s.x2) || (svgX < plotLeft ? stageSegments[0] : stageSegments[stageSegments.length - 1]);
      if (!activeSeg) return;

      hoverLine.setAttribute('x1', activeSeg.midX);
      hoverLine.setAttribute('x2', activeSeg.midX);
      hoverLine.style.display = 'block';
      if (hoverDot) {
        hoverDot.setAttribute('cx', activeSeg.midX);
        hoverDot.setAttribute('cy', activeSeg.y);
        hoverDot.style.display = 'block';
      }

      const stName = activeSeg.stationName || activeSeg.name || '';
      const isRox = activeSeg.type === 'RoxZone' || activeSeg.type === 'Roxzone' || stName.toLowerCase().includes('rox');
      const stageLabel = activeSeg.type === 'Run' ? '🏃 Run' : (isRox ? '⚡ RoxZone' : (activeSeg.type === 'Rest' ? '⏳ Rest' : `💪 ${escapeHtml(stName || 'Station')}`));
      const durStr = `${activeSeg.durationMin || 0}m ${(activeSeg.durationSec || 0).toString().padStart(2, '0')}s`;

      tooltip.style.display = 'block';
      tooltip.style.opacity = '1';
      tooltip.style.left = `${(activeSeg.midX / width) * 100}%`;
      tooltip.innerHTML = `<div style="text-align:center; line-height:1.35;"><strong>Stage ${activeSeg.stageNum}: ${stageLabel}</strong><br><span style="color:#fda4af; font-weight:700;">❤️ ${activeSeg.avgHr || '--'} bpm</span> • <span style="color:var(--text-muted);">${durStr}</span></div>`;
    });

    svgEl.addEventListener('mouseleave', () => {
      hoverLine.style.display = 'none';
      if (hoverDot) hoverDot.style.display = 'none';
      tooltip.style.opacity = '0';
      tooltip.style.display = 'none';
    });
  }
}

export function setupExpandedHeartRateModal() {
  if (typeof document === 'undefined' || expandedHrModalSetupDone) return;
  const dialog = document.getElementById('expanded-hr-dialog');
  const closeBtn = document.getElementById('close-expanded-hr-btn');
  if (!dialog) return;
  expandedHrModalSetupDone = true;

  const closeExpanded = () => {
    dialog.close();
    dialog.style.display = 'none';
  };

  if (closeBtn) {
    closeBtn.addEventListener('click', closeExpanded);
  }
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) closeExpanded();
  });
}

export function openExpandedHeartRateModal(workout, initialStageIdx = null) {
  if (typeof document === 'undefined') return;
  const targetWorkout = workout || currentHrChartWorkout || (currentWorkoutDetailId ? (store.workouts || []).find(w => w && (w.id === currentWorkoutDetailId || String(w.id) === String(currentWorkoutDetailId))) : null);
  if (!targetWorkout) return;
  setupExpandedHeartRateModal();

  const dialog = document.getElementById('expanded-hr-dialog');
  const titleEl = document.getElementById('expanded-hr-dialog-title') || document.getElementById('expanded-hr-title');
  const subtitleEl = document.getElementById('expanded-hr-dialog-subtitle') || document.getElementById('expanded-hr-subtitle');
  const badgesEl = document.getElementById('expanded-hr-badges');
  const chartContainer = document.getElementById('expanded-hr-chart-content') || document.getElementById('expanded-hr-chart-container');
  const hudContainer = document.getElementById('expanded-hr-segment-hud');

  if (!dialog || !chartContainer) return;

  const stageHrs = Array.isArray(targetWorkout.stages)
    ? targetWorkout.stages.map(s => Number(s.avgHr || s.avg_hr || 0)).filter(v => v > 0)
    : [];
  const resolvedAvgHr = targetWorkout.avgHeartrate || targetWorkout.avgHr || (stageHrs.length > 0 ? Math.round(stageHrs.reduce((a, b) => a + b, 0) / stageHrs.length) : 0);
  const resolvedMaxHr = targetWorkout.maxHeartrate || targetWorkout.maxHr || (stageHrs.length > 0 ? Math.max(...stageHrs) : 0);

  if (titleEl) {
    titleEl.innerHTML = `❤️ ${escapeHtml(targetWorkout.name || targetWorkout.type || 'Workout')} — Heart Rate Splits &amp; Analysis`;
  }
  if (subtitleEl) {
    const datePart = `${targetWorkout.date || ''}${targetWorkout.time ? ` at ${targetWorkout.time}` : ''}`.trim();
    const durPart = `${targetWorkout.duration || 0}m total`;
    const coreSub = datePart ? `${datePart} • ${durPart}` : durPart;
    subtitleEl.innerText = `EXECUTIVE HEALTH & PERFORMANCE MONITOR • ${coreSub} • TIME SERIES DATA`;
  }
  if (badgesEl) {
    let badgesHtml = '';
    if (resolvedAvgHr) {
      badgesHtml += `<span class="expanded-hr-badge" style="font-size: 11px; font-weight: 700; color: #f43f5e; background: rgba(244, 63, 94, 0.1); border: 1px solid rgba(244, 63, 94, 0.25); padding: 4px 10px; border-radius: 999px; white-space: nowrap; display: inline-flex; align-items: center; gap: 4px;">❤️ Avg: ${resolvedAvgHr} bpm</span>`;
    }
    if (resolvedMaxHr) {
      badgesHtml += `<span class="expanded-hr-badge" style="font-size: 11px; font-weight: 700; color: #ef4444; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.25); padding: 4px 10px; border-radius: 999px; white-space: nowrap; display: inline-flex; align-items: center; gap: 4px;">🔥 Peak: ${resolvedMaxHr} bpm</span>`;
    }
    if (targetWorkout.calories) {
      badgesHtml += `<span class="expanded-hr-badge" style="font-size: 11px; font-weight: 700; color: #10b981; background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.25); padding: 4px 10px; border-radius: 999px; white-space: nowrap; display: inline-flex; align-items: center; gap: 4px;">⚡ ${targetWorkout.calories} kcal</span>`;
    }
    badgesHtml += `
      <span class="expanded-hr-badge" style="font-size: 10.5px; font-weight: 600; color: #cbd5e1; background: rgba(15, 23, 42, 0.75); border: 1px solid rgba(148, 163, 184, 0.2); padding: 4px 10px; border-radius: 999px; white-space: nowrap; display: inline-flex; align-items: center; gap: 8px;">
        <span style="display:inline-flex;align-items:center;gap:4px;"><i style="width:7px;height:7px;border-radius:50%;background:#f43f5e;display:inline-block;"></i>Peak Z5</span>
        <span style="display:inline-flex;align-items:center;gap:4px;"><i style="width:7px;height:7px;border-radius:50%;background:#f59e0b;display:inline-block;"></i>Threshold</span>
        <span style="display:inline-flex;align-items:center;gap:4px;"><i style="width:7px;height:7px;border-radius:50%;background:#38bdf8;display:inline-block;"></i>Aerobic</span>
      </span>
    `;
    badgesEl.innerHTML = badgesHtml;
  }

  if (hudContainer) {
    hudContainer.innerHTML = `
      <span style="grid-column: 1 / -1; color: var(--text-muted); font-size: 12.5px; font-style: italic; width: 100%; text-align: center;">
        👆 Tap or hover any stage split on the chart below to inspect segment metrics.
      </span>
    `;
  }

  dialog.style.display = 'flex';
  if (typeof dialog.showModal === 'function' && !dialog.open) {
    dialog.showModal();
  }

  const hasStages = Array.isArray(targetWorkout.stages) && targetWorkout.stages.length > 0;
  const hasStream = Boolean(targetWorkout.streams && targetWorkout.streams.heartrate && Array.isArray(targetWorkout.streams.heartrate.data) && targetWorkout.streams.heartrate.data.length > 0);

  if (hasStages && (initialStageIdx !== null || !hasStream)) {
    renderExpandedStagesHeartRateChart(targetWorkout, chartContainer, initialStageIdx);
  } else if (hasStream) {
    const timeArr = targetWorkout.streams.time ? targetWorkout.streams.time.data : [];
    renderExpandedHeartRateChart(targetWorkout, targetWorkout.streams.heartrate.data, timeArr, chartContainer);
  } else if (hasStages) {
    renderExpandedStagesHeartRateChart(targetWorkout, chartContainer, initialStageIdx);
  }
}

export function setupShareCardModal() {
  if (typeof document === 'undefined' || shareCardModalSetupDone) return;
  shareCardModalSetupDone = true;

  const dialog = document.getElementById('share-card-dialog');
  const closeBtn = document.getElementById('close-share-card-btn');
  const footerCloseBtn = document.getElementById('btn-close-share-card');
  const downloadBtn = document.getElementById('btn-download-share-card');

  if (closeBtn) closeBtn.addEventListener('click', () => dialog?.close());
  if (footerCloseBtn) footerCloseBtn.addEventListener('click', () => dialog?.close());

  if (dialog) {
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
  }

  if (downloadBtn) {
    downloadBtn.addEventListener('click', async () => {
      const workout = findWorkoutInStore(currentWorkoutDetailId);
      const rawDate = workout?.date || workout?.startTime || new Date().toISOString().split('T')[0];
      const cleanDate = String(rawDate).slice(0, 10).replace(/[^0-9-]/g, '');
      const filename = `RoxKit_Card_${cleanDate || 'workout'}.png`;

      if (typeof navigator !== 'undefined' && navigator.share && activeShareCardBlob) {
        try {
          const file = new File([activeShareCardBlob], filename, { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: 'RoxKit Performance Card'
            });
            return;
          }
        } catch (err) {
          if (err && err.name === 'AbortError') return; // Athlete cancelled share sheet
          console.warn('Web Share failed, falling back to download:', err);
        }
      }

      const previewImg = document.getElementById('share-card-img');
      const canvas = document.getElementById('share-card-canvas');
      const dataUrl = previewImg?.src || (canvas ? canvas.toDataURL('image/png') : null);

      if (dataUrl) {
        const a = document.createElement('a');
        a.download = filename;
        a.href = dataUrl;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast('Performance card downloaded!');
        }
      } else {
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast('Card image not ready to download.', 'warning');
        }
      }
    });
  }
}

export function openShareCardModal(workout) {
  if (typeof document === 'undefined') return;
  const targetWorkout = workout || findWorkoutInStore(currentWorkoutDetailId);
  if (!targetWorkout) {
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast('No workout selected to share.', 'warning');
    }
    return;
  }

  setupShareCardModal();

  const dialog = document.getElementById('share-card-dialog');
  const previewImg = document.getElementById('share-card-img');
  const loadingEl = document.getElementById('share-card-loading');
  const canvas = document.getElementById('share-card-canvas');

  if (previewImg) {
    previewImg.style.display = 'none';
    previewImg.src = '';
  }
  if (loadingEl) {
    loadingEl.style.display = 'block';
  }
  activeShareCardBlob = null;

  if (dialog && typeof dialog.showModal === 'function') {
    dialog.showModal();
  }

  setTimeout(async () => {
    try {
      if (!canvas) throw new Error('Canvas element #share-card-canvas not found');
      renderInstagramCard(targetWorkout, { canvas });
      const dataUrl = canvas.toDataURL('image/png');
      if (previewImg) {
        previewImg.src = dataUrl;
        previewImg.style.display = 'block';
      }
      if (loadingEl) {
        loadingEl.style.display = 'none';
      }
      if (typeof canvas.toBlob === 'function') {
        canvas.toBlob((blob) => {
          activeShareCardBlob = blob;
        }, 'image/png');
      }
    } catch (err) {
      console.error('Failed to generate sharing card:', err);
      if (loadingEl) loadingEl.style.display = 'none';
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast('Failed to generate sharing card', 'error');
      }
    }
  }, 100);
}

export function renderExpandedHeartRateChart(workout, hrArray, timeArray, containerEl) {
  if (!containerEl) return;
  containerEl.innerHTML = '';

  const maxPoints = 240;
  const step = Math.max(1, Math.floor(hrArray.length / maxPoints));
  const sampledHr = [];
  const sampledTime = [];
  for (let i = 0; i < hrArray.length; i += step) {
    sampledHr.push(hrArray[i]);
    sampledTime.push(timeArray && timeArray[i] !== undefined ? timeArray[i] : i);
  }

  const actualMin = Math.min(...sampledHr);
  const actualMax = Math.max(...sampledHr);
  const avgHr = Math.round(sampledHr.reduce((a, b) => a + b, 0) / sampledHr.length);
  const minHr = Math.max(40, actualMin - 12);
  const maxHr = Math.min(220, actualMax + 14);
  const range = Math.max(12, maxHr - minHr);

  const width = 880;
  const height = 320;
  const padLeft = 56;
  const padRight = 22;
  const padTop = 30;
  const padBottom = 32;
  const plotWidth = width - padLeft - padRight;
  const plotHeight = height - padTop - padBottom;
  const plotBottom = height - padBottom;

  const pts = sampledHr.map((hr, idx) => {
    const x = padLeft + (idx / Math.max(1, sampledHr.length - 1)) * plotWidth;
    const y = plotBottom - (((hr - minHr) / range) * plotHeight);
    return { x, y, hr };
  });

  const { linePath, areaPath } = buildMonotoneCubicSpline(pts, plotBottom, padTop + 2, plotBottom - 2);

  let gridHtml = '';
  for (let i = 0; i <= 10; i++) {
    const gx = padLeft + (i / 10) * plotWidth;
    gridHtml += `<line x1="${gx.toFixed(1)}" y1="${padTop}" x2="${gx.toFixed(1)}" y2="${plotBottom}" stroke="rgba(148,163,184,0.05)" stroke-width="1"/>`;
  }

  containerEl.innerHTML = `
    <div id="hr-chart-tooltip"></div>
    <div class="exec-expanded-chart-shell">
      <div class="exec-hr-panel-bar">
        <span class="exec-hr-panel-label">HIGH-RESOLUTION TELEMETRY STREAM • CARDIOVASCULAR OUTPUT</span>
        <div class="exec-hr-status-pill">
          <span class="exec-hr-delta-tag">PEAK ${actualMax} BPM</span>
          <span class="exec-status-dot dot-emerald"></span>
          <span class="exec-status-dot dot-amber"></span>
          <span class="exec-status-dot dot-rose"></span>
        </div>
      </div>
      <svg id="exp-hr-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" style="width: 100%; height: auto; max-height: 100%; display: block; overflow: visible; cursor: crosshair;">
        <defs>
          <linearGradient id="expHrStrokeGrad" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stop-color="#38bdf8"/>
            <stop offset="48%" stop-color="#fb923c"/>
            <stop offset="100%" stop-color="#f43f5e"/>
          </linearGradient>
          <linearGradient id="expHrGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#f43f5e" stop-opacity="0.45"/>
            <stop offset="55%" stop-color="#fb923c" stop-opacity="0.14"/>
            <stop offset="100%" stop-color="#0f172a" stop-opacity="0.0"/>
          </linearGradient>
        </defs>
        ${gridHtml}
        <line x1="${padLeft}" y1="${padTop}" x2="${width - padRight}" y2="${padTop}" stroke="rgba(148,163,184,0.1)" stroke-dasharray="4,4"/>
        <text x="${padLeft - 8}" y="${padTop + 4}" fill="rgba(148,163,184,0.65)" font-size="11" font-family="'Outfit', monospace" font-weight="600" text-anchor="end">${maxHr} bpm</text>
        <line x1="${padLeft}" y1="${(padTop + plotBottom) / 2}" x2="${width - padRight}" y2="${(padTop + plotBottom) / 2}" stroke="rgba(148,163,184,0.08)" stroke-dasharray="4,4"/>
        <text x="${padLeft - 8}" y="${(padTop + plotBottom) / 2 + 4}" fill="rgba(148,163,184,0.6)" font-size="11" font-family="'Outfit', monospace" font-weight="600" text-anchor="end">${Math.round((maxHr + minHr) / 2)} bpm</text>
        <line x1="${padLeft}" y1="${plotBottom}" x2="${width - padRight}" y2="${plotBottom}" stroke="rgba(148,163,184,0.18)"/>
        <text x="${padLeft - 8}" y="${plotBottom + 4}" fill="rgba(148,163,184,0.6)" font-size="11" font-family="'Outfit', monospace" font-weight="600" text-anchor="end">${minHr} bpm</text>
        <path d="${areaPath}" fill="url(#expHrGrad)"/>
        <path d="${linePath}" fill="none" stroke="url(#expHrStrokeGrad)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 0 8px rgba(244, 63, 94, 0.35));"/>
        <line id="exp-hr-hover-line" x1="0" y1="${padTop}" x2="0" y2="${plotBottom}" stroke="rgba(255,255,255,0.45)" stroke-width="1.5" stroke-dasharray="3,3" style="display: none;"/>
        <circle id="exp-hr-hover-dot" cx="0" cy="0" r="6" fill="#f43f5e" stroke="#ffffff" stroke-width="2" style="display: none;"/>
      </svg>
      <div class="exec-hr-footer-strip">
        <div class="exec-hr-metric-cell">
          <span class="exec-metric-lbl">AVERAGE HR</span>
          <strong class="exec-metric-val">${avgHr} <small>bpm</small></strong>
        </div>
        <div class="exec-hr-metric-cell">
          <span class="exec-metric-lbl">MAX HR</span>
          <strong class="exec-metric-val text-rose">${actualMax} <small>bpm</small></strong>
        </div>
        <div class="exec-hr-metric-cell">
          <span class="exec-metric-lbl">MIN / RECOVERY HR</span>
          <strong class="exec-metric-val text-sky">${actualMin} <small>bpm</small></strong>
        </div>
      </div>
    </div>
  `;

  const svgEl = containerEl.querySelector('#exp-hr-svg');
  const hoverLine = containerEl.querySelector('#exp-hr-hover-line');
  const hoverDot = containerEl.querySelector('#exp-hr-hover-dot');
  const hudContainer = document.getElementById('expanded-hr-segment-hud');

  const updateStreamHUD = (relX) => {
    const idx = Math.min(sampledHr.length - 1, Math.max(0, Math.round(relX * (sampledHr.length - 1))));
    const hrVal = sampledHr[idx];
    const tSec = sampledTime[idx];
    const mins = Math.floor(tSec / 60);
    const secs = Math.floor(tSec % 60);
    const timeStr = `${mins}:${secs.toString().padStart(2, '0')}`;

    const svgX = padLeft + (idx / Math.max(1, sampledHr.length - 1)) * plotWidth;
    const svgY = plotBottom - (((hrVal - minHr) / range) * plotHeight);

    if (hoverLine && hoverDot) {
      hoverLine.setAttribute('x1', svgX);
      hoverLine.setAttribute('x2', svgX);
      hoverLine.style.display = 'block';
      hoverDot.setAttribute('cx', svgX);
      hoverDot.setAttribute('cy', svgY);
      hoverDot.style.display = 'block';
    }

    const th = resolveHrZoneThresholds();
    let zoneLabel = 'Z1 • Endurance';
    let zoneColor = '#94a3b8';
    if (hrVal >= th.z4) { zoneLabel = 'Z5 • Anaerobic'; zoneColor = '#f43f5e'; }
    else if (hrVal >= th.z3) { zoneLabel = 'Z4 • Threshold'; zoneColor = '#f59e0b'; }
    else if (hrVal >= th.z2) { zoneLabel = 'Z3 • Tempo'; zoneColor = '#10b981'; }
    else if (hrVal >= th.z1) { zoneLabel = 'Z2 • Moderate'; zoneColor = '#38bdf8'; }

    if (hudContainer) {
      hudContainer.innerHTML = `
        <div class="hr-hud-chip">
          <span class="hud-lbl">Elapsed Time</span>
          <span class="hud-val">⏱️ ${timeStr}</span>
        </div>
        <div class="hr-hud-chip">
          <span class="hud-lbl">Heart Rate</span>
          <span class="hud-val" style="color: #f43f5e;">❤️ ${hrVal} bpm</span>
        </div>
        <div class="hr-hud-chip">
          <span class="hud-lbl">Intensity Zone</span>
          <span class="hud-val" style="color: ${zoneColor};">${zoneLabel}</span>
        </div>
        <div class="hr-hud-chip">
          <span class="hud-lbl">% of Max HR</span>
          <span class="hud-val">${workout.maxHeartrate ? Math.round((hrVal / workout.maxHeartrate) * 100) + '%' : '--'}</span>
        </div>
      `;
    }
  };

  if (svgEl) {
    svgEl.addEventListener('mousemove', (e) => {
      const rect = svgEl.getBoundingClientRect();
      const relX = Math.max(0, Math.min(1, (e.clientX - rect.left) / (rect.width || 1)));
      updateStreamHUD(relX);
    });
    svgEl.addEventListener('click', (e) => {
      const rect = svgEl.getBoundingClientRect();
      const relX = Math.max(0, Math.min(1, (e.clientX - rect.left) / (rect.width || 1)));
      updateStreamHUD(relX);
    });
  }
}

export function renderExpandedStagesHeartRateChart(workout, containerEl, initialStageIdx = null) {
  if (!containerEl) return;
  containerEl.innerHTML = '';

  const width = 900;
  const height = 330;
  const padLeft = 58;
  const padRight = 22;
  const padTop = 42;
  const padBottom = 34;

  const model = buildStagesTelemetryModel(workout, width, height, padLeft, padRight, padTop, padBottom, true);
  if (!model) return;

  const {
    minHr,
    maxHr,
    actualMinHr,
    actualMaxHr,
    resolvedWorkoutAvgHr,
    plotLeft,
    plotRight,
    plotTop,
    plotBottom,
    stageSegments,
    linePath,
    areaPath,
    placedCallouts,
    peakStageIdx,
    peakSeg,
    lastSeg,
    peakEffortTimeStr,
    peakDeltaPct,
    hasRecoveryDrop,
    sustainedRun
  } = model;

  let gridHtml = '';
  const horizontalSteps = 3;
  for (let i = 0; i <= horizontalSteps; i++) {
    const ratio = i / horizontalSteps;
    const gy = plotTop + ratio * (plotBottom - plotTop);
    const gHr = Math.round(maxHr - ratio * (maxHr - minHr));
    const isBaseline = i === horizontalSteps;
    gridHtml += `
      <line x1="${plotLeft}" y1="${gy.toFixed(1)}" x2="${plotRight}" y2="${gy.toFixed(1)}" stroke="${isBaseline ? 'rgba(148,163,184,0.2)' : 'rgba(148,163,184,0.08)'}" stroke-dasharray="${isBaseline ? 'none' : '4,4'}" style="pointer-events:none;"/>
      <text x="${plotLeft - 10}" y="${(gy + 4).toFixed(1)}" fill="rgba(148,163,184,0.68)" font-size="10.5" font-weight="600" font-family="'Outfit', monospace" text-anchor="end" style="pointer-events:none;">${gHr} bpm</text>
    `;
  }

  let bandsHtml = '';
  let peakColumnsHtml = '';
  let labelsHtml = '';

  stageSegments.forEach((seg, idx) => {
    gridHtml += `<line x1="${seg.x1.toFixed(1)}" y1="${plotTop}" x2="${seg.x1.toFixed(1)}" y2="${plotBottom}" stroke="rgba(148,163,184,0.06)" stroke-width="1" style="pointer-events:none;"/>`;

    if (seg.isHighExertion) {
      peakColumnsHtml += `
        <rect x="${seg.x1.toFixed(1)}" y="${plotTop}" width="${seg.w.toFixed(1)}" height="${(plotBottom - plotTop).toFixed(1)}" fill="url(#expHrPeakColGrad)" style="pointer-events:none;"/>
        <line x1="${seg.x1.toFixed(1)}" y1="${plotTop}" x2="${seg.x1.toFixed(1)}" y2="${plotBottom}" stroke="rgba(244,63,94,0.25)" stroke-width="1" stroke-dasharray="3,3" style="pointer-events:none;"/>
        <line x1="${seg.x2.toFixed(1)}" y1="${plotTop}" x2="${seg.x2.toFixed(1)}" y2="${plotBottom}" stroke="rgba(244,63,94,0.25)" stroke-width="1" stroke-dasharray="3,3" style="pointer-events:none;"/>
      `;
    }

    bandsHtml += `<rect class="stage-band-interactive" data-stage-idx="${idx}" x="${seg.x1.toFixed(1)}" y="${plotTop}" width="${seg.w.toFixed(1)}" height="${(plotBottom - plotTop).toFixed(1)}" fill="transparent" stroke="rgba(255,255,255,0.02)" stroke-width="1"/>`;

    const isPeak = idx === peakStageIdx;
    labelsHtml += `
      <line x1="${seg.midX.toFixed(1)}" y1="${plotBottom}" x2="${seg.midX.toFixed(1)}" y2="${plotBottom + 5}" stroke="${isPeak ? '#f43f5e' : 'rgba(148,163,184,0.32)'}" stroke-width="1.2" style="pointer-events:none;"/>
      <text x="${seg.midX.toFixed(1)}" y="${height - 9}" fill="${isPeak ? '#fda4af' : 'rgba(203,213,225,0.78)'}" font-size="${stageSegments.length > 14 ? '9.5' : '10.5'}" font-weight="700" font-family="'Outfit', sans-serif" text-anchor="middle" style="pointer-events:none;">S${seg.stageNum}</text>
    `;
  });

  let annotationsHtml = '';
  if (sustainedRun) {
    const sStart = stageSegments[sustainedRun.startIdx];
    const sEnd = stageSegments[sustainedRun.endIdx];
    const spanMidX = (sStart.x1 + sEnd.x2) / 2;
    annotationsHtml += `
      <text x="${spanMidX.toFixed(1)}" y="${plotTop - 12}" fill="rgba(251, 146, 60, 0.9)" font-size="9.5" font-weight="700" font-family="'Outfit', sans-serif" letter-spacing="1" text-anchor="middle" style="pointer-events:none;">AEROBIC THRESHOLD SUSTAINED</text>
    `;
  }
  if (hasRecoveryDrop && lastSeg) {
    annotationsHtml += `
      <text x="${Math.min(plotRight - 44, lastSeg.midX).toFixed(1)}" y="${Math.max(plotTop + 14, lastSeg.y - 28).toFixed(1)}" fill="rgba(56, 189, 248, 0.9)" font-size="9" font-weight="700" font-family="'Outfit', sans-serif" letter-spacing="0.8" text-anchor="middle" style="pointer-events:none;">RECOVERY STAGE</text>
    `;
  }

  let dotsHtml = '';
  stageSegments.forEach((seg, idx) => {
    if (seg.avgHr > 0) {
      const isPeak = idx === peakStageIdx;
      if (isPeak) {
        dotsHtml += `<line x1="${seg.midX.toFixed(1)}" y1="${seg.y.toFixed(1)}" x2="${seg.midX.toFixed(1)}" y2="${plotBottom}" stroke="rgba(244,63,94,0.5)" stroke-width="1.3" stroke-dasharray="3,3" style="pointer-events:none;"/>`;
      }
      dotsHtml += `
        <circle cx="${seg.midX.toFixed(1)}" cy="${seg.y.toFixed(1)}" r="${isPeak ? '10' : '7.5'}" fill="${seg.nodeColor}" fill-opacity="${isPeak ? '0.3' : '0.18'}" style="pointer-events:none;"/>
        <circle class="stage-dot-interactive" data-stage-idx="${idx}" cx="${seg.midX.toFixed(1)}" cy="${seg.y.toFixed(1)}" r="${isPeak ? '5.2' : '4.2'}" fill="#ffffff" stroke="${seg.nodeColor}" stroke-width="${isPeak ? '2.6' : '2.2'}" style="cursor: pointer; transition: r 0.15s;"/>
      `;
    }
  });

  let calloutsHtml = '';
  placedCallouts.forEach(c => {
    if (c.staggered) {
      const tickStartY = c.below ? (c.anchorY + 5) : (c.anchorY - 5);
      const tickEndY = c.below ? (c.y - 9) : (c.y + 3);
      calloutsHtml += `<line x1="${c.x.toFixed(1)}" y1="${tickStartY.toFixed(1)}" x2="${c.x.toFixed(1)}" y2="${tickEndY.toFixed(1)}" stroke="rgba(203,213,225,0.4)" stroke-width="1" stroke-dasharray="2,2" style="pointer-events:none;"/>`;
    }
    if (c.isPeak) {
      calloutsHtml += `
        <rect x="${(c.x - 21).toFixed(1)}" y="${(c.y - 13).toFixed(1)}" width="42" height="17" rx="8.5" fill="rgba(15, 23, 42, 0.95)" stroke="rgba(244, 63, 94, 0.65)" stroke-width="1.2" style="pointer-events:none;"/>
        <text x="${c.x.toFixed(1)}" y="${(c.y - 1).toFixed(1)}" fill="#ffffff" font-size="11" font-weight="800" font-family="'Outfit', sans-serif" text-anchor="middle" style="pointer-events:none;">${c.hr}</text>
      `;
    } else {
      calloutsHtml += `
        <rect x="${(c.x - 16).toFixed(1)}" y="${(c.y - 11).toFixed(1)}" width="32" height="14" rx="6" fill="rgba(11, 17, 32, 0.82)" stroke="rgba(148, 163, 184, 0.18)" stroke-width="0.8" style="pointer-events:none;"/>
        <text x="${c.x.toFixed(1)}" y="${c.y.toFixed(1)}" fill="#f1f5f9" font-size="10" font-weight="700" font-family="'Outfit', sans-serif" text-anchor="middle" style="pointer-events:none;">${c.hr}</text>
      `;
    }
  });

  const sparkPts = stageSegments.map((s, i) => {
    const sx = (i / Math.max(1, stageSegments.length - 1)) * 54;
    const sy = 16 - (((s.avgHr - minHr) / Math.max(1, maxHr - minHr)) * 13);
    return `${sx.toFixed(1)},${sy.toFixed(1)}`;
  }).join(' ');

  containerEl.innerHTML = `
    <div id="hr-chart-tooltip"></div>
    <div class="exec-expanded-chart-shell">
      <div class="exec-hr-panel-bar">
        <span class="exec-hr-panel-label">SPLIT ANALYSIS: PEAK CARDIO, THRESHOLD &amp; ACTIVE RECOVERY</span>
        <div class="exec-hr-status-pill">
          <span class="exec-hr-delta-tag">+${peakDeltaPct}% PEAK</span>
          <span class="exec-status-dot dot-emerald"></span>
          <span class="exec-status-dot dot-sky"></span>
          <span class="exec-status-dot dot-amber"></span>
          <span class="exec-status-dot dot-rose"></span>
        </div>
      </div>
      <svg id="exp-hr-stages-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" style="width: 100%; height: auto; max-height: 100%; display: block; overflow: visible;">
        <defs>
          <linearGradient id="expHrStagesStrokeGrad" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stop-color="#38bdf8"/>
            <stop offset="45%" stop-color="#fb923c"/>
            <stop offset="100%" stop-color="#f43f5e"/>
          </linearGradient>
          <linearGradient id="expHrStagesGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#f43f5e" stop-opacity="0.46"/>
            <stop offset="52%" stop-color="#fb923c" stop-opacity="0.15"/>
            <stop offset="100%" stop-color="#0f172a" stop-opacity="0.0"/>
          </linearGradient>
          <linearGradient id="expHrPeakColGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#f43f5e" stop-opacity="0.18"/>
            <stop offset="65%" stop-color="#f43f5e" stop-opacity="0.06"/>
            <stop offset="100%" stop-color="#f43f5e" stop-opacity="0.0"/>
          </linearGradient>
          <filter id="expHrSplineGlow" x="-10%" y="-20%" width="120%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur"/>
            <feMerge>
              <feMergeNode in="blur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        ${peakColumnsHtml}
        ${gridHtml}
        ${bandsHtml}
        <path d="${areaPath}" fill="url(#expHrStagesGrad)" style="pointer-events: none;"/>
        <path d="${linePath}" fill="none" stroke="url(#expHrStagesStrokeGrad)" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" filter="url(#expHrSplineGlow)" style="pointer-events: none;"/>
        ${annotationsHtml}
        ${dotsHtml}
        ${calloutsHtml}
        ${labelsHtml}
      </svg>
      <div class="exec-hr-footer-strip">
        <div class="exec-hr-metric-cell">
          <span class="exec-metric-lbl">AVERAGE HR</span>
          <strong class="exec-metric-val">${resolvedWorkoutAvgHr} <small>bpm</small></strong>
        </div>
        <div class="exec-hr-metric-cell">
          <span class="exec-metric-lbl">MAX HR (S${peakSeg.stageNum})</span>
          <strong class="exec-metric-val text-rose">${actualMaxHr} <small>bpm</small></strong>
        </div>
        <div class="exec-hr-metric-cell exec-hr-metric-spark">
          <div>
            <span class="exec-metric-lbl">PEAK EFFORT</span>
            <strong class="exec-metric-val">${peakEffortTimeStr}</strong>
          </div>
          <div class="exec-mini-spark-wrap">
            <span class="exec-spark-delta">+${peakDeltaPct}%</span>
            <svg width="56" height="18" viewBox="0 0 56 18" style="overflow:visible;">
              <polyline fill="none" stroke="#f43f5e" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" points="${sparkPts}"/>
            </svg>
          </div>
        </div>
        <div class="exec-hr-metric-cell">
          <span class="exec-metric-lbl">${hasRecoveryDrop ? 'FINAL RECOVERY' : 'MIN SPLIT HR'}</span>
          <strong class="exec-metric-val text-sky">${hasRecoveryDrop ? lastSeg.avgHr : actualMinHr} <small>bpm</small></strong>
        </div>
      </div>
    </div>
  `;

  const svgEl = containerEl.querySelector('#exp-hr-stages-svg');
  const tooltip = containerEl.querySelector('#hr-chart-tooltip') || (typeof document !== 'undefined' ? document.getElementById('expanded-hr-tooltip') : null);
  let lockedStageIdx = Number.isFinite(initialStageIdx) && stageSegments[initialStageIdx] ? initialStageIdx : null;

  const selectSegment = (idx) => {
    const seg = stageSegments[idx];
    if (!seg) return;
    const durStr = `${seg.durationMin || 0}m ${(seg.durationSec || 0).toString().padStart(2, '0')}s`;
    updateExpandedSegmentHUD(workout, idx, seg, durStr);
  };

  if (lockedStageIdx !== null) {
    selectSegment(lockedStageIdx);
  } else if (stageSegments.length > 0) {
    selectSegment(0);
  }

  if (svgEl) {
    const bands = svgEl.querySelectorAll('.stage-band-interactive');
    const dots = svgEl.querySelectorAll('.stage-dot-interactive');

    const handleSelectIdx = (idx, isClick = false) => {
      const seg = stageSegments[idx];
      if (!seg) return;

      if (isClick) {
        lockedStageIdx = (lockedStageIdx === idx) ? null : idx;
      }

      bands.forEach((b, bIdx) => {
        b.classList.toggle('active-band', bIdx === idx || bIdx === lockedStageIdx);
      });

      const targetIdx = lockedStageIdx !== null ? lockedStageIdx : idx;
      selectSegment(targetIdx);
    };

    bands.forEach(band => {
      const idx = parseInt(band.getAttribute('data-stage-idx'), 10);
      if (lockedStageIdx === idx) {
        band.classList.add('active-band');
      }
      band.addEventListener('mouseenter', () => {
        if (lockedStageIdx === null) handleSelectIdx(idx, false);
      });
      band.addEventListener('click', () => handleSelectIdx(idx, true));
    });

    dots.forEach(dot => {
      const idx = parseInt(dot.getAttribute('data-stage-idx'), 10);
      dot.addEventListener('mouseenter', () => {
        if (lockedStageIdx === null) handleSelectIdx(idx, false);
      });
      dot.addEventListener('click', () => handleSelectIdx(idx, true));
    });

    svgEl.addEventListener('mousemove', (e) => {
      if (!tooltip) return;
      const rect = svgEl.getBoundingClientRect();
      const relX = Math.max(0, Math.min(1, (e.clientX - rect.left) / (rect.width || 1)));
      const svgX = relX * width;
      const activeSeg = stageSegments.find(s => svgX >= s.x1 && svgX <= s.x2) || (svgX < plotLeft ? stageSegments[0] : stageSegments[stageSegments.length - 1]);
      if (!activeSeg) return;

      const stName = activeSeg.stationName || activeSeg.name || '';
      const isRox = activeSeg.type === 'RoxZone' || activeSeg.type === 'Roxzone' || stName.toLowerCase().includes('rox');
      const stageLabel = activeSeg.type === 'Run' ? '🏃 Run' : (isRox ? '⚡ RoxZone' : (activeSeg.type === 'Rest' ? '⏳ Rest' : `💪 ${escapeHtml(stName || 'Station')}`));
      const durStr = `${activeSeg.durationMin || 0}m ${(activeSeg.durationSec || 0).toString().padStart(2, '0')}s`;

      tooltip.style.display = 'block';
      tooltip.style.opacity = '1';
      tooltip.style.left = `${(activeSeg.midX / width) * 100}%`;
      tooltip.innerHTML = `<div style="text-align:center; line-height:1.35;"><strong>Stage ${activeSeg.stageNum}: ${stageLabel}</strong><br><span style="color:#fda4af; font-weight:700;">❤️ ${activeSeg.avgHr || '--'} bpm</span> • <span style="color:var(--text-muted);">${durStr}</span><br><span style="font-size:10px; color:#818cf8;">Click to ${lockedStageIdx === activeSeg.stageIdx ? 'unlock' : 'lock'} split</span></div>`;
    });

    svgEl.addEventListener('mouseleave', () => {
      if (tooltip) {
        tooltip.style.opacity = '0';
        tooltip.style.display = 'none';
      }
    });
  }
}

export function updateExpandedSegmentHUD(workout, idx, seg, durStr) {
  const hudContainer = typeof document !== 'undefined' ? document.getElementById('expanded-hr-segment-hud') : null;
  if (!hudContainer || !seg) return;

  const stName = (seg.stationName || seg.name || '').trim();
  const rawType = (seg.type || seg.stage_type || '').toLowerCase();
  const isRox = rawType.includes('rox') || stName.toLowerCase().includes('rox');
  const isRun = seg.type === 'Run' || rawType === 'run' || rawType === 'running';
  const isRest = seg.type === 'Rest' || rawType === 'rest';
  const isCardio = seg.type === 'Cardio' || rawType === 'cardio';

  let stageLabel = '';
  if (isRun) {
    stageLabel = `${getStationBadgeHtml('Run', 'station-badge-sm')} <span>Run</span>`;
  } else if (isRox) {
    stageLabel = `${getStationBadgeHtml('RoxZone', 'station-badge-sm')} <span>RoxZone Transition</span>`;
  } else if (isRest) {
    stageLabel = '⏳ Rest Period';
  } else if (isCardio) {
    stageLabel = '🚴 Cardio';
  } else {
    const cleanName = stName || 'Station';
    const hasCustomIcon = STATION_META.some(m => m.key === cleanName || m.label === cleanName) || cleanName.toLowerCase().includes('farmer') || cleanName.toLowerCase().includes('lunge') || cleanName.toLowerCase().includes('burpee') || cleanName.toLowerCase().includes('wall') || cleanName.toLowerCase().includes('row') || cleanName.toLowerCase().includes('ski') || cleanName.toLowerCase().includes('sled');
    stageLabel = hasCustomIcon
      ? `${getStationBadgeHtml(cleanName, 'station-badge-sm')} <span>${escapeHtml(cleanName)}</span>`
      : `💪 <span>${escapeHtml(cleanName)}</span>`;
  }

  const th = resolveHrZoneThresholds();
  const hrVal = seg.avgHr || 0;
  let zoneLabel = 'Z1 • Endurance';
  let zoneColor = '#94a3b8';
  if (hrVal >= th.z4) { zoneLabel = 'Z5 • Anaerobic'; zoneColor = '#f43f5e'; }
  else if (hrVal >= th.z3) { zoneLabel = 'Z4 • Threshold'; zoneColor = '#f59e0b'; }
  else if (hrVal >= th.z2) { zoneLabel = 'Z3 • Tempo'; zoneColor = '#10b981'; }
  else if (hrVal >= th.z1) { zoneLabel = 'Z2 • Moderate'; zoneColor = '#38bdf8'; }

  let metricLabel = 'Distance / Reps';
  let metricVal = '--';
  const rawDist = Number(seg.distance !== null && seg.distance !== undefined ? seg.distance : (seg.distance_meters || 0));
  const rawReps = Number(seg.reps || 0);
  if (isRun || isRox || isCardio) {
    metricLabel = 'Distance';
    metricVal = rawDist > 0 ? `${rawDist}m` : '0m';
  } else if (!isRest) {
    const isDist = isDistanceStation(stName, seg.type);
    if (isDist) {
      metricLabel = 'Distance';
      metricVal = rawDist > 0 ? `${rawDist}m` : '--';
    } else {
      metricLabel = 'Reps';
      const effectiveReps = rawReps > 0 ? rawReps : (rawDist > 0 ? rawDist : 0);
      metricVal = effectiveReps > 0 ? `${effectiveReps} reps` : '--';
    }
  }

  const startMin = Math.floor((seg.startSec || 0) / 60);
  const startRem = Math.floor((seg.startSec || 0) % 60);
  const endMin = Math.floor((seg.endSec || 0) / 60);
  const endRem = Math.floor((seg.endSec || 0) % 60);
  const windowStr = `${startMin}:${startRem.toString().padStart(2, '0')} – ${endMin}:${endRem.toString().padStart(2, '0')}`;

  hudContainer.innerHTML = `
    <div class="hr-hud-chip hr-hud-chip-stage">
      <span class="hud-lbl">Stage ${seg.stageNum || (idx + 1)}</span>
      <span class="hud-val" style="color: #818cf8; display: inline-flex; align-items: center; gap: 6px;">${stageLabel}</span>
    </div>
    <div class="hr-hud-chip">
      <span class="hud-lbl">Avg Heart Rate</span>
      <span class="hud-val" style="color: #f43f5e;">❤️ ${hrVal ? `${hrVal} bpm` : '--'}</span>
    </div>
    <div class="hr-hud-chip">
      <span class="hud-lbl">Split Duration</span>
      <span class="hud-val">⏱️ ${durStr} <span style="font-size: 11px; color: var(--text-muted); font-weight: 500;">(${windowStr})</span></span>
    </div>
    <div class="hr-hud-chip">
      <span class="hud-lbl">${metricLabel}</span>
      <span class="hud-val">${metricVal}</span>
    </div>
    <div class="hr-hud-chip">
      <span class="hud-lbl">Intensity Zone</span>
      <span class="hud-val" style="color: ${zoneColor};">${hrVal ? zoneLabel : '--'}</span>
    </div>
  `;
}


// -- Picker Wheel Engine --
function initPickerColumn(columnId, min, max) {
  const columnEl = document.getElementById(columnId);
  if (!columnEl) return;
  columnEl.innerHTML = '';
  
  // Padding top (50px to center the first 40px item in a 140px container)
  const padTop = document.createElement('div');
  padTop.style.minHeight = '50px';
  columnEl.appendChild(padTop);

  for (let i = min; i <= max; i++) {
    const item = document.createElement('div');
    item.className = 'picker-item';
    item.textContent = i.toString().padStart(columnId.includes('dist') ? 1 : 2, '0');
    item.dataset.val = i;
    columnEl.appendChild(item);
  }

  const padBottom = document.createElement('div');
  padBottom.style.minHeight = '50px';
  columnEl.appendChild(padBottom);

  const updateActiveItem = () => {
    const index = Math.round((columnEl.scrollTop || 0) / 40);
    const items = columnEl.querySelectorAll('.picker-item');
    const isScrollable = Number.isFinite(columnEl.scrollHeight) && Number.isFinite(columnEl.clientHeight) && columnEl.scrollHeight > columnEl.clientHeight;
    items.forEach((item, idx) => {
      if (idx === index) {
        item.classList.add('active');
        if (columnEl.dataset && (isScrollable || columnEl.dataset.selectedVal === undefined)) {
          columnEl.dataset.selectedVal = String(item.dataset.val || 0);
        }
      } else {
        item.classList.remove('active');
      }
    });
  };

  columnEl.addEventListener('scroll', () => {
    // Debounce slightly for performance
    if (columnEl._scrollTimeout) clearTimeout(columnEl._scrollTimeout);
    columnEl._scrollTimeout = setTimeout(updateActiveItem, 20);
  });
  
  // Initial active state
  setTimeout(updateActiveItem, 0);
}

export function getPickerValue(columnId) {
  const columnEl = typeof columnId === 'string' ? document.getElementById(columnId) : columnId;
  if (!columnEl) return 0;
  const items = columnEl.querySelectorAll('.picker-item');
  const isScrollable = Number.isFinite(columnEl.scrollHeight) && Number.isFinite(columnEl.clientHeight) && columnEl.scrollHeight > columnEl.clientHeight;
  const hasScrolledFromSet = isScrollable && Number.isFinite(columnEl.scrollTop) && columnEl.scrollTop !== (columnEl._programmaticScrollTop ?? 0);
  if (hasScrolledFromSet && items && items.length > 0) {
    const index = Math.round(columnEl.scrollTop / 40);
    if (items[index]) return parseInt(items[index].dataset.val, 10) || 0;
  }
  if (columnEl.dataset && columnEl.dataset.selectedVal !== undefined) {
    return parseInt(columnEl.dataset.selectedVal, 10) || 0;
  }
  if (items && items.length > 0 && Number.isFinite(columnEl.scrollTop)) {
    const index = Math.round(columnEl.scrollTop / 40);
    if (items[index]) return parseInt(items[index].dataset.val, 10) || 0;
  }
  return 0;
}

export function setPickerValue(columnId, value) {
  const columnEl = typeof columnId === 'string' ? document.getElementById(columnId) : columnId;
  if (!columnEl) return;
  if (columnEl.dataset) {
    columnEl.dataset.selectedVal = String(value);
  }
  const items = Array.from(columnEl.querySelectorAll('.picker-item'));
  const index = items.findIndex(el => parseInt(el.dataset.val, 10) === value);
  if (index !== -1) {
    columnEl.scrollTop = index * 40;
    columnEl._programmaticScrollTop = columnEl.scrollTop;
    // Force active state update
    items.forEach((item, idx) => {
      if (idx === index) item.classList.add('active');
      else item.classList.remove('active');
    });
  }
}

export function populateModalStationSelect(stationSelectOrValue, currentStationValue = '') {
  const isStringArg = typeof stationSelectOrValue === 'string';
  const stationSelect = isStringArg
    ? (typeof document !== 'undefined' ? document.getElementById('modal-stage-station') : null)
    : stationSelectOrValue;
  const effectiveCurrentValue = isStringArg ? stationSelectOrValue : currentStationValue;
  if (!stationSelect) return;

  const altsMap = getStationAlternatives();
  const knownOptionValues = new Set();
  let optionsHtml = '<optgroup label="Standard HYROX Stations">';

  CANONICAL_HYROX_STATIONS.forEach(station => {
    knownOptionValues.add(station);
    optionsHtml += `<option value="${escapeHtml(station)}">${escapeHtml(station)}</option>`;
  });
  optionsHtml += '</optgroup>';

  CANONICAL_HYROX_STATIONS.forEach(station => {
    const altKey = altsMap[station]
      ? station
      : (station === 'Burpee Broad Jumps' && altsMap['Burpee Broad Jump']
        ? 'Burpee Broad Jump'
        : (station === 'Farmers Carry' && altsMap["Farmer's Carry"] ? "Farmer's Carry" : station));
    const stationAlts = Array.isArray(altsMap[altKey]) ? altsMap[altKey] : [];
    if (stationAlts.length > 0) {
      optionsHtml += `<optgroup label="${escapeHtml(station)} Alternatives">`;
      stationAlts.forEach(alt => {
        const compositeVal = `${station}: ${alt}`;
        knownOptionValues.add(compositeVal);
        optionsHtml += `<option value="${escapeHtml(compositeVal)}">${escapeHtml(compositeVal)}</option>`;
      });
      optionsHtml += '</optgroup>';
    }
  });

  const trimmedCurrent = String(effectiveCurrentValue || '').trim();
  const normalizedCurrent = normalizeStationName(trimmedCurrent);
  let targetValue = 'SkiErg';

  if (trimmedCurrent && !isGenericStageName(trimmedCurrent)) {
    if (knownOptionValues.has(trimmedCurrent)) {
      targetValue = trimmedCurrent;
    } else if (normalizedCurrent && knownOptionValues.has(normalizedCurrent)) {
      targetValue = normalizedCurrent;
    } else {
      knownOptionValues.add(trimmedCurrent);
      optionsHtml += `<optgroup label="Custom / Recorded Station"><option value="${escapeHtml(trimmedCurrent)}">${escapeHtml(trimmedCurrent)}</option></optgroup>`;
      targetValue = trimmedCurrent;
    }
  }

  stationSelect.innerHTML = optionsHtml;
  stationSelect.value = targetValue;
}

function updateModalMetricLabel(typeVal, stationName) {
  if (typeof document === 'undefined') return;
  const isStation = typeVal === 'Strength' || typeVal === 'Station';
  const isDist = !isStation || isDistanceStation(stationName, typeVal);
  let labelEl = document.getElementById('modal-stage-metric-label');
  if (!labelEl) {
    const modal = document.getElementById('modal-stage-editor');
    if (modal && typeof modal.querySelectorAll === 'function') {
      const labels = modal.querySelectorAll('.picker-label');
      labels.forEach(el => {
        if (el.textContent === 'DISTANCE (M)' || el.textContent === 'REPS') {
          labelEl = el;
        }
      });
    }
  }
  if (labelEl) {
    labelEl.textContent = isDist ? 'DISTANCE (M)' : 'REPS';
  }
}

export function setupStageEditorModal() {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('modal-stage-editor');
  if (!modal) return;

  const btnClose = document.getElementById('btn-close-stage-modal');
  const btnCancel = document.getElementById('btn-cancel-stage-modal');
  const btnSave = document.getElementById('btn-save-stage-modal');
  const btnDelete = document.getElementById('btn-delete-stage-modal');
  const typeSelect = document.getElementById('modal-stage-type');
  const stationSelect = document.getElementById('modal-stage-station');

  populateModalStationSelect(stationSelect);

  // Init Picker Columns
  initPickerColumn('picker-time-hr', 0, 23);
  initPickerColumn('picker-time-min', 0, 59);
  initPickerColumn('picker-time-sec', 0, 59);
  initPickerColumn('picker-dist-1000', 0, 9);
  initPickerColumn('picker-dist-100', 0, 9);
  initPickerColumn('picker-dist-10', 0, 9);
  initPickerColumn('picker-dist-1', 0, 9);

  if (typeSelect) {
    typeSelect.addEventListener('change', () => {
      const val = typeSelect.value;
      if (val === 'Strength' || val === 'Station') {
        if (stationSelect) {
          if (!stationSelect.value) {
            populateModalStationSelect(stationSelect, 'SkiErg');
          }
          stationSelect.style.display = 'block';
        }
      } else if (stationSelect) {
        stationSelect.style.display = 'none';
      }
      updateModalMetricLabel(val, stationSelect ? stationSelect.value : '');
    });
  }

  if (stationSelect) {
    stationSelect.addEventListener('change', () => {
      updateModalMetricLabel(typeSelect ? typeSelect.value : 'Station', stationSelect.value);
    });
  }

  const closeModal = (keepScrollState = false) => {
    const scrollToRestore = savedDetailScrollState;
    modal.close();
    activeEditRow = null;
    activeEditStageIndex = null;
    activeEditIsNewStage = false;
    activeEditStageDraft = null;
    if (!keepScrollState) {
      savedDetailScrollState = null;
    }
    if (scrollToRestore) {
      restoreDetailScrollState(scrollToRestore);
    }
  };

  if (btnClose) btnClose.addEventListener('click', () => closeModal(false));
  if (btnCancel) btnCancel.addEventListener('click', () => closeModal(false));

  if (btnSave) btnSave.addEventListener('click', async () => {
    if (activeEditRow === null && activeEditStageIndex === null) return;

    // Read picker values
    const hrs = getPickerValue('picker-time-hr');
    const mins = getPickerValue('picker-time-min');
    const secs = getPickerValue('picker-time-sec');
    const d1000 = getPickerValue('picker-dist-1000');
    const d100 = getPickerValue('picker-dist-100');
    const d10 = getPickerValue('picker-dist-10');
    const d1 = getPickerValue('picker-dist-1');

    const totalMin = (hrs * 60) + mins;
    const totalSec = (totalMin * 60) + secs;
    const totalDist = (d1000 * 1000) + (d100 * 100) + (d10 * 10) + d1;
    const rawTypeVal = typeSelect ? typeSelect.value : 'Station';
    const isStation = rawTypeVal === 'Strength' || rawTypeVal === 'Station';
    const fallbackDraftStation = activeEditStageDraft ? (activeEditStageDraft.stationName || activeEditStageDraft.name || '') : '';
    const resolvedStationVal = isStation
      ? ((stationSelect && stationSelect.value) || (!isGenericStageName(fallbackDraftStation) ? fallbackDraftStation : 'SkiErg'))
      : '';

    let stageName = '';
    if (isStation) stageName = resolvedStationVal || 'SkiErg';
    else if (rawTypeVal === 'Run') stageName = 'Run';
    else if (rawTypeVal === 'RoxZone' || rawTypeVal === 'Roxzone') stageName = 'RoxZone';
    else if (rawTypeVal === 'Rest') stageName = 'Rest';
    else if (rawTypeVal === 'Cardio') stageName = 'Cardio';
    else stageName = 'Station';

    const canonicalType = isStation ? 'Station' : rawTypeVal;
    const isDistMetric = !isStation || isDistanceStation(stageName, canonicalType);

    if (activeEditRow) {
      const minInput = activeEditRow.querySelector('.detail-min-input');
      const secInput = activeEditRow.querySelector('.detail-sec-input');
      const metricInput = activeEditRow.querySelector('.detail-stage-metric-input');
      const rowTypeSelect = activeEditRow.querySelector('.detail-stage-type-select');
      const rowStationSelect = activeEditRow.querySelector('.detail-stage-station-select');

      if (minInput) minInput.value = totalMin;
      if (secInput) secInput.value = secs;
      if (metricInput) metricInput.value = totalDist;
      if (rowTypeSelect) rowTypeSelect.value = rawTypeVal;
      if (rowStationSelect) rowStationSelect.value = isStation ? stageName : '';

      const durDisplay = activeEditRow.querySelector('.row-display-time');
      if (durDisplay) durDisplay.textContent = `${totalMin}m ${secs}s`;
      const distDisplay = activeEditRow.querySelector('.row-display-dist');
      if (distDisplay) distDisplay.textContent = isDistMetric ? `${totalDist}m` : `${totalDist} reps`;

      let emoji = '💪';
      if (rawTypeVal === 'Run') emoji = '🏃';
      else if (rawTypeVal === 'RoxZone' || rawTypeVal === 'Roxzone') emoji = '⚡';
      else if (rawTypeVal === 'Rest') emoji = '☕';
      else if (rawTypeVal === 'Cardio') emoji = '🚴';

      const titleDisplay = activeEditRow.querySelector('.row-display-title');
      if (titleDisplay) titleDisplay.textContent = `${emoji} ${stageName}`;
    }

    const editIdx = activeEditStageIndex;
    const isNew = activeEditIsNewStage;
    const draft = activeEditStageDraft;
    closeModal(true);

    if (editIdx !== null && currentWorkoutDetailId) {
      const workout = (store.workouts || []).find(w => w && (w.id === currentWorkoutDetailId || String(w.id) === String(currentWorkoutDetailId)));
      if (workout) {
        const existingStages = Array.isArray(workout.stages) ? [...workout.stages] : [];
        const prevStage = isNew ? (draft || {}) : (existingStages[editIdx] || draft || {});
        const updatedStage = {
          ...prevStage,
          stageNum: editIdx + 1,
          stage_index: editIdx + 1,
          type: canonicalType,
          stage_type: canonicalType,
          name: stageName,
          stationName: stageName,
          durationMin: totalMin,
          durationSec: secs,
          duration: totalSec,
          duration_seconds: totalSec,
          distance: isDistMetric ? totalDist : 0,
          distance_meters: isDistMetric ? totalDist : 0,
          avgHr: prevStage.avgHr !== undefined ? prevStage.avgHr : (prevStage.avg_hr || 0),
          reps: isDistMetric ? (prevStage.reps !== undefined ? prevStage.reps : null) : totalDist,
          weight_kg: prevStage.weight_kg !== undefined ? prevStage.weight_kg : (prevStage.weight !== undefined ? prevStage.weight : null),
          metadata: prevStage.metadata || {}
        };

        if (isNew || editIdx >= existingStages.length) {
          existingStages.push(updatedStage);
        } else {
          existingStages[editIdx] = updatedStage;
        }

        const reindexed = existingStages.map((st, idx) => ({
          ...st,
          stageNum: idx + 1,
          stage_index: idx + 1
        }));

        await saveWorkoutSplits(currentWorkoutDetailId, reindexed);
      }
    }
  });

  if (btnDelete) btnDelete.addEventListener('click', async () => {
    if (activeEditRow) {
      activeEditRow.remove();
    }
    const editIdx = activeEditStageIndex;
    const isNew = activeEditIsNewStage;
    closeModal(true);

    if (!isNew && editIdx !== null && currentWorkoutDetailId) {
      const workout = (store.workouts || []).find(w => w && (w.id === currentWorkoutDetailId || String(w.id) === String(currentWorkoutDetailId)));
      if (workout && Array.isArray(workout.stages)) {
        const remainingStages = workout.stages
          .filter((_, idx) => idx !== editIdx)
          .map((st, idx) => ({
            ...st,
            stageNum: idx + 1,
            stage_index: idx + 1
          }));
        await saveWorkoutSplits(currentWorkoutDetailId, remainingStages);
      }
    }
  });
}

export function openStageEditorForIndex(stageIdx, isNewStage = false, initialStage = null) {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('modal-stage-editor');
  if (!modal) return;

  const workout = (store.workouts || []).find(w => w && (w.id === currentWorkoutDetailId || String(w.id) === String(currentWorkoutDetailId)));
  const st = isNewStage && initialStage
    ? initialStage
    : (workout && Array.isArray(workout.stages) ? workout.stages[stageIdx] : initialStage);
  if (!st) return;

  savedDetailScrollState = captureDetailScrollState();
  activeEditRow = null;
  activeEditStageIndex = stageIdx;
  activeEditIsNewStage = Boolean(isNewStage);
  activeEditStageDraft = { ...st };

  const btnDelete = document.getElementById('btn-delete-stage-modal');
  if (btnDelete) {
    btnDelete.classList.toggle('hidden', Boolean(isNewStage));
    btnDelete.style.display = isNewStage ? 'none' : '';
  }

  const stageNum = st.stageNum || st.stage_index || (stageIdx + 1);
  const titleEl = document.getElementById('modal-stage-editor-title');
  if (titleEl) titleEl.textContent = `Stage ${stageNum}`;

  const typeSelect = document.getElementById('modal-stage-type');
  const stationSelect = document.getElementById('modal-stage-station');
  const hrBadge = document.getElementById('modal-stage-hr');

  const rawName = (st.stationName || st.station_name || st.name || '').trim();
  const rawType = (st.type || st.stage_type || '').toLowerCase();
  const isExplicitStation = rawType === 'station' || rawType === 'strength';
  const isRox = rawType.includes('rox') || rawName.toLowerCase().includes('rox');
  const isRest = rawType === 'rest' || (!isExplicitStation && /^rest(?:\s+period)?$/i.test(rawName));
  const isCardio = rawType === 'cardio' || rawName.toLowerCase() === 'cardio';
  const isRun = rawType === 'run' || rawType === 'running' || (!isExplicitStation && /^(?:run|running)(?:\b|\s*:)/i.test(rawName));

  let typeVal = 'Station';
  if (isRox) typeVal = 'RoxZone';
  else if (isRest) typeVal = 'Rest';
  else if (isCardio) typeVal = 'Cardio';
  else if (isRun) typeVal = 'Run';

  const durSecTotal = Number.isFinite(st.duration_seconds)
    ? Math.round(st.duration_seconds)
    : (Number.isFinite(st.duration)
      ? Math.round(st.duration)
      : ((parseInt(st.durationMin, 10) || 0) * 60 + (parseInt(st.durationSec, 10) || 0)));
  const durMin = st.durationMin !== undefined ? (parseInt(st.durationMin, 10) || 0) : Math.floor(durSecTotal / 60);
  const durSec = st.durationSec !== undefined ? (parseInt(st.durationSec, 10) || 0) : (durSecTotal % 60);
  const rawDistance = parseInt(st.distance !== undefined && st.distance !== null ? st.distance : (st.distance_meters || 0), 10) || 0;
  const rawReps = parseInt(st.reps !== undefined && st.reps !== null ? st.reps : 0, 10) || 0;
  const isDistMetric = typeVal !== 'Station' || isDistanceStation(rawName, typeVal);
  const distance = isDistMetric ? rawDistance : (rawReps > 0 ? rawReps : rawDistance);
  const avgHrNum = Number.isFinite(st.avgHr) ? Math.round(st.avgHr) : (Number.isFinite(st.avg_hr) ? Math.round(st.avg_hr) : (Number.isFinite(st.average_heart_rate) ? Math.round(st.average_heart_rate) : 0));
  const avgHr = avgHrNum > 0 ? avgHrNum : '--';

  const hours = Math.floor(durMin / 60);
  const minutes = durMin % 60;
  const seconds = durSec;

  const d1000 = Math.floor((distance % 10000) / 1000);
  const d100 = Math.floor((distance % 1000) / 100);
  const d10 = Math.floor((distance % 100) / 10);
  const d1 = distance % 10;

  if (typeSelect) typeSelect.value = typeVal;
  populateModalStationSelect(stationSelect, typeVal === 'Station' ? rawName : '');
  updateModalMetricLabel(typeVal, stationSelect ? stationSelect.value : rawName);
  if (hrBadge) hrBadge.textContent = `❤️ ${avgHr}`;

  if (typeVal === 'Strength' || typeVal === 'Station') {
    if (stationSelect) stationSelect.style.display = 'block';
  } else {
    if (stationSelect) stationSelect.style.display = 'none';
  }

  modal.showModal();

  setPickerValue('picker-time-hr', hours);
  setPickerValue('picker-time-min', minutes);
  setPickerValue('picker-time-sec', seconds);
  setPickerValue('picker-dist-1000', d1000);
  setPickerValue('picker-dist-100', d100);
  setPickerValue('picker-dist-10', d10);
  setPickerValue('picker-dist-1', d1);

  setTimeout(() => {
    setPickerValue('picker-time-hr', hours);
    setPickerValue('picker-time-min', minutes);
    setPickerValue('picker-time-sec', seconds);
    setPickerValue('picker-dist-1000', d1000);
    setPickerValue('picker-dist-100', d100);
    setPickerValue('picker-dist-10', d10);
    setPickerValue('picker-dist-1', d1);
  }, 10);
}

export function openStageEditorModal(row, stageNum) {
  if (typeof row === 'number') {
    openStageEditorForIndex(row);
    return;
  }
  if (typeof document === 'undefined' || !row) return;
  const modal = document.getElementById('modal-stage-editor');
  if (!modal) return;

  savedDetailScrollState = captureDetailScrollState();
  activeEditRow = row;
  activeEditStageIndex = Number.isFinite(stageNum) ? stageNum - 1 : null;
  activeEditIsNewStage = false;

  const titleEl = document.getElementById('modal-stage-editor-title');
  if (titleEl) titleEl.textContent = `Stage ${stageNum}`;

  const typeSelect = document.getElementById('modal-stage-type');
  const stationSelect = document.getElementById('modal-stage-station');
  const hrBadge = document.getElementById('modal-stage-hr');

  const rawTypeVal = row.querySelector('.detail-stage-type-select')?.value || 'Station';
  const typeVal = rawTypeVal === 'Strength' ? 'Station' : rawTypeVal;
  const stationVal = row.querySelector('.detail-stage-station-select')?.value || '';
  const durMin = parseInt(row.querySelector('.detail-min-input')?.value, 10) || 0;
  const durSec = parseInt(row.querySelector('.detail-sec-input')?.value, 10) || 0;
  const distance = parseInt(row.querySelector('.detail-stage-metric-input')?.value, 10) || 0;
  const avgHr = row.querySelector('.detail-hr-input')?.value || '--';
  const isDistMetric = typeVal !== 'Station' || isDistanceStation(stationVal, typeVal);

  activeEditStageDraft = {
    stageNum,
    type: typeVal,
    stationName: stationVal,
    durationMin: durMin,
    durationSec: durSec,
    distance: isDistMetric ? distance : 0,
    reps: isDistMetric ? null : distance,
    avgHr: parseInt(avgHr, 10) || 0
  };

  // Math for Time
  const hours = Math.floor(durMin / 60);
  const minutes = durMin % 60;
  const seconds = durSec;

  // Math for Distance
  const d1000 = Math.floor((distance % 10000) / 1000);
  const d100 = Math.floor((distance % 1000) / 100);
  const d10 = Math.floor((distance % 100) / 10);
  const d1 = distance % 10;

  // Populate basic inputs
  if (typeSelect) typeSelect.value = typeVal;
  populateModalStationSelect(stationSelect, stationVal);
  updateModalMetricLabel(typeVal, stationSelect ? stationSelect.value : stationVal);
  if (hrBadge) hrBadge.textContent = `❤️ ${avgHr}`;

  if (typeVal === 'Strength' || typeVal === 'Station') {
    if (stationSelect) stationSelect.style.display = 'block';
  } else {
    if (stationSelect) stationSelect.style.display = 'none';
  }

  modal.showModal();

  setPickerValue('picker-time-hr', hours);
  setPickerValue('picker-time-min', minutes);
  setPickerValue('picker-time-sec', seconds);
  setPickerValue('picker-dist-1000', d1000);
  setPickerValue('picker-dist-100', d100);
  setPickerValue('picker-dist-10', d10);
  setPickerValue('picker-dist-1', d1);

  // Set picker scrolls immediately after modal opens (requires DOM measurement)
  setTimeout(() => {
    setPickerValue('picker-time-hr', hours);
    setPickerValue('picker-time-min', minutes);
    setPickerValue('picker-time-sec', seconds);
    
    setPickerValue('picker-dist-1000', d1000);
    setPickerValue('picker-dist-100', d100);
    setPickerValue('picker-dist-10', d10);
    setPickerValue('picker-dist-1', d1);
  }, 10);
}
